
import React, { useEffect, useState, useRef, useCallback } from "react";
import "./App.css";
import { scoreTrade, rsi, calculateIndicators, computeMoreInfoIndicators } from "./tradeEngine";
import { computeVolumeProfile, VolumeProfilePrimitive } from "./volumeProfile";
import { CONFIG } from "./config";
import RainCanvas from "./RainCanvas";
import { getProvider, fetchAlpacaLatestPrices, fetchAlpacaSnapshots } from "./candles";
import { getCandles, getSpotPrice } from "./marketData";
import { isStockMarketOpen } from "./marketHours";
import { ASSET_LIBRARY } from "./assetLibrary";
import {
  IconScan,
  IconLayoutDashboard,
  IconChartCandle,
  IconBookmark,
  IconHistory,
  IconBell,
  IconMenu2,
  IconX,
  IconCircleCheck,
  IconTrendingUp,
  IconChartBar,
  IconEye,
  IconAlertTriangle,
  IconInfoCircle,
  IconPencil,
  IconLogout,
  IconSettings,
  IconUser,
  IconAdjustments,
  IconFileText,
  IconShieldLock,
  IconTrash,
  IconNews,
  IconBook,
} from '@tabler/icons-react';
import { createChart, ColorType, CrosshairMode } from "lightweight-charts";
import SignalChart from "./SignalChart";
import { granularityMap, normalizeCandles } from "./chartHelpers";
import { supabase } from "./supabaseClient";
import { fetchFollowedAssets, insertFollow, deleteFollow, setFollowFlag, seedDefaults, dismissSignal } from "./followStore";
import { fetchDrawings, insertDrawing, deleteDrawing } from "./drawingsStore";
import { useMeasureTool, MeasureOverlay } from "./measureTool";
import { useTrendLineTool, TrendLineOverlay } from "./trendLineTool";
import { useFibTool, FibOverlay } from "./fibTool";
import { useChannelTool, ChannelOverlay } from "./channelTool";
import { useRayTool, RayOverlay } from "./rayTool";
import { useLongPositionTool, LongPositionOverlay } from "./longPositionTool";
import { limitsForTier } from "./tierLimits";





const formatCandles = (raw) => {
    return raw.map(candle => ({
      time:   candle[0],
      open:   candle[3],
      high:   candle[2],
      low:    candle[1],
      close:  candle[4],
      volume: candle[5]    // include volume for indicator logic

    })).reverse();
  };

// granularityMap + normalizeCandles now live in ./chartHelpers (shared with SignalChart.js).

// ───────────────────────────────────────────────────────────────
// Helpers: Read‐only, cache‐only candle retrieval for Coin Updates
// ───────────────────────────────────────────────────────────────
function getCachedCandles(allCandles, coin, timeframe) {
  return (allCandles[coin] && allCandles[coin][timeframe]) || [];
}

function collectCachedUpdates(followedCoins, timeframes, allCandles) {
  const cache = {};
  for (const coin of Object.keys(followedCoins)) {
    if (!followedCoins[coin]) continue;
    cache[coin] = {};
    for (const tf of timeframes) {
      cache[coin][tf] = getCachedCandles(allCandles, coin, tf);
    }
  }
  return cache;
}

// ───────────────────────────────────────────────────────────────
// Evaluate a coin update by computing indicators and scoring
function evaluateCoinUpdate(coin, timeframe, candles) {
  if (!candles || candles.length < 50) return null;
  // Compute indicators
  const { ema9, rsiValue, macd, signal, atrValue } = calculateIndicators(candles);
  const macdHist = macd - signal;
  // For EMA trend, compute percent change over the prior EMA
  const emaHistory = candles.slice(-2).map(c => calculateIndicators([c]).ema9);
  const prevEma = emaHistory[0] || ema9;
  const emaPct = ((ema9 - prevEma) / prevEma) * 100;
  let emaTrend = "Neutral";
  if (emaPct >= 0.13) {
    emaTrend = "Increasing";
  } else if (emaPct <= -0.10) {
    emaTrend = "Decreasing";
  }
  // Volume surge: compare last candle volume to 10-period average
  const volumes = candles.slice(0, 11).map(c => c.volume);
  const avgVol = volumes.slice(1).reduce((sum, v) => sum + v, 0) / 10;
  const volumeSurge = candles[0].volume > avgVol;

  // Average percent move per candle
  let avgMove = null;
  if (candles.length > 1) {
    const moves = candles.slice(1).map((c, i) => {
      const prev = candles[i].close;
      return Math.abs((c.close - prev) / prev) * 100;
    });
    avgMove = moves.reduce((sum, m) => sum + m, 0) / moves.length;
  }

  // --- Stock RSI (Stochastic RSI) Calculation ---
  let stochRsiK = null;
  const stochPeriod = 14;
  const closePrices = candles.map(c => c.close);
  if (closePrices.length >= stochPeriod + 1) {
    // Build a rolling RSI series
    const rsiArr = [];
    for (let i = stochPeriod; i < closePrices.length; i++) {
      const slice = closePrices.slice(i - stochPeriod, i + 1);
      rsiArr.push(rsi(slice, stochPeriod));
    }
    if (rsiArr.length >= stochPeriod) {
      const lastRsi = rsiArr[rsiArr.length - 1];
      const window = rsiArr.slice(rsiArr.length - stochPeriod);
      const minRsi = Math.min(...window);
      const maxRsi = Math.max(...window);
      stochRsiK = maxRsi !== minRsi
        ? ((lastRsi - minRsi) / (maxRsi - minRsi)) * 100
        : 0;
    }
  }

  // --- Stock RSI Scoring Adjustment ---
  let stochScore = 0;
  if (typeof stochRsiK === "number") {
    if (stochRsiK < 20 || stochRsiK > 80) {
      stochScore = -1;
    } else if (stochRsiK >= 40 && stochRsiK <= 70) {
      stochScore = 1;
    }
  }

  // New sentiment logic: require MACD line over signal line for bullish,
  // Enhanced sentiment logic: require RSI > 54, MACD histogram positive, and EMA trend neutral or increasing
  const isBullish = rsiValue > 54 && macdHist > 0 && (emaTrend === "Increasing" || emaTrend === "Neutral");
  // Bearish: MACD histogram negative and RSI below 50
  const isBearish = macdHist < 0 && rsiValue < 50;
  const score =
    (macdHist > 0 ? 2 : 0) +
    (rsiValue > 50 && rsiValue < 70 ? 1 : 0) +
    (emaTrend === "Increasing" ? 1 : 0) +
    (volumeSurge ? 1 : 0) +
    stochScore;
  return {
    ema9,
    rsi: rsiValue,
    rsiValue, // add rsiValue as a separate property
    macdHist,
    macd,
    signal,
    emaTrend,
    volumeSurge,
    stochRsi: stochRsiK,
    avgMove,
    score,
    isBullish,
    isBearish
  };
}

// ───────────────────────────────────────────────────────────────
// Format a graded result into styled notification lines
function formatCoinUpdateNotification(coin, timeframe, result) {
  if (!result) return null;
  const sentiment = result.isBullish ? "Bullish" : result.isBearish ? "Bearish" : "Neutral";
  const color = result.isBullish ? "green" : result.isBearish ? "red" : "goldenrod";
  return [
    { text: `${coin} – ${timeframe} ${sentiment} Update`, color },
    `• MACD Histogram: ${result.macdHist.toFixed(2)}`,
    `• RSI: ${result.rsiValue.toFixed(1)}`,
    `• Stoch RSI: ${typeof result.stochRsi === "number" ? result.stochRsi.toFixed(1) : "N/A"}`,
    `• EMA: ${result.emaTrend}`,
    `• Avg Move: ${result.avgMove != null ? result.avgMove.toFixed(2) + '%' : 'N/A'}`
  ];
}



function isDuplicateTrade(prevLogs, coin, label, profitPercentage) {
  const now = Date.now();

  return prevLogs.some(note => {
    const isSameCoin = note.coin === coin;
    const isSameTimeframe = note.timeframe === label;
    const timestamp = note.timestamp || 0;
    const timeDiff = (now - timestamp) / 1000 / 60;

    const profitMatch = note.message.match(/Profit Potential: \+([\d.]+)%/);
    const previousProfit = profitMatch ? parseFloat(profitMatch[1]) : null;

    const isSimilarProfit = previousProfit !== null && Math.abs(previousProfit - profitPercentage) < 1.0;

    return isSameCoin && isSameTimeframe && isSimilarProfit;
  });
}

function shouldSendNewTrade(recentMap, coin, label, grade, cooldownMinutes = 60) {
  const key = `${coin}-${label}-${grade}`;
  const now = Date.now();
  const lastSent = recentMap[key];

  if (!lastSent) return true;

  const minutesAgo = (now - lastSent) / (1000 * 60);
  return minutesAgo >= cooldownMinutes;
}

function shouldSkipByUserMode(userMode, alertTag, label) {
  const conservativeTags = ["📊 High Potential Setup", "✅ Strong Trade Signal", "⚡ Breakout Candidate"];
  const moderateTags = ["🔰 Watchlist Candidate", ...conservativeTags];

  if (userMode === "Conservative") {
    // Only scan 1h, 6h, and 1d, and only allow top-tier tags
    const allowedTimeframes = ["1h", "6h", "1d"];
    return !(
      allowedTimeframes.includes(label) &&
      conservativeTags.includes(alertTag)
    );
  }

  if (userMode === "Moderate") {
    // Skip risky setups on longer timeframes
    if ((label === "1h" || label === "6h" || label === "1d") && alertTag === "🌗 Risky Setup") {
      return true;
    }
    return !moderateTags.includes(alertTag);
  }

  return false;
}
// Enforce allowed timeframes per mode
function isTimeframeAllowedForMode(userMode, label) {
  // Conservative: only higher timeframes
  if (userMode === "Conservative") return ["1h", "6h", "1d"].includes(label);
  // Moderate/Aggressive: allow all scanned timeframes
  return true;
}
// ✅ You need to add this now
function isBelowGradeThreshold(userMode, confidenceScore) {
  if (userMode === "Conservative" && confidenceScore < 9) return true;
  if (userMode === "Moderate" && confidenceScore < 7) return true;
  if (userMode === "Aggressive" && confidenceScore < 4) return true;
  return false;
}

// Build B: `coins` is now component state (see useState in App, above followedCoins),
// so crypto can be added/removed. The default crypto universe lives in that useState.

// Phase 5: hardcoded default stock tickers (Alpaca). Seeds stockSymbols + followedCoins
// on load so a fixed default stock list always returns after refresh (no localStorage).
const defaultStocks = [
  "AAPL", "MSFT", "NVDA", "GOOGL", "AMZN",
  "META", "TSLA", "AMD", "NFLX", "JPM",
];

// Phase 3.3: default crypto universe. Extracted to a module-level const (mirroring
// defaultStocks) so both the useState seed AND the clean-break Supabase seed
// (seedDefaults) draw from the same list. Follow-state now persists per-user in
// Supabase (followed_assets), not localStorage — the old LS_KEY / loadFollowState /
// saveFollowState helpers were removed in this build.
const defaultCryptos = [
  "BTC-USD","ETH-USD","SOL-USD","XRP-USD","LTC-USD","ADA-USD",
  "DOGE-USD","DOT-USD","LINK-USD","POL-USD","COMP-USD","DRIFT-USD",
  "MKR-USD","AVAX-USD","SUI-USD","ONDO-USD","CRO-USD","ORCA-USD",
  "AAVE-USD","NEAR-USD","RARE-USD","ZRO-USD","WIF-USD","SEI-USD",
  "JASMY-USD","ZORA-USD",
];

// Symbol → full display name. Crypto keyed WITH -USD, stocks keyed bare.
// User-added tickers have no entry — displayName() falls back to the raw symbol.
const SYMBOL_NAMES = {
  "BTC-USD": "Bitcoin", "ETH-USD": "Ethereum", "SOL-USD": "Solana",
  "XRP-USD": "XRP", "LTC-USD": "Litecoin", "ADA-USD": "Cardano",
  "DOGE-USD": "Dogecoin", "DOT-USD": "Polkadot", "LINK-USD": "Chainlink",
  "POL-USD": "Polygon", "COMP-USD": "Compound", "DRIFT-USD": "Drift",
  "MKR-USD": "Maker", "AVAX-USD": "Avalanche", "SUI-USD": "Sui",
  "ONDO-USD": "Ondo", "CRO-USD": "Cronos", "ORCA-USD": "Orca",
  "AAVE-USD": "Aave", "NEAR-USD": "NEAR", "RARE-USD": "SuperRare",
  "ZRO-USD": "LayerZero", "WIF-USD": "dogwifhat", "SEI-USD": "Sei",
  "JASMY-USD": "JasmyCoin", "ZORA-USD": "Zora",
  "AAPL": "Apple", "MSFT": "Microsoft", "NVDA": "Nvidia", "GOOGL": "Alphabet",
  "AMZN": "Amazon", "META": "Meta", "TSLA": "Tesla", "AMD": "AMD",
  "NFLX": "Netflix", "JPM": "JPMorgan",
};
// Short ticker for the line under the name: crypto without -USD ("BTC"), stock bare.
const shortTicker = (symbol) => symbol.replace(/-USD$/, "");
// Canonical colored-text asset tag (matches the QuickView add-popup style),
// keyed off the app-wide getProvider predicate. crypto #818cf8 / stock #f97316.
function assetTag(symbol) {
  const isCrypto = getProvider(symbol) === "coinbase";
  return (
    <span style={{
      fontSize: "11px",
      fontWeight: 600,
      color: isCrypto ? "#a5b0ff" : "#ffb86b",
      background: isCrypto ? "rgba(129,140,248,0.14)" : "rgba(249,115,22,0.14)",
      border: `0.5px solid ${isCrypto ? "rgba(129,140,248,0.3)" : "rgba(249,115,22,0.35)"}`,
      padding: "1px 8px",
      borderRadius: "5px"
    }}>
      {isCrypto ? "Crypto" : "Stock"}
    </span>
  );
}
// Full display name with graceful fallback to the raw symbol.
// Backed by the FULL asset universe (ASSET_LIBRARY, 226 names — single source of truth);
// SYMBOL_NAMES stays as a legacy override for any symbol not in the universe.
const NAME_BY_SYMBOL = ASSET_LIBRARY.reduce((m, a) => { m[a.symbol] = a.name; return m; }, {});
const displayName = (symbol) => NAME_BY_SYMBOL[symbol] || SYMBOL_NAMES[symbol] || symbol;

// Per-engine notification prefs → stored-grade vocabulary for get_my_notifications(p_grades).
// Module scope (pure, no state) so the effects that call it don't need it as a dependency.
// V2 (buffet) retired to silent background (founder call 2026-07-10): the worker keeps
// writing engine:"v2" rows for the engine comparison, but the app hides them everywhere
// (refine filter below) and the V2 notification toggles are hidden in Settings.
// Flip to false to fully revive. Mirrors worker.cjs V2_NOTIFY_ACTIVE.
const V2_RETIRED = true;
// V5 (pullback) REPLAY-FALSIFIED at every configuration tried (2026-07-10, see
// v5Detector.cjs) — the worker writes v5 rows silently for the live engine
// comparison, but the app hides them and sends no notifications. Flip to false
// (plus worker.cjs V5_NOTIFY_ACTIVE) if live specimens redeem the setup.
const V5_SILENT = true;
// V2B's 1d mode is LIVE (founder call 2026-07-10, upgraded from silent same
// day). Pairs with worker.cjs V2B_1D_NOTIFY_ACTIVE=true. Flip both to true/
// false together to re-silence the 1d pilot.
const V2B_1D_SILENT = false;
const NOTIF_STORED_BY_V2 = { A: "Strong+Room", B: "Strong", C: "High Potential", Watchlist: "Watchlist" };
const NOTIF_STORED_BY_V3 = { Prime: "Strong+Room", Strong: "Strong", Base: "High Potential" };
// New Claude-piloted engines (2026-07-10) share the Prime/Strong/Base display
// vocabulary and the same stored-grade mapping as V3. Engine ids: v2b = the NEW
// V2 (breakout-hold; retired buffet keeps id "v2"), v4 = trading range, v5 =
// pullback. The app renders v2b's chip as "V2".
const NOTIF_STORED_BY_PSB = { Prime: "Strong+Room", Strong: "Strong", Base: "High Potential" };
// Engine chip rendering — one source of truth for text/colors across the four
// chip sites + Settings. V2B renders as "V2" (it IS version two to users).
const ENGINE_CHIP_TEXT = { V1: "V1", V2: "V2", V2B: "V2", V3: "V3", V4: "V4", V5: "V5" };
const ENGINE_CHIP_BG = { V2: "#f97316", V2B: "#f97316", V3: "#2dd4bf", V4: "#a78bfa", V5: "#fb7185" };
const engineChipStyle = (v) => ({
  text: ENGINE_CHIP_TEXT[v] || v,
  bg: ENGINE_CHIP_BG[v] || "rgba(129,140,248,0.18)",
  fg: ENGINE_CHIP_BG[v] ? "#0b0b14" : "#c7d2fe",
});
// mapSignalRowToNote — ONE mapping from a signals row to the note object every
// card renders (History, SIGNALS panel, Followed Trades). Extracted 2026-07-11
// so Followed Trades hydration (get_my_followed_trades RPC) produces identical
// objects to the notification loader. Pure: row + the user's trade prefs in,
// note out.
const mapSignalRowToNote = (row, tradeAmount, tradeFee) => ({
  id: row.id,
  coin: row.asset,
  timeframe: row.timeframe,
  grade: row.grade,
  tier: row.tier_label,
  type: "trade",
  timestamp: Date.parse(row.fired_at),
  entry: row.entry_price,
  exit: row.exit_price,
  profitPct: row.est_profit_pct,
  estProfit: ((tradeAmount * row.est_profit_pct) / 100) - ((tradeAmount * tradeFee) / 100),
  fvgs: row.fvgs,
  supportResistance: row.support_resistance,
  // Setup-type label (founder ask 2026-07-09, two-line form locked same day).
  ...(() => {
    const s = row.fingerprint && row.fingerprint.setup;
    const m = {
      bounce: { kind: "Trading Range", side: "Range Low" },
      engulf: { kind: "Trading Range", side: "Range High" },
      conviction: { kind: "Trading Range", side: "Range High" },
      grind: { kind: "Trading Range", side: "Range High" },
      breakout: { kind: "Breakout", side: null },
      momentum: { kind: "General", side: null },
      "trend-pullback": { kind: "Trend Line", side: null },
      "breakout-hold": { kind: "Breakout", side: "Break Held" },
      "range-low": { kind: "Trading Range", side: "Range Low" },
      pullback: { kind: "Pullback", side: null },
    };
    const hit = s ? (m[s] || { kind: s, side: null }) : { kind: null, side: null };
    return { setupKind: hit.kind, setupSide: hit.side };
  })(),
  // Engine-version tag. Rows fired before tagging shipped have no
  // fingerprint.engine → default "v1".
  version: (row.fingerprint && row.fingerprint.engine) === "v2" ? "V2"
    : (row.fingerprint && row.fingerprint.engine) === "v3" ? "V3"
    : (row.fingerprint && row.fingerprint.engine) === "v2b" ? "V2B"
    : (row.fingerprint && row.fingerprint.engine) === "v4" ? "V4"
    : (row.fingerprint && row.fingerprint.engine) === "v5" ? "V5"
    : "V1",
  gradeLabel: (row.fingerprint && row.fingerprint.engine && row.fingerprint.engine !== "v1" && row.fingerprint.displayGrade)
    ? row.fingerprint.displayGrade
    : row.grade,
  tags: (row.fingerprint && Array.isArray(row.fingerprint.tags)) ? row.fingerprint.tags : [],
});
const deriveStoredGrades = (prefs) => {
  const s = new Set();
  (Array.isArray(prefs.v1) ? prefs.v1 : []).forEach((g) => s.add(g)); // v1 uses stored vocab directly
  (prefs.v2 || []).forEach((g) => NOTIF_STORED_BY_V2[g] && s.add(NOTIF_STORED_BY_V2[g]));
  (prefs.v3 || []).forEach((g) => NOTIF_STORED_BY_V3[g] && s.add(NOTIF_STORED_BY_V3[g]));
  (prefs.v2b || []).forEach((g) => NOTIF_STORED_BY_PSB[g] && s.add(NOTIF_STORED_BY_PSB[g]));
  (prefs.v4 || []).forEach((g) => NOTIF_STORED_BY_PSB[g] && s.add(NOTIF_STORED_BY_PSB[g]));
  (prefs.v5 || []).forEach((g) => NOTIF_STORED_BY_PSB[g] && s.add(NOTIF_STORED_BY_PSB[g]));
  return [...s];
};

// Tier color by signal STRENGTH (shared by SIGNALS panel + Notification History tier lines).
// green = positive setups, yellow = watch/awareness, red = caution, grey = pre-tier fallback.
const TIER_COLORS = {
  "Strong Trade Signal": "#4ade80",
  "Breakout Candidate": "#4ade80",
  "High Potential Setup": "#4ade80",
  "Watchlist Candidate": "#fde047",
  "Risky Setup": "#f87171",
};
const tierColorFor = (tier) => (tier && TIER_COLORS[tier]) || "#888";

// Trade History hidden from UI (code preserved for future revival — flip to true to restore).
const SHOW_TRADE_HISTORY = false;

// Admin Portal (Build 1): the one founder account allowed to see the Admin tab. Single
// client-side source of truth, reused by the nav-array filter and the render-block gate.
// This is NOT the security boundary — the admin_list_accounts() RPC enforces the same uid
// via SECURITY DEFINER + WHERE auth.uid() server-side; this const only hides the tab UI.
const FOUNDER_UID = "5e1ab1e6-7288-498b-8957-c1efbcd289fe";

// Updates-card display tags (display-only; read the numeric value, recompute nothing).
// Inclusive-low / exclusive-high bands; top band includes 100.
const rsiBandTag = (v) =>
  v < 20 ? { tag: "Very Oversold", color: "#c0392b" }
  : v < 30 ? { tag: "Oversold", color: "#c0392b" }
  : v < 45 ? { tag: "Bearish", color: "#ff6b6b" }
  : v < 55 ? { tag: "Neutral", color: "#fde047" }
  : v < 70 ? { tag: "Bullish", color: "#34e88a" }
  : v < 80 ? { tag: "Overbought", color: "#ffa552" }
  : { tag: "Very Overbought", color: "#ffa552" };

const stochBandTag = (v) =>
  v < 20 ? { tag: "Oversold", color: "#c0392b" }
  : v < 45 ? { tag: "Bearish but Climbing", color: "#ff6b6b" }
  : v < 55 ? { tag: "Neutral", color: "#fde047" }
  : v < 80 ? { tag: "Bullish", color: "#34e88a" }
  : { tag: "Overbought", color: "#ffa552" };

// MACD momentum tag from the metrics object's macd line, signal line, and the
// magnitude-correct macdWidening boolean (NOT the signed macdHistDir). Near-cross
// is checked first and overrides the geometry table.
const macdMomentumTag = (m) => {
  const { macd, signal, macdWidening: widening } = m;
  const denom = Math.max(Math.abs(macd), Math.abs(signal));
  if (Math.abs(macd - signal) < 0.10 * denom) return { tag: "No Clear Momentum", color: "#fde047" };
  const aboveSignal = macd > signal;
  const aboveZero = macd > 0;
  if (aboveSignal && !aboveZero && widening)  return { tag: "Bullish Momentum Recovering", color: "#34e88a" };
  if (aboveSignal && !aboveZero && !widening) return { tag: "Momentum Fading", color: "#fde047" };
  if (aboveSignal && aboveZero && widening)   return { tag: "Strong Bullish Momentum", color: "#00ff9d" };
  if (aboveSignal && aboveZero && !widening)  return { tag: "Bullish Momentum Weakening", color: "#8fd9a8" };
  if (!aboveSignal && aboveZero && widening)  return { tag: "Bearish Momentum Building", color: "#ff6b6b" };
  if (!aboveSignal && aboveZero && !widening) return { tag: "Bearish Momentum Fading", color: "#e89a9a" };
  if (!aboveSignal && !aboveZero && widening) return { tag: "Strong Bearish Momentum", color: "#c0392b" };
  return { tag: "Bearish Momentum Fading", color: "#e89a9a" };
};


function App() {
    const [livePrices, setLivePrices] = useState({});
    const [tradeSignal, setTradeSignal] = useState("");
    const [entryPrice, setEntryPrice] = useState(null);
    const [exitPrice, setExitPrice] = useState(null);
    // Persist the viewed asset across refresh (founder ask 2026-07-09) — same guarded
    // lazy-init pattern as activeTab. Validated against the asset universe.
    const [selectedCoin, setSelectedCoin] = useState(() => {
      try {
        const stored = localStorage.getItem("selectedCoin");
        return stored && ASSET_LIBRARY.some((a) => a.symbol === stored) ? stored : "BTC-USD";
      } catch {
        return "BTC-USD";
      }
    });
    const [tradeAmount, setTradeAmount] = useState(10000);
    const [tradeFee, setTradeFee] = useState(0.1);
    const [profitTargetLow, setProfitTargetLow] = useState(CONFIG.profitTarget.low);
    const [profitTargetHigh, setProfitTargetHigh] = useState(CONFIG.profitTarget.high);
    const [scanFrequency, setScanFrequency] = useState(15);
    const [potentialProfit, setPotentialProfit] = useState(0);
    const [followingTrade, setFollowingTrade] = useState(false);
    const [tradeLog, setTradeLog] = useState([]);
    const [aiGrade, setAiGrade] = useState(null);
    const [activeTab, setActiveTab] = useState(() => {
      try {
        const stored = localStorage.getItem("activeTab");
        const valid = ["quick_view", "dashboard", "coin_updates", "followed", "alerts", "settings", "admin"];
        if (SHOW_TRADE_HISTORY) valid.push("history");
        return valid.includes(stored) ? stored : "dashboard";
      } catch {
        return "dashboard";
      }
    });
    const [menuCollapsed, setMenuCollapsed] = useState(true);
    // Enable all timeframes including 6h and 1d by default
    const [timeframes, setTimeframes] = useState({ "1m": false, "5m": true, "15m": true, "1h": true, "6h": true, "1d":true });
    // TEMP HIDDEN — tradeStyles state parked with the timeframe selector UI (its only
    // reader). Revive together. Keep for later, do not delete.
    // const [tradeStyles, setTradeStyles] = useState({
    //   scalp: true,
    //   swing: true,
    //   day: true
    // });
    const [notificationLog, setNotificationLog] = useState([]);
    const [signalGradeFilter, setSignalGradeFilter] = useState("All"); // SIGNALS panel grade filter
    const [followedTrades, setFollowedTrades] = useState([]);
    const [recentAlertsMap, setRecentAlertsMap] = useState({});
    const [selectedTimeframe, setSelectedTimeframe] = useState("1d"); // Default to 1-day chart
    const [userMode, setUserMode] = useState("Moderate");
    // Notification prefs — PER ENGINE (founder ask 2026-07-09). Display-grade toggles for
    // v2 (A/B/C/Watchlist) and v3 (Prime/Strong/Base); v1 = one legacy master switch.
    // Persisted to profiles.settings.notifPrefs. The get_my_notifications RPC still takes
    // stored-grade vocabulary (p_grades) — deriveStoredGrades() maps these prefs onto it
    // (coarse server-side cut), then loadNotifications refines by engine + displayGrade.
    // Defaults = everything on → identical to prior behavior until the user changes it.
    // v1 = per-grade array like v2/v3 (founder call 2026-07-09): ONLY Strong+Room on by
    // default — everything else legacy opt-in.
    const [notifPrefs, setNotifPrefs] = useState({ v1: ["Strong+Room"], v2: ["A", "B", "C", "Watchlist"], v3: ["Prime", "Strong", "Base"], v2b: ["Prime", "Strong", "Base"], v4: ["Prime", "Strong", "Base"], v5: ["Prime", "Strong", "Base"] });
    // Settings page (Build A): active subtab + current-user email for the Account subtab.
    // Build C: persist the active subtab across refresh via localStorage (browser UI
    // position, NOT account data — same guarded lazy-init pattern as activeTab above).
    const [settingsSubtab, setSettingsSubtab] = useState(() => {
      try {
        const stored = localStorage.getItem("settingsActiveSubtab");
        const valid = ["account", "preferences", "notifications", "trading_logic", "terms", "privacy"];
        return valid.includes(stored) ? stored : null;
      } catch {
        return null;
      }
    }); // null = card-grid landing; key = section open
    const [userEmail, setUserEmail] = useState("");
    useEffect(() => {
      let cancelled = false;
      supabase.auth.getSession().then(({ data }) => {
        if (!cancelled) setUserEmail(data?.session?.user?.email || "");
      });
      return () => { cancelled = true; };
    }, []);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    // Track viewport across rotation/resize (founder mobile pass 2026-07-09). Drives
    // layout-only switches (SIGNALS rail hidden, stat row wraps) — nothing visual changes
    // on desktop.
    useEffect(() => {
      const onResize = () => setIsMobile(window.innerWidth <= 768);
      window.addEventListener("resize", onResize);
      return () => window.removeEventListener("resize", onResize);
    }, []);
    // Phase 3.3: first render seeds from CODE defaults (no localStorage). The
    // authenticated load effect below replaces this wholesale from per-user Supabase
    // rows (or seeds + persists the defaults when the user's row set is empty).
    // userId is null until the session resolves; write sites guard on it.
    const [userId, setUserId] = useState(null);
    // Phase 3.3b: subscription tier for the current user (drives follow-cap enforcement).
    // Defaults to the most restrictive tier ("scout") until the profiles read resolves.
    const [tier, setTier] = useState("scout");
    // Admin Portal (Build 1): account-list view state. Fetched on entering the Admin tab
    // via the admin_list_accounts() RPC (SECURITY DEFINER, server-side uid fence). Declared
    // after userId/activeTab so the fetch effect's deps reference initialized bindings.
    const [adminAccounts, setAdminAccounts] = useState([]);
    const [adminLoading, setAdminLoading] = useState(false);
    const [adminError, setAdminError] = useState("");
    useEffect(() => {
      // Fire only when the admin opens the Admin tab. The RPC also returns zero rows to any
      // non-admin caller (its WHERE auth.uid() predicate), so this client gate is defense in
      // depth, not the boundary. Refetches each time the tab is (re)entered — fine for v1.
      if (activeTab !== "admin" || userId !== FOUNDER_UID) return;
      let cancelled = false;
      setAdminLoading(true);
      setAdminError("");
      supabase.rpc("admin_list_accounts").then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setAdminError(error.message); setAdminAccounts([]); }
        else setAdminAccounts(Array.isArray(data) ? data : []);
        setAdminLoading(false);
      });
      return () => { cancelled = true; };
    }, [activeTab, userId]);
    const [coins, setCoins] = useState([...defaultCryptos]);
    const [followedCoins, setFollowedCoins] = useState(() => {
             const defaults = {};
             defaultCryptos.forEach(coin => { defaults[coin] = true; });
             defaultStocks.forEach(ticker => { defaults[ticker] = true; });
             return defaults;
          });
    // Phase 5 Build 2: followable bare stock tickers (Alpaca). Kept separate from
    // the crypto `coins` const so the crypto universe stays byte-stable.
    const [stockSymbols, setStockSymbols] = useState([...defaultStocks]);
    // Dashboard quick-buttons: persisted, validated array of up to 5 symbols.
    // Lazy-init mirrors the overlayFilters pattern; on a clean store it returns
    // followedPool.slice(0,5) — byte-identical to the prior inline derivation.
    const [quickButtons, setQuickButtons] = useState(() => {
      const followedPool = [...coins, ...stockSymbols].filter(sym => followedCoins[sym]);
      try {
        const saved = localStorage.getItem("quickButtons");
        if (!saved) return followedPool.slice(0, 5);
        const parsed = JSON.parse(saved);
        const kept = Array.isArray(parsed)
          ? parsed.filter(sym => typeof sym === "string" && followedPool.includes(sym))
          : [];
        for (const sym of followedPool) {
          if (kept.length >= 5) break;
          if (!kept.includes(sym)) kept.push(sym);
        }
        return kept.slice(0, 5);
      } catch (e) {
        return followedPool.slice(0, 5);
      }
    });
    // Persist quick-buttons (guarded — private mode / quota failures are non-fatal).
    useEffect(() => {
      try { localStorage.setItem("quickButtons", JSON.stringify(quickButtons)); }
      catch (e) { /* ignore */ }
    }, [quickButtons]);
    // Stock quick-access — parallel to quickButtons but stocks-only, own 5-slot list + persistence.
    const [stockQuickButtons, setStockQuickButtons] = useState(() => {
      const followedStocks = stockSymbols.filter(sym => followedCoins[sym]);
      try {
        const saved = localStorage.getItem("stockQuickButtons");
        if (!saved) return followedStocks.slice(0, 5);
        const parsed = JSON.parse(saved);
        const kept = Array.isArray(parsed)
          ? parsed.filter(sym => typeof sym === "string" && followedStocks.includes(sym))
          : [];
        for (const sym of followedStocks) {
          if (kept.length >= 5) break;
          if (!kept.includes(sym)) kept.push(sym);
        }
        return kept.slice(0, 5);
      } catch (e) {
        return followedStocks.slice(0, 5);
      }
    });
    useEffect(() => {
      try { localStorage.setItem("stockQuickButtons", JSON.stringify(stockQuickButtons)); }
      catch (e) { /* ignore */ }
    }, [stockQuickButtons]);
    const [quickEditTab, setQuickEditTab] = useState("crypto"); // active sub-tab in the unified quick-access editor
    const [stockQuickEditDraft, setStockQuickEditDraft] = useState([]);
    const [stockQuickEditNudge, setStockQuickEditNudge] = useState(false); // transient flash when cap (5) is hit
    const [stockInput, setStockInput] = useState("");
    // QuickView crypto search box — filters visible crypto cards (no add this build).
    const [cryptoInput, setCryptoInput] = useState("");
    // Quick-access edit panel (Variant C): draft-edit the dashboard quick-buttons.
    // Draft seeds from current quickButtons on open; only Save commits via setQuickButtons.
    const [showQuickEdit, setShowQuickEdit] = useState(false);
    const [quickEditDraft, setQuickEditDraft] = useState([]);
    const [quickEditNudge, setQuickEditNudge] = useState(false); // transient flash when cap (5) is hit
    // Add-asset popup (typeahead over ASSET_LIBRARY).
    const [showAddPopup, setShowAddPopup] = useState(false);
    const [addQuery, setAddQuery] = useState("");
    const [addTypeFilter, setAddTypeFilter] = useState("all");  // "all" | "crypto" | "stock"
    // Chart-selector search dropdown (dashboard Crypto/Stocks cards) — replaces the old native
    // <select> with a searchable modal, same visual pattern as the Add-asset popup above.
    // null = closed, "crypto" | "stock" = which card opened it (picks the symbol pool + accent).
    const [chartSelectOpen, setChartSelectOpen] = useState(null);
    const [chartSelectQuery, setChartSelectQuery] = useState("");
    // Build D: delisting notice — assets removed on load because they left the catalog.
    const [showDelistedNotice, setShowDelistedNotice] = useState(false);
    const [delistedList, setDelistedList] = useState([]); // array of removed symbol strings
    // QuickView top filter bar — two independent axes.
    const [assetTypeFilter, setAssetTypeFilter] = useState("crypto");   // "crypto" | "stocks" (sub-tabs, no combined "all" view)
    const [directionFilter, setDirectionFilter] = useState("all");   // "all" | "positive" | "negative"
    // Phase 3.3b: per-tier follow caps. Counts WATCHLIST MEMBERSHIP per class (paused
    // assets still occupy a slot). Caps from tierLimits; tier defaults to "scout" until
    // the profiles read resolves. In scope for both add handlers and the Add-asset modal.
    const caps = limitsForTier(tier);
    const cryptoAtCap = coins.length >= caps.crypto;
    const stockAtCap = stockSymbols.length >= caps.stock;
    const addStock = (override) => {
      const ticker = (override ?? stockInput).trim().toUpperCase();
      if (!ticker) return;                                                   // reject empty
      if (ticker.includes("-")) return;                                      // dashed = crypto, not a stock here
      if (coins.includes(ticker) || stockSymbols.includes(ticker)) return;   // no dupes
      if (stockAtCap) return;                                                // Phase 3.3b: at tier stock cap — block new add
      setStockSymbols(prev => [...prev, ticker]);
      setFollowedCoins(prev => ({ ...prev, [ticker]: true }));               // seed the gate so the scan picks it up
      if (userId) insertFollow(userId, ticker, "stock");                     // Phase 3.3: persist to Supabase
      setStockInput("");
    };
    const removeStock = (ticker) => {
      setStockSymbols(prev => prev.filter(t => t !== ticker));
      setFollowedCoins(prev => {
        const next = { ...prev };
        delete next[ticker];
        return next;
      });
      if (userId) deleteFollow(userId, ticker);                              // Phase 3.3: persist to Supabase
    };
    // Build B: crypto add/remove, mirroring addStock/removeStock. removeCrypto is
    // wired to the crypto card ×; addCrypto is machinery only this build (no UI yet).
    const removeCrypto = (symbol) => {
      setCoins(prev => prev.filter(c => c !== symbol));
      setFollowedCoins(prev => {
        const next = { ...prev };
        delete next[symbol];
        return next;
      });
      if (userId) deleteFollow(userId, symbol);                              // Phase 3.3: persist to Supabase
    };
    const addCrypto = (symbol) => {
      const sym = symbol.trim().toUpperCase();
      if (!sym) return;
      if (!sym.includes("-")) return;                                 // crypto requires a dash pair
      if (coins.includes(sym) || stockSymbols.includes(sym)) return; // no dupes
      if (cryptoAtCap) return;                                        // Phase 3.3b: at tier crypto cap — block new add
      setCoins(prev => [...prev, sym]);
      setFollowedCoins(prev => ({ ...prev, [sym]: true }));
      if (userId) insertFollow(userId, sym, "crypto");                       // Phase 3.3: persist to Supabase
    };
    const recentAlertsRef = useRef({});
    // Previous MACD histogram per coin|timeframe, carried across scans for
    // grow/fade direction. Internal state (no prop mirror) — read before write.
    const prevMacdHistRef = useRef({});
    // Latest stockSymbols snapshot for fetchPrices (whose effect has a []
    // dep array). Read via ref so the price poller always sees current stocks
    // without re-arming the 10s interval.
    const stockSymbolsRef = useRef(stockSymbols);
    useEffect(() => { stockSymbolsRef.current = stockSymbols; }, [stockSymbols]);
    // Mirror for coins: fetchPrices ([] deps) reads coinsRef.current so it sees
    // a removed/added coin without re-arming the 10s price interval.
    const coinsRef = useRef(coins);
    useEffect(() => { coinsRef.current = coins; }, [coins]);
    // Build B: settings-hydration guard. Stays false until the authed load has applied the
    // saved Preferences (or confirmed none exist); the debounced save effect below returns early
    // while false, so rehydrating never writes the just-loaded values straight back.
    const hydratedRef = useRef(false);
    // Phase 3.3: load per-user follow-state from Supabase (followed_assets), replacing
    // the old localStorage persist effect. REPLACE-not-merge — an existing row set wins
    // wholesale; only a genuinely empty set seeds the code defaults (clean break, no
    // localStorage import). userId is set LAST so the delisting effect (keyed on userId)
    // runs against the freshly loaded/seeded state. Errors are intentionally NOT caught:
    // a missing-relation/permission failure must surface, never fall back to localStorage.
    useEffect(() => {
      let cancelled = false;
      (async () => {
        const { data: { session } } = await supabase.auth.getSession();
        const uid = session?.user?.id;
        if (!uid || cancelled) return;
        // Phase 3.3b: read the user's subscription tier (profiles.id = uid, RLS profiles_self_read).
        // A missing/unreadable/null tier degrades to the default "scout" (most restrictive) — never throws.
        try {
          const { data: profile, error: profileError } = await supabase
            .from("profiles")
            .select("tier, settings")
            .eq("id", uid)
            .single();
          if (!cancelled && !profileError && profile) {
            if (profile.tier) setTier(profile.tier);
            // Build B: rehydrate the five Preferences values from profiles.settings (JSONB).
            // Per-key type guard → a missing/null/garbage key keeps its code default.
            const s = profile.settings;
            if (s && typeof s === "object") {
              if (typeof s.profitTargetLow === "number") setProfitTargetLow(s.profitTargetLow);
              if (typeof s.profitTargetHigh === "number") setProfitTargetHigh(s.profitTargetHigh);
              if (typeof s.tradeAmount === "number") setTradeAmount(s.tradeAmount);
              if (typeof s.tradeFee === "number") setTradeFee(s.tradeFee);
              if (typeof s.userMode === "string") setUserMode(s.userMode);
              if (s.notifPrefs && typeof s.notifPrefs === "object") {
                setNotifPrefs({
                  // v1 migration: old boolean prefs (true/false) collapse to the new
                  // Strong+Room-only default; arrays pass through.
                  v1: Array.isArray(s.notifPrefs.v1) ? s.notifPrefs.v1 : ["Strong+Room"],
                  v2: Array.isArray(s.notifPrefs.v2) ? s.notifPrefs.v2 : ["A", "B", "C", "Watchlist"],
                  v3: Array.isArray(s.notifPrefs.v3) ? s.notifPrefs.v3 : ["Prime", "Strong", "Base"],
                  // New engines (2026-07-10): rows saved before they shipped have no
                  // keys for them → default all-on (Prime/Strong/Base).
                  v2b: Array.isArray(s.notifPrefs.v2b) ? s.notifPrefs.v2b : ["Prime", "Strong", "Base"],
                  v4: Array.isArray(s.notifPrefs.v4) ? s.notifPrefs.v4 : ["Prime", "Strong", "Base"],
                  v5: Array.isArray(s.notifPrefs.v5) ? s.notifPrefs.v5 : ["Prime", "Strong", "Base"],
                });
              }
            }
          }
        } catch (_) { /* leave defaults (tier "scout", code Preferences) */ }
        const rows = await fetchFollowedAssets(uid);
        if (cancelled) return;
        if (rows.length === 0) {
          // Empty → seed code defaults (all followed) and persist. In-memory state
          // already equals these defaults (initializers), so only the write is needed.
          await seedDefaults(uid, defaultCryptos, defaultStocks);
        } else {
          // Non-empty → REPLACE in-memory state wholesale from rows. Never merge.
          const nextCoins = rows.filter(r => r.asset_class === "crypto").map(r => r.symbol);
          const nextStocks = rows.filter(r => r.asset_class === "stock").map(r => r.symbol);
          const nextFollowed = {};
          rows.forEach(r => { nextFollowed[r.symbol] = r.is_followed; });
          if (cancelled) return;
          setCoins(nextCoins);
          setStockSymbols(nextStocks);
          setFollowedCoins(nextFollowed);
        }
        if (cancelled) return;
        setUserId(uid);
      })();
      return () => { cancelled = true; };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Server-fed notifications: the notification LIST is now a per-user read of graded `signals`
    // via the get_my_notifications() SECURITY DEFINER RPC (follow-set × tier × grade × timeframe,
    // scoped by auth.uid() server-side). Mirrors the getSession()→supabase pattern above. Runs on
    // mount and every 60s (matching SCAN_INTERVAL). estProfit is computed client-side from the
    // user's tradeAmount/tradeFee (the server carries only the % est_profit_pct), so the effect
    // re-runs when those prefs change to keep the dollar figure current.
    useEffect(() => {
      let cancelled = false;
      const loadNotifications = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id || cancelled) return;
        const { data, error } = await supabase.rpc("get_my_notifications", { p_grades: deriveStoredGrades(notifPrefs) });
        if (cancelled || error || !Array.isArray(data)) return;
        // Mapping extracted to module scope (mapSignalRowToNote) so Followed
        // Trades hydration renders identical objects — see 2026-07-11 persist fix.
        const mapped = data.map((row) => mapSignalRowToNote(row, tradeAmount, tradeFee));
        // Engine-aware refinement (per-engine prefs): the RPC filtered on stored grade,
        // which collides across engines (v2 A and v3 Prime both store "Strong+Room") —
        // refine here by engine + displayed grade.
        setNotificationLog(mapped.filter((n) =>
          n.version === "V2" ? (!V2_RETIRED && notifPrefs.v2.includes(n.gradeLabel))
          : n.version === "V3" ? notifPrefs.v3.includes(n.gradeLabel)
          : n.version === "V2B" ? (!(V2B_1D_SILENT && n.timeframe === "1d") && (notifPrefs.v2b || []).includes(n.gradeLabel))
          : n.version === "V4" ? (notifPrefs.v4 || []).includes(n.gradeLabel)
          : n.version === "V5" ? (!V5_SILENT && (notifPrefs.v5 || []).includes(n.gradeLabel))
          : (Array.isArray(notifPrefs.v1) && notifPrefs.v1.includes(n.gradeLabel))
        ));
      };
      loadNotifications();
      const interval = setInterval(loadNotifications, 60000); // 1 min, matches SCAN_INTERVAL
      return () => { cancelled = true; clearInterval(interval); };
    }, [tradeAmount, tradeFee, notifPrefs]);
    // Build B: debounced (~600ms trailing) per-user persist of the five Preferences values to
    // profiles.settings. Uses .update (NOT .upsert) — the signup trigger guarantees the profiles
    // row exists and we deliberately grant no client INSERT, so .upsert's INSERT path would fail;
    // do NOT "fix" this to .upsert. hydratedRef gates the write: the first userId-settled run (after
    // the load above applied the saved values / confirmed none) flips it true and returns WITHOUT
    // saving — preventing the rehydrate→save write-back loop. The first real USER edit persists.
    useEffect(() => {
      if (!userId) return;
      if (!hydratedRef.current) { hydratedRef.current = true; return; }
      const t = setTimeout(() => {
        supabase
          .from("profiles")
          .update({ settings: { profitTargetLow, profitTargetHigh, tradeAmount, tradeFee, userMode, notifPrefs, notifGrades: deriveStoredGrades(notifPrefs) } })
          .eq("id", userId)
          .then(({ error }) => { if (error) console.warn("[settings] save failed:", error.message); });
      }, 600);
      return () => clearTimeout(t);
    }, [profitTargetLow, profitTargetHigh, tradeAmount, tradeFee, userMode, notifPrefs, userId]);
    // Build D (Phase 3.3): after the per-user load resolves (effect keyed on userId),
    // drop any followed asset no longer in the static catalog (delisted). Exact-symbol
    // membership (formats are byte-identical — no transform). Now runs against the
    // LOADED state (not the mount defaults) and persists each removal to Supabase via
    // deleteFollow. Re-keyed from [] → [userId] so deleteFollow has a userId and the
    // catalog check sees the user's real assets; runs once per resolved user.
    useEffect(() => {
      if (!userId) return;
      const catalogSet = new Set(ASSET_LIBRARY.map(a => a.symbol));
      const watchlist = [...coins, ...stockSymbols];
      const delisted = watchlist.filter(sym => !catalogSet.has(sym));
      if (delisted.length === 0) return;
      setCoins(prev => prev.filter(c => !delisted.includes(c)));
      setStockSymbols(prev => prev.filter(t => !delisted.includes(t)));
      setFollowedCoins(prev => {
        const next = { ...prev };
        delisted.forEach(s => delete next[s]);
        return next;
      });
      delisted.forEach(s => deleteFollow(userId, s));   // Phase 3.3: persist delisting removal
      // selectedCoin (useState("BTC-USD"), a bare symbol string) would dangle if it
      // was one of the removed assets — reset it to the first surviving asset.
      if (delisted.includes(selectedCoin)) {
        const survivingCoins = coins.filter(c => !delisted.includes(c));
        const survivingStocks = stockSymbols.filter(t => !delisted.includes(t));
        const fallback = survivingCoins[0] ?? survivingStocks[0];
        if (fallback) setSelectedCoin(fallback);
      }
      setDelistedList(delisted);
      setShowDelistedNotice(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);
    const [chartData, setChartData] = useState([]);
    const [selectedInterval, setSelectedInterval] = useState("1h");
    // Dashboard-only chart interval — SEPARATE from selectedInterval (which the More Info
    // chart reads); keeping them decoupled means the dashboard toggle can't perturb More Info.
    // Persisted like selectedCoin (founder ask 2026-07-09) — refresh returns to the
    // same asset AND timeframe.
    const [dashboardInterval, setDashboardInterval] = useState(() => {
      try {
        const stored = localStorage.getItem("dashboardInterval");
        return ["1h", "6h", "1d"].includes(stored) ? stored : "1h";
      } catch {
        return "1h";
      }
    });
    // Dashboard-local RSI/MACD subpanel toggles — SEPARATE from More Info's overlayFilters.
    // Default off (zero indicator-chart cost on load); global in-session, not per-symbol.
    const [dashShowRSI, setDashShowRSI] = useState(false);
    const [dashShowMACD, setDashShowMACD] = useState(false);
    const [dashShowBB, setDashShowBB] = useState(false);
    const [dashShowVolProfile, setDashShowVolProfile] = useState(false);
    const [dashShowLevels, setDashShowLevels] = useState(false);
    // Per-category Levels visibility (Build 3) — sub-toggles under the Levels master. Global
    // in-session; all default ON, reset to all-true whenever the Levels master flips on.
    const [dashLevelCats, setDashLevelCats] = useState({ support: true, resistance: true, fvg: true });
    // More Info chart page — full note stashed for later overlay builds; Build 1 reads coin+timeframe only.
    const [selectedNote, setSelectedNote] = useState(null);
    const moreInfoChartRef = useRef(null);
    const moreInfoRsiChartRef = useRef(null);   // RSI subpanel container
    const moreInfoChartObjRef = useRef(null);   // holds the chart instance
    const moreInfoSeriesRef = useRef(null);      // holds the candlestick series
    const moreInfoPriceLinesRef = useRef([]);    // holds IPriceLine handles for teardown
    const moreInfoFvgSeriesRef = useRef([]);     // holds Baseline series handles for FVG band teardown
    const moreInfoLastTimeRef = useRef(null);    // last candle time, for FVG band right-edge
    const moreInfoIndicatorsRef = useRef(null);  // holds {rsi,macd,stochRsi} series for subpanels
    const moreInfoRsiChartObjRef = useRef(null); // RSI subpanel chart instance
    const moreInfoRsiSyncRef = useRef(null);     // {paneKey: logical-range handler} map, for unsubscribe on teardown
    const moreInfoPriceXhairRef = useRef(null);  // overlay vertical line over the price chart
    const moreInfoRsiXhairRef = useRef(null);    // overlay vertical line over the RSI pane
    const moreInfoXhairHandlersRef = useRef(null); // { priceXhairHandler, rsiXhairHandler, macdXhairHandler } for unsubscribe
    const moreInfoPriceLegendRef = useRef(null);        // OHLCV hover-legend div (top-left of More Info price pane)
    const moreInfoLegendHandlerRef = useRef(null);      // price-pane legend crosshair handler, for unsubscribe
    const moreInfoRsiLegendRef = useRef(null);          // RSI hover-legend div (top-left of RSI pane)
    const moreInfoMacdLegendRef = useRef(null);         // MACD hover-legend div (top-left of MACD pane)
    const moreInfoMacdChartRef = useRef(null);   // MACD subpanel container
    const moreInfoMacdChartObjRef = useRef(null);// MACD subpanel chart instance
    const moreInfoMacdXhairRef = useRef(null);   // overlay vertical line over the MACD pane
    // Overlay filter state — persisted to localStorage, guarded read (defaults on any error/miss).
    // Per-field Number.isInteger/typeof guards mean an OLD persisted blob (e.g. with srCount,
    // no supportCount/resistanceCount) degrades cleanly to the defaults below.
    const [overlayFilters, setOverlayFilters] = useState(() => {
      const defaults = { showEntryExit: true, showMarker: true, showRSI: true, showMACD: true, showBB: false, showVolProfile: false };
      try {
        const saved = localStorage.getItem("moreInfoOverlayFilters");
        if (!saved) return defaults;
        const parsed = JSON.parse(saved);
        return {
          showEntryExit: typeof parsed.showEntryExit === "boolean" ? parsed.showEntryExit : defaults.showEntryExit,
          showMarker: typeof parsed.showMarker === "boolean" ? parsed.showMarker : defaults.showMarker,
          showRSI: typeof parsed.showRSI === "boolean" ? parsed.showRSI : defaults.showRSI,
          showMACD: typeof parsed.showMACD === "boolean" ? parsed.showMACD : defaults.showMACD,
          showBB: typeof parsed.showBB === "boolean" ? parsed.showBB : defaults.showBB,
          showVolProfile: typeof parsed.showVolProfile === "boolean" ? parsed.showVolProfile : defaults.showVolProfile,
        };
      } catch (e) {
        return defaults;
      }
    });
    // Per-note overlay visibility — a Set of hidden level keys ("S","R2","F1",...).
    // Per-note (resets on note change); NOT persisted (index identity isn't stable across notes).
    const [hiddenOverlays, setHiddenOverlays] = useState(() => new Set());
    const toggleOverlay = (key) =>
      setHiddenOverlays((prev) => {
        const next = new Set(prev);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });
    // Per-type master switch — per-note, resets with hiddenOverlays. master false ⇒ whole
    // type is masked off (draw skips it, chips disabled). master true ⇒ chips clickable.
    const [overlayMaster, setOverlayMaster] = useState({ support: true, resistance: true, fvg: true });
    // OFF: just lower the flag (draw skips the type; chips disable). Leave hidden as-is.
    const masterOff = (type) => setOverlayMaster((m) => ({ ...m, [type]: false }));
    // ON: raise the flag AND hide every key for the type, so re-enabling starts blank
    // (founder spec — nothing auto-selected; user picks chips back on).
    const masterOn = (type, keys) => {
      setHiddenOverlays((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));
        return next;
      });
      setOverlayMaster((m) => ({ ...m, [type]: true }));
    };
    // Master is OFF and a chip was clicked: revive the type with ONLY that chip shown.
    // Hide every key for the type, then un-hide the clicked one → exactly one level visible.
    const reviveWithOne = (type, keys, clickedKey) => {
      setHiddenOverlays((prev) => {
        const next = new Set(prev);
        keys.forEach((k) => next.add(k));   // hide all of this type
        next.delete(clickedKey);            // except the one clicked
        return next;
      });
      setOverlayMaster((m) => ({ ...m, [type]: true }));
    };
    // Chart-ready flag so overlays draw on first open (series is created async after fetch).
    const [moreInfoReady, setMoreInfoReady] = useState(false);
    // Load-failure UI (stopgap for the rate-limit blank-chart bug — see
    // docs/known-issue-rate-limiting-blank-charts.md). Same pattern as SignalChart.js.
    const [moreInfoLoadError, setMoreInfoLoadError] = useState(null);
    const [moreInfoRetryTick, setMoreInfoRetryTick] = useState(0);
    // Drawing tools (v1: horizontal lines only, context "more_info" — separate memory from
    // the Dashboard chart's "dashboard" context. Mirrors SignalChart.js's implementation;
    // see src/db/migrations/0005_chart_drawings.sql).
    const [moreInfoDrawings, setMoreInfoDrawings] = useState([]); // [{id, price}]
    const [moreInfoDrawMode, setMoreInfoDrawMode] = useState(false);
    const moreInfoDrawingLinesRef = useRef([]);
    // Measure tool (v1: price/%/candle-count click-drag readout, persisted per user × symbol
    // × context "more_info" via Supabase chart_measurements — see measureTool.js. Shared
    // hook with SignalChart.js so this isn't a second copy-pasted implementation).
    const moreInfoGranularitySeconds = granularityMap[selectedNote?.timeframe || selectedInterval] || 3600;
    const moreInfoMeasureSymbol = selectedNote?.coin || selectedCoin;
    const { measureMode: moreInfoMeasureMode, setMeasureMode: setMoreInfoMeasureMode, measurement: moreInfoMeasurement, stats: moreInfoMeasureStats, clearMeasurement: clearMoreInfoMeasurement } = useMeasureTool({
      chartRef: moreInfoChartObjRef, seriesRef: moreInfoSeriesRef, containerRef: moreInfoChartRef,
      granularitySeconds: moreInfoGranularitySeconds,
      userId, symbol: moreInfoMeasureSymbol, context: "more_info",
    });
    // Trend Line tool (v1 of the "advanced drawing tools" build: multi-point, persisted-list
    // click-drag lines, per user × symbol × context "more_info" via Supabase
    // chart_advanced_drawings — see trendLineTool.js. Shared hook with SignalChart.js).
    const { trendLineMode: moreInfoTrendLineMode, setTrendLineMode: setMoreInfoTrendLineMode, myLines: moreInfoTrendLines, deleteTrendLine: deleteMoreInfoTrendLine, projectedLines: moreInfoTrendProjectedLines } = useTrendLineTool({
      chartRef: moreInfoChartObjRef, seriesRef: moreInfoSeriesRef, containerRef: moreInfoChartRef,
      userId, symbol: moreInfoMeasureSymbol, context: "more_info",
    });
    // Fib Retracement tool (v1: 2-point click-drag, persisted list — see fibTool.js).
    const { fibMode: moreInfoFibMode, setFibMode: setMoreInfoFibMode, myFibs: moreInfoFibs, deleteFib: deleteMoreInfoFib, projectedFibs: moreInfoProjectedFibs } = useFibTool({
      chartRef: moreInfoChartObjRef, seriesRef: moreInfoSeriesRef, containerRef: moreInfoChartRef,
      userId, symbol: moreInfoMeasureSymbol, context: "more_info",
    });
    // Parallel Channel tool (v1: click-drag main line + one more click for the offset —
    // see channelTool.js for the 2-stage interaction).
    const { channelMode: moreInfoChannelMode, setChannelMode: setMoreInfoChannelMode, myChannels: moreInfoChannels, deleteChannel: deleteMoreInfoChannel, projectedChannels: moreInfoProjectedChannels, phase: moreInfoChannelPhase } = useChannelTool({
      chartRef: moreInfoChartObjRef, seriesRef: moreInfoSeriesRef, containerRef: moreInfoChartRef,
      userId, symbol: moreInfoMeasureSymbol, context: "more_info",
    });
    // Ray tool (v1: 2-point click-drag, persisted list — see rayTool.js).
    const { rayMode: moreInfoRayMode, setRayMode: setMoreInfoRayMode, myRays: moreInfoRays, deleteRay: deleteMoreInfoRay, projectedRays: moreInfoProjectedRays, phase: moreInfoRayPhase } = useRayTool({
      chartRef: moreInfoChartObjRef, seriesRef: moreInfoSeriesRef, containerRef: moreInfoChartRef,
      userId, symbol: moreInfoMeasureSymbol, context: "more_info",
    });
    // Long Position tool (v1: click-drag entry+risk, target auto-derived at a fixed 2:1
    // reward:risk — see longPositionTool.js. Last of the original 4-tool plan).
    const { longMode: moreInfoLongMode, setLongMode: setMoreInfoLongMode, myPositions: moreInfoPositions, deletePosition: deleteMoreInfoPosition, projectedPositions: moreInfoProjectedPositions } = useLongPositionTool({
      chartRef: moreInfoChartObjRef, seriesRef: moreInfoSeriesRef, containerRef: moreInfoChartRef,
      userId, symbol: moreInfoMeasureSymbol, context: "more_info",
    });
    // Mutual exclusion — only one drawing/measure tool armed at a time on the More Info
    // chart. Mirrors SignalChart.js's armTool.
    const armMoreInfoTool = useCallback((tool) => {
      setMoreInfoMeasureMode(tool === "measure");
      setMoreInfoDrawMode(tool === "draw");
      setMoreInfoTrendLineMode(tool === "trend");
      setMoreInfoFibMode(tool === "fib");
      setMoreInfoChannelMode(tool === "channel");
      setMoreInfoRayMode(tool === "ray");
      setMoreInfoLongMode(tool === "long");
    }, [setMoreInfoMeasureMode, setMoreInfoDrawMode, setMoreInfoTrendLineMode, setMoreInfoFibMode, setMoreInfoChannelMode, setMoreInfoRayMode, setMoreInfoLongMode]);
    const [lastChartUpdate, setLastChartUpdate] = useState(Date.now());
    const handleIntervalChange = (interval) => {
      setSelectedInterval(interval);
    };
    const [gradeTimestampMap, setGradeTimestampMap] = useState({});
    const [coinUpdateNotifications, setCoinUpdateNotifications] = useState([]);

    // Coin Updates filter state
    const [coinUpdateFilter, setCoinUpdateFilter] = useState("all");
    // Timeframe filter for Coin Updates
    const [timeframeFilter, setTimeframeFilter] = useState("all");
    // Asset-class filter for Coin Updates
    const [assetClassFilter, setAssetClassFilter] = useState("all");
    // Asset-class filter for Notification History (independent of Coin Updates)
    const [notifClassFilter, setNotifClassFilter] = useState("all"); // "all"|"crypto"|"stock"
    const [historySearch, setHistorySearch] = useState("");          // Notification History symbol search
    const [historyGradeFilter, setHistoryGradeFilter] = useState("All"); // Notification History grade filter
    const [historySetupFilter, setHistorySetupFilter] = useState("All"); // Notification History kind-of-trade filter (setupKind)
    // Soft-dismissed signal ids (per-user). Optimistic hide so a dismissed card vanishes
    // immediately without waiting for the 60s get_my_notifications reload (which the server
    // GATE 4 filter will then also exclude). Set of signals.id (note.id).
    const [dismissedIds, setDismissedIds] = useState(() => new Set());
    // Dismiss a Notification History card: optimistically hide, then persist. On write error,
    // revert the optimistic hide (do not leave the UI lying) and log. Only trade cards carry a
    // real note.id (signals.id); guarded on id + userId.
    const handleDismissNotification = (id) => {
      if (id == null || !userId) return;
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      dismissSignal(userId, id).catch((err) => {
        console.error("[dismiss-error]", id, err && err.message ? err.message : err);
        setDismissedIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      });
    };

    // Fetch and store all candle data in local state
    const [allCandles, setAllCandles] = useState({});

    
    // Cache candles and refresh every 2 minutes
    useEffect(() => {
      const loadAllCandles = async () => {
        const data = {};
        for (const coin of [...coins, ...stockSymbols]) {
          data[coin] = {};
          for (const [label, secs] of Object.entries(granularityMap)) {
            try {
              // Centralized fetch (marketData.js): same provider routing + data shape
              // as the old inline axios/fetchAlpacaCandles branch, now queued + cached.
              const raw = await getCandles(coin, secs);
              data[coin][label] = formatCandles(normalizeCandles(raw)).slice(-50);
              // space out requests to avoid rate limits
              await new Promise(resolve => setTimeout(resolve, 200));
            } catch (err) {
              console.error(`Error loading candles for ${coin} (${label}):`, err);
              data[coin][label] = [];
            }
          }
        }
        setAllCandles(data);
      };
      loadAllCandles();
      const cacheInterval = setInterval(loadAllCandles, 120000);
      return () => clearInterval(cacheInterval);
    }, [coins, stockSymbols]);


    useEffect(() => {
      const handleResize = () => {};
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    
    useEffect(() => {
      if (activeTab === "alerts") {
      }
    }, [activeTab]);
    useEffect(() => {
      try { localStorage.setItem("activeTab", activeTab); } catch {}
    }, [activeTab]);
    // Persist viewed asset + timeframe (founder ask 2026-07-09) — refresh restores both.
    useEffect(() => {
      try { localStorage.setItem("selectedCoin", selectedCoin); } catch {}
    }, [selectedCoin]);
    useEffect(() => {
      try { localStorage.setItem("dashboardInterval", dashboardInterval); } catch {}
    }, [dashboardInterval]);
    // Build C: persist active Settings subtab (string key) / clear on return to landing (null).
    useEffect(() => {
      try {
        if (settingsSubtab) localStorage.setItem("settingsActiveSubtab", settingsSubtab);
        else localStorage.removeItem("settingsActiveSubtab");
      } catch {}
    }, [settingsSubtab]);
const [dayChangeMap, setDayChangeMap] = useState({});
    let macdHistNow = 0;
    let macdHistPrev = 0;


// Live Coin Update Notifications Effect
useEffect(() => {
  const runCoinUpdates = async () => {
    console.log("🏃 Running coin update scan (cache only)…");
    if (!allCandles || !Object.keys(allCandles).length) return;

    // Grab only the 1m, 5m, 15m, 1h, 6h & 1d slices for followed coins
    const cached = collectCachedUpdates(followedCoins, ['1m','5m','15m','1h','6h','1d'], allCandles);
    console.log("Using cached candle data:", cached);

    const newUpdates = [];

    for (const coin of Object.keys(cached)) {
      for (const timeframe of Object.keys(cached[coin])) {
        const candles = cached[coin][timeframe];
        console.log(`Cached ${coin} (${timeframe}) length:`, candles.length);
        if (candles.length < 50) continue;

        const raw = evaluateCoinUpdate(coin, timeframe, candles);
        console.log("Raw update result for", coin, timeframe, "=>", raw);
        const formatted = formatCoinUpdateNotification(coin, timeframe, raw);
        console.log("Formatting update for", coin, timeframe, "=>", formatted);
        if (!formatted) continue;

        // Grow/fade: compare this scan's histogram to the previous one for this
        // coin|timeframe. READ before WRITE — do not reorder.
        const macdKey = `${coin}|${timeframe}`;
        const prevHist = prevMacdHistRef.current[macdKey];
        const macdHistDir =
          prevHist === undefined ? "flat"
          : raw.macdHist > prevHist ? "growing"
          : raw.macdHist < prevHist ? "fading"
          : "flat";
        // Magnitude-correct widening: compare |this hist| to |previous hist| using the
        // same prevHist read above (read-before-write at L700, before the overwrite).
        const macdWidening = (prevHist === undefined) ? false : Math.abs(raw.macdHist) > Math.abs(prevHist);
        prevMacdHistRef.current[macdKey] = raw.macdHist;

        newUpdates.push({
          coin,
          timeframe,
          message: formatted,
          metrics: {
            macdHist: raw.macdHist,
            macd: raw.macd,
            signal: raw.signal,
            macdHistDir,
            macdWidening,
            rsiValue: raw.rsiValue,
            stochRsi: raw.stochRsi,
            emaTrend: raw.emaTrend,
            avgMove: raw.avgMove
          },
          time: new Date().toLocaleTimeString()
        });
      }
    }

    console.log("New coinUpdateNotifications:", newUpdates);
    setCoinUpdateNotifications(newUpdates);
  };

  const interval = setInterval(runCoinUpdates, 120000); // Every 10 seconds
  runCoinUpdates();
  return () => clearInterval(interval);
}, [followedCoins, allCandles]);
    
 

    // (Old Coin Update useEffect removed)
    
    useEffect(() => {
      let intervalId;
 
      const fetchChartDataForInterval = async () => {
        if (getProvider(selectedCoin) !== "coinbase") return;
        try {
          const granularity = granularityMap[selectedInterval] || 3600;
          // Short TTL (4s) keeps this 5s poller's refresh cadence identical while the
          // request itself now flows through the shared queue with everything else.
          const raw = await getCandles(selectedCoin, granularity, { ttlMs: 4000, priority: true });
          const formatted = formatCandles(normalizeCandles(raw));
          console.log("📊 Chart updated at:", new Date().toLocaleTimeString());
          if (!chartData || chartData.length === 0) return;
          const lastTime = chartData[0]?.time;
          if (!lastTime || formatted[0].time !== lastTime) {
            setChartData(formatted);
            setLastChartUpdate(Date.now());
          }
        } catch (error) {
          console.error("Error fetching interval chart data:", error);
        }
      };
 
      fetchChartDataForInterval();
 
      // Refresh every 5 seconds ONLY for the selected chart
      intervalId = setInterval(() => {
        fetchChartDataForInterval();
      }, 5000);
 
      return () => clearInterval(intervalId);
    }, [selectedCoin, selectedInterval, chartData]);

    // More Info page chart — self-contained candlestick (Build 1). Owns its own fetch;
    // does NOT touch the dashboard chart effect above. Candlestick only — no overlays yet.
    useEffect(() => {
      if (activeTab !== "more_info") return;
      setMoreInfoReady(false); // re-arm on every fetch (e.g. symbol/timeframe change without unmount)
      const el = moreInfoChartRef.current;
      if (!el || !selectedNote) return;

      const symbol = selectedNote?.coin || selectedCoin;
      const tf = selectedNote?.timeframe || selectedInterval;
      // Real timeframes map to their own granularity; only a non-key (e.g. "all") falls back to 1h.
      const granularity = granularityMap[tf] || 3600;

      let chart = null;
      let cancelled = false;

      const run = async () => {
        try {
          if (!cancelled) setMoreInfoLoadError(null); // clear any prior failure on every (re)fetch
          // display:true = the deep display-chart variant (Coinbase pages backwards via
          // COINBASE_PAGES; Alpaca uses ALPACA_DISPLAY_LOOKBACK_DAYS) — same data as the
          // old inline branch, now routed/cached through marketData.js under its own key.
          const raw = await getCandles(symbol, granularity, { display: true });
          // Defensive: lightweight-charts requires ascending time. Coinbase + Alpaca
          // reach here via formatCandles' .reverse() in opposite native orders, so sort
          // explicitly rather than trust source order. (Boundary-order cleanup is a
          // separate future build; this keeps the fix isolated to the chart.)
          const formatted = formatCandles(normalizeCandles(raw)).sort((a, b) => a.time - b.time);
          moreInfoIndicatorsRef.current = computeMoreInfoIndicators(formatted);
          if (cancelled || !moreInfoChartRef.current) return;

          chart = createChart(el, {
            width: el.clientWidth,
            height: 360,
            layout: { background: { type: ColorType.Solid, color: "rgba(17,17,17,0.96)" }, textColor: "#aaa" },
            grid: { vertLines: { color: "rgba(255,255,255,0.05)" }, horzLines: { color: "rgba(255,255,255,0.05)" } },
            timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
            rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", minimumWidth: 70 },
            crosshair: { mode: CrosshairMode.Normal },
          });
          moreInfoChartObjRef.current = chart;
          // Volume histogram — overlay on its own (empty) price scale, pinned to the
          // bottom band. Created BEFORE the candlestick series so candles draw on top.
          // Ports the dashboard overlay (commit 9984bfc); reuses c.volume already carried
          // by App.js formatCandles — no new fetch, no pipeline change.
          const volSeries = chart.addHistogramSeries({
            priceScaleId: "",
            priceFormat: { type: "volume" },
            lastValueVisible: false,
            priceLineVisible: false,
          });
          chart.priceScale("").applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
          volSeries.setData(
            formatted.map((c) => ({
              time: c.time,
              value: c.volume,
              color: c.close >= c.open ? "rgba(74, 222, 128, 0.5)" : "rgba(248, 113, 113, 0.5)",
            }))
          );
          const series = chart.addCandlestickSeries({ priceLineColor: "#F0F0F0" });
          moreInfoSeriesRef.current = series;
          series.setData(
            formatted.map((c) => ({ time: c.time, open: c.open, high: c.high, low: c.low, close: c.close }))
          );
          moreInfoLastTimeRef.current =
            formatted.length ? formatted[formatted.length - 1].time : null;

          // --- Bollinger Bands overlay — 3 line series directly on the price pane. Unlike
          // RSI/MACD (which are always built and only visually collapsed via a 0-height
          // container), BB has no separate pane to hide, so it's gated here on
          // overlayFilters.showBB at build time — this effect's deps include showBB so
          // toggling it live rebuilds the chart with/without the bands. ---
          const bbData = overlayFilters.showBB ? (moreInfoIndicatorsRef.current?.bollinger || []) : [];
          if (bbData.length) {
            const bbByTime = new Map(bbData.map((p) => [p.time, p]));
            const paddedBand = (key) => formatted.map((c) =>
              bbByTime.has(c.time) ? { time: c.time, value: bbByTime.get(c.time)[key] } : { time: c.time }
            );
            const bbUpper = chart.addLineSeries({ color: "rgba(129,140,248,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
            const bbMiddle = chart.addLineSeries({ color: "rgba(255,255,255,0.35)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
            const bbLower = chart.addLineSeries({ color: "rgba(129,140,248,0.7)", lineWidth: 1, priceLineVisible: false, lastValueVisible: false });
            bbUpper.setData(paddedBand("upper"));
            bbMiddle.setData(paddedBand("middle"));
            bbLower.setData(paddedBand("lower"));
          }

          // --- Volume Profile overlay — same gating pattern as BB (no separate collapsible
          // pane, so gated at build time on overlayFilters.showVolProfile; deps include it). ---
          if (overlayFilters.showVolProfile && formatted.length) {
            const profile = computeVolumeProfile(formatted, 24);
            const vpPrimitive = new VolumeProfilePrimitive(() => profile);
            series.attachPrimitive(vpPrimitive);
            vpPrimitive.updateAllViews();
          }

          // Load zoomed to the RECENT candles (founder ask 2026-07-09): last ~90 with a
          // small right margin, instead of fitContent's full-history view. Short feeds
          // fall back to fitContent. User can still scroll/zoom out to full history.
          if (formatted.length > 95) {
            chart.timeScale().setVisibleLogicalRange({ from: formatted.length - 90, to: formatted.length + 6 });
          } else {
            chart.timeScale().fitContent();
          }

          // --- RSI subpanel (Build 3 sub-build 2): second chart instance, one-way logical-range synced ---
          const rsiEl = moreInfoRsiChartRef.current;
          const rsiSeriesData = moreInfoIndicatorsRef.current?.rsi || [];
          if (rsiEl && rsiSeriesData.length) {
            const rsiChart = createChart(rsiEl, {
              width: rsiEl.clientWidth,
              height: 150,
              layout: { background: { type: ColorType.Solid, color: "rgba(17,17,17,0.96)" }, textColor: "#aaa" },
              grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
              timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
              rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", minimumWidth: 70 },
              crosshair: { mode: CrosshairMode.Normal },
            });
            moreInfoRsiChartObjRef.current = rsiChart;

            // PAD to full candle length so logical indexes match the price chart exactly.
            // formatted is the ascending candle array already in scope. Whitespace = {time} only.
            const rsiByTime = new Map(rsiSeriesData.map((p) => [p.time, p.value]));
            const paddedRsi = formatted.map((c) =>
              rsiByTime.has(c.time) ? { time: c.time, value: rsiByTime.get(c.time) } : { time: c.time }
            );

            const rsiLine = rsiChart.addLineSeries({ color: "#818cf8", lineWidth: 2, priceLineVisible: false, lastValueVisible: true });
            rsiLine.setData(paddedRsi);

            // 30 / 70 guide lines (standard RSI overbought/oversold)
            rsiLine.createPriceLine({ price: 70, color: "rgba(248,113,113,0.5)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "70" });
            rsiLine.createPriceLine({ price: 30, color: "rgba(74,222,128,0.5)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "30" });

            rsiChart.timeScale().fitContent();
          }

          // --- MACD subpanel (sub-build 3): third chart instance, mirrors the RSI pane ---
          const macdEl = moreInfoMacdChartRef.current;
          const macdSeriesData = moreInfoIndicatorsRef.current?.macd || [];
          if (macdEl && macdSeriesData.length) {
            const macdChart = createChart(macdEl, {
              width: macdEl.clientWidth,
              height: 150,
              layout: { background: { type: ColorType.Solid, color: "rgba(17,17,17,0.96)" }, textColor: "#aaa" },
              grid: { vertLines: { color: "rgba(255,255,255,0.04)" }, horzLines: { color: "rgba(255,255,255,0.04)" } },
              timeScale: { timeVisible: true, borderColor: "rgba(255,255,255,0.1)" },
              rightPriceScale: { borderColor: "rgba(255,255,255,0.1)", minimumWidth: 70 },
              crosshair: { mode: CrosshairMode.Normal },
            });
            moreInfoMacdChartObjRef.current = macdChart;

            // PAD each series to full candle length so logical indexes match the price chart.
            const macdByTime = new Map(macdSeriesData.map((p) => [p.time, p]));
            const paddedMacdLine = formatted.map((c) =>
              macdByTime.has(c.time) ? { time: c.time, value: macdByTime.get(c.time).macd } : { time: c.time }
            );
            const paddedSignalLine = formatted.map((c) =>
              macdByTime.has(c.time) ? { time: c.time, value: macdByTime.get(c.time).signal } : { time: c.time }
            );
            const paddedHistogram = formatted.map((c) => {
              if (!macdByTime.has(c.time)) return { time: c.time };
              const h = macdByTime.get(c.time).histogram;
              return { time: c.time, value: h, color: h >= 0 ? "rgba(74,222,128,0.5)" : "rgba(248,113,113,0.5)" };
            });

            // Histogram FIRST so the lines render on top of it.
            const macdHist = macdChart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false,
              priceFormat: { type: "custom", minMove: 0.0000001, formatter: (p) => {
                const a = Math.abs(p);
                if (a === 0) return "0";
                if (a >= 1) return p.toFixed(2);
                if (a >= 0.01) return p.toFixed(4);
                return p.toPrecision(3);
              } } });
            macdHist.setData(paddedHistogram);

            const macdLineSeries = macdChart.addLineSeries({ color: "#818cf8", lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
              priceFormat: { type: "custom", minMove: 0.0000001, formatter: (p) => {
                const a = Math.abs(p);
                if (a === 0) return "0";
                if (a >= 1) return p.toFixed(2);
                if (a >= 0.01) return p.toFixed(4);
                return p.toPrecision(3);
              } } });
            macdLineSeries.setData(paddedMacdLine);

            const signalLineSeries = macdChart.addLineSeries({ color: "#f97316", lineWidth: 2, priceLineVisible: false, lastValueVisible: true,
              priceFormat: { type: "custom", minMove: 0.0000001, formatter: (p) => {
                const a = Math.abs(p);
                if (a === 0) return "0";
                if (a >= 1) return p.toFixed(2);
                if (a >= 0.01) return p.toFixed(4);
                return p.toPrecision(3);
              } } });
            signalLineSeries.setData(paddedSignalLine);

            // Zero guide line, faint white (mirrors the RSI 30/70 guide treatment).
            macdLineSeries.createPriceLine({ price: 0, color: "rgba(255,255,255,0.25)", lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: "0" });

            macdChart.timeScale().fitContent();
          }

          // --- Pane registry (Build: N-pane sync refactor) ---
          // One shared structure the logical-range sync AND the crosshair overlays loop over.
          // Only panes that actually exist: price always; RSI/MACD behind their existence guards.
          const panes = [];
          panes.push({
            key: "price",
            chart: moreInfoChartObjRef.current,
            xhairDiv: moreInfoPriceXhairRef.current,
          });
          if (moreInfoRsiChartObjRef.current) {
            panes.push({
              key: "rsi",
              chart: moreInfoRsiChartObjRef.current,
              xhairDiv: moreInfoRsiXhairRef.current,
            });
          }
          if (moreInfoMacdChartObjRef.current) {
            panes.push({
              key: "macd",
              chart: moreInfoMacdChartObjRef.current,
              xhairDiv: moreInfoMacdXhairRef.current,
            });
          }

          // --- Price-scale width parity (synced-crosshair alignment) ---
          // The crosshair x is computed per-pane via each pane's OWN timeScale.timeToCoordinate,
          // so unequal right-price-scale widths (wide price labels vs narrow RSI 30/70 vs tiny
          // MACD decimals) map the same timestamp to different x → the lines diverge across panes.
          // minimumWidth in the createChart config is only a FLOOR (70) and is exceeded by the
          // price pane's labels, so the panes' widths diverge. There's no single safe fixed width
          // (label width is asset-dependent), so measure each pane's actual right-scale width and
          // raise every pane's minimumWidth to the max — the documented vertical-stack-alignment
          // pattern (lightweight-charts v4.2 PriceScaleOptions.minimumWidth), made asset-agnostic.
          // Deferred one frame because price-scale width is computed on the next paint; the
          // existing `cancelled` guard makes a late callback safe (charts may be removed by then).
          if (panes.length > 1) {
            requestAnimationFrame(() => {
              if (cancelled) return;
              let maxScaleWidth = 0;
              for (const p of panes) {
                try {
                  const w = p.chart.priceScale("right").width();
                  if (w > maxScaleWidth) maxScaleWidth = w;
                } catch (e) {}
              }
              if (maxScaleWidth > 0) {
                for (const p of panes) {
                  try { p.chart.priceScale("right").applyOptions({ minimumWidth: maxScaleWidth }); } catch (e) {}
                }
              }
            });
          }

          // Bidirectional logical-range sync: any pane drives the others ("move in unison").
          // The VisibleLogicalRange change event fires ASYNC (next rAF, verified against v4
          // internals), so a synchronous true/false flag can't catch the echo. Instead we record
          // the last range applied to each pane and skip no-op/echo sets within a float epsilon:
          // when B receives A's range and applies it, B's own event fires next frame and tries to
          // set A — but A is already (≈) at that range, so it's skipped and the chain dies after
          // one hop. No timing dependency.
          const lastApplied = {};  // key -> {from, to} most recently set on that pane's timeScale
          const EPS = 0.0001;      // logical-range units; tolerant of float round-trip drift
          const nearlyEqual = (a, b) =>
            a && b && Math.abs(a.from - b.from) < EPS && Math.abs(a.to - b.to) < EPS;

          const rangeHandlers = {};
          for (const source of panes) {
            const handler = (range) => {
              if (range == null) return;
              for (const target of panes) {
                if (target.key === source.key) continue;
                if (nearlyEqual(lastApplied[target.key], range)) continue;  // echo / no-op: skip
                lastApplied[target.key] = { from: range.from, to: range.to };
                target.chart.timeScale().setVisibleLogicalRange(range);
              }
            };
            rangeHandlers[source.key] = handler;
            source.chart.timeScale().subscribeVisibleLogicalRangeChange(handler);
          }
          // Repurposed: moreInfoRsiSyncRef now holds the {paneKey: handler} MAP (was a single
          // handler), so teardown can unsubscribe each pane's handler from its own timeScale.
          moreInfoRsiSyncRef.current = rangeHandlers;

          // --- Synced crosshair (custom overlay; v3.8 can't draw natively) ---
          // Hovering one chart draws a faint vertical line on the OTHER at the same time.
          // The hovered chart keeps its own native crosshair. DOM overlay → echo-free.
          let xhairSyncing = false;  // reentrancy guard (safety default)

          const drawXhair = (targetChart, lineDiv, time) => {
            if (!targetChart || !lineDiv) return;
            if (time == null) { lineDiv.style.display = "none"; return; }
            const x = targetChart.timeScale().timeToCoordinate(time);
            if (x == null) { lineDiv.style.display = "none"; return; }
            lineDiv.style.left = x + "px";
            lineDiv.style.display = "block";
          };

          // Each pane's handler draws on every OTHER pane, under the shared reentrancy guard.
          const xhairHandlers = {};
          for (const source of panes) {
            const handler = (param) => {
              if (xhairSyncing) return;
              xhairSyncing = true;
              for (const target of panes) {
                if (target.key === source.key) continue;
                drawXhair(target.chart, target.xhairDiv, param.time);
              }
              xhairSyncing = false;
            };
            xhairHandlers[source.key] = handler;
            source.chart.subscribeCrosshairMove(handler);
          }
          // Store under the SAME keys the teardown expects (leaves teardown a no-diff).
          moreInfoXhairHandlersRef.current = {
            priceXhairHandler: xhairHandlers.price,
            rsiXhairHandler: xhairHandlers.rsi,
            macdXhairHandler: xhairHandlers.macd,
          };

          // --- Hover legends (price + RSI/MACD panes) — top-left, updated via DIRECT DOM on crosshair
          // move (NOT React state; a per-move setState would re-render the app). ONE shared handler
          // (subscribed to every pane) drives ALL legends off the hovered param.time; the time axes are
          // synced, so a time from any pane resolves in each per-indicator by-time Map. Free crosshair
          // (CrosshairMode.Normal) can land between bars → each legend falls back to its LAST value so
          // something always shows once data loads. RSI/MACD legends update ONLY when their pane exists
          // (toggled on); everything null-guarded. Torn down in the same teardown block. ---
          const legendEl = moreInfoPriceLegendRef.current;
          if (legendEl && formatted.length) {
            const lab = (t) => `<span style="color:#666">${t}</span>`;
            const val = (t) => `<span style="color:#ddd">${t}</span>`;
            const SEP = `<span style="color:#555">&nbsp;&nbsp;·&nbsp;&nbsp;</span>`;
            const fmt2 = (v) => (Number.isFinite(v) ? v.toFixed(2) : "—");
            const fmtPrice = (v) => {
              if (!Number.isFinite(v)) return "—";
              const a = Math.abs(v);
              const dp = a >= 100 ? 2 : a >= 1 ? 3 : a >= 0.01 ? 5 : 8;
              return v.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });
            };
            const fmtVol = (v) => {
              if (!Number.isFinite(v)) return "—";
              const a = Math.abs(v);
              if (a >= 1e9) return (v / 1e9).toFixed(1) + "B";
              if (a >= 1e6) return (v / 1e6).toFixed(1) + "M";
              if (a >= 1e3) return (v / 1e3).toFixed(0) + "K";
              return String(Math.round(v));
            };

            // PRICE legend — full-word labels, thousands-separated prices, ·-separated pairs.
            const legendByTime = new Map(
              formatted.map((c, i) => [c.time, { c, prev: i > 0 ? formatted[i - 1] : null }])
            );
            const lastEntry = {
              c: formatted[formatted.length - 1],
              prev: formatted.length > 1 ? formatted[formatted.length - 2] : null,
            };
            const renderPrice = ({ c, prev }) => {
              const pct = prev
                ? ((c.close - prev.close) / prev.close) * 100
                : ((c.close - c.open) / c.open) * 100;
              const pctColor = pct >= 0 ? "#4ADE80" : "#F87171";
              const pctStr = (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%";
              // Value colors: High always green, Low always red, Close by this candle's direction,
              // Open by the PREVIOUS candle's direction (fall back to this candle on the first bar).
              const GREEN = "#4ADE80", RED = "#F87171";
              const closeColor = c.close >= c.open ? GREEN : RED;
              const openColor = prev ? (prev.close >= prev.open ? GREEN : RED) : closeColor;
              const cval = (color, t) => `<span style="color:${color}">${t}</span>`;
              legendEl.innerHTML =
                `${lab("Open")} ${cval(openColor, fmtPrice(c.open))}${SEP}${lab("High")} ${cval(GREEN, fmtPrice(c.high))}${SEP}` +
                `${lab("Low")} ${cval(RED, fmtPrice(c.low))}${SEP}${lab("Close")} ${cval(closeColor, fmtPrice(c.close))}${SEP}` +
                `${lab("Volume")} ${val(fmtVol(c.volume))}${SEP}<span style="color:${pctColor}">${pctStr}</span>`;
            };

            // RSI legend — only when the RSI pane exists this run (rsiSeriesData = [{time,value}]).
            let renderRsi = null;
            if (moreInfoRsiChartObjRef.current && moreInfoRsiLegendRef.current && rsiSeriesData.length) {
              const rsiLegendEl = moreInfoRsiLegendRef.current;
              const rsiMap = new Map(rsiSeriesData.map((p) => [p.time, p.value]));
              const rsiLast = rsiSeriesData[rsiSeriesData.length - 1];
              renderRsi = (time) => {
                const v = time != null && rsiMap.has(time) ? rsiMap.get(time) : rsiLast.value;
                rsiLegendEl.innerHTML = `${lab("RSI")} <span style="color:#818cf8">${fmt2(v)}</span>`;
              };
            }

            // MACD legend — only when the MACD pane exists this run (macdSeriesData = [{time,macd,signal,histogram}]).
            let renderMacd = null;
            if (moreInfoMacdChartObjRef.current && moreInfoMacdLegendRef.current && macdSeriesData.length) {
              const macdLegendEl = moreInfoMacdLegendRef.current;
              const macdMap = new Map(macdSeriesData.map((p) => [p.time, p]));
              const macdLast = macdSeriesData[macdSeriesData.length - 1];
              renderMacd = (time) => {
                const m = time != null && macdMap.has(time) ? macdMap.get(time) : macdLast;
                const histColor = m.histogram >= 0 ? "#4ADE80" : "#F87171";
                macdLegendEl.innerHTML =
                  `${lab("MACD")} <span style="color:#818cf8">${fmt2(m.macd)}</span>${SEP}${lab("Signal")} <span style="color:#f97316">${fmt2(m.signal)}</span>${SEP}` +
                  `${lab("Hist")} <span style="color:${histColor}">${fmt2(m.histogram)}</span>`;
              };
            }

            // Initial paint (last values), then ONE handler updates all present legends on crosshair move.
            renderPrice(lastEntry);
            if (renderRsi) renderRsi(null);
            if (renderMacd) renderMacd(null);
            const legendHandler = (param) => {
              const t = param && param.time != null ? param.time : null;
              const entry = t != null && legendByTime.has(t) ? legendByTime.get(t) : lastEntry;
              renderPrice(entry);
              if (renderRsi) renderRsi(t);
              if (renderMacd) renderMacd(t);
            };
            moreInfoLegendHandlerRef.current = legendHandler;
            // Subscribe the SAME handler to EVERY pane (price + RSI/MACD when present) so hovering any
            // pane updates the legends — the time axes are synced, so param.time from any pane resolves
            // in the per-indicator Maps. Mirrors the synced-crosshair pane loop; `panes` holds only the
            // charts that were actually created (inherently null-safe).
            for (const p of panes) {
              try { p.chart.subscribeCrosshairMove(legendHandler); } catch (e) {}
            }
          }

          if (!cancelled) setMoreInfoReady(true);
        } catch (error) {
          console.error("More Info chart fetch/render failed:", error);
          if (!cancelled) {
            const status = error?.response?.status;
            setMoreInfoLoadError(status === 429 ? "Rate limited — try again shortly" : "Couldn't load chart");
          }
        }
      };
      run();

      return () => {
        cancelled = true;
        // Synced-crosshair teardown — unsubscribe both crosshair handlers BEFORE any remove.
        if (moreInfoXhairHandlersRef.current) {
          const h = moreInfoXhairHandlersRef.current;
          try { if (chart) chart.unsubscribeCrosshairMove(h.priceXhairHandler); } catch (e) {}
          try { if (moreInfoRsiChartObjRef.current) moreInfoRsiChartObjRef.current.unsubscribeCrosshairMove(h.rsiXhairHandler); } catch (e) {}
          try { if (moreInfoMacdChartObjRef.current) moreInfoMacdChartObjRef.current.unsubscribeCrosshairMove(h.macdXhairHandler); } catch (e) {}
          moreInfoXhairHandlersRef.current = null;
        }
        // OHLCV legend crosshair teardown — unsubscribe the ONE legend handler from EVERY pane it
        // was subscribed to (price + RSI/MACD when present), each null-guarded, BEFORE any
        // chart.remove() below. Mirrors the synced-crosshair chartByKey teardown.
        if (moreInfoLegendHandlerRef.current) {
          const legendChartByKey = { price: chart, rsi: moreInfoRsiChartObjRef.current, macd: moreInfoMacdChartObjRef.current };
          for (const key of Object.keys(legendChartByKey)) {
            const c = legendChartByKey[key];
            try { if (c) c.unsubscribeCrosshairMove(moreInfoLegendHandlerRef.current); } catch (e) {}
          }
          moreInfoLegendHandlerRef.current = null;
        }
        // Bidirectional range-sync teardown — unsubscribe each pane's handler from its OWN
        // timeScale BEFORE any chart.remove(). moreInfoRsiSyncRef holds the {paneKey: handler} map;
        // resolve each key to its chart ref (mirrors the crosshair teardown above), null-guarded.
        if (moreInfoRsiSyncRef.current) {
          const rh = moreInfoRsiSyncRef.current;
          const chartByKey = {
            price: chart,
            rsi: moreInfoRsiChartObjRef.current,
            macd: moreInfoMacdChartObjRef.current,
          };
          for (const key of Object.keys(rh)) {
            const c = chartByKey[key];
            try { if (c) c.timeScale().unsubscribeVisibleLogicalRangeChange(rh[key]); } catch (e) {}
          }
          moreInfoRsiSyncRef.current = null;
        }
        if (moreInfoRsiChartObjRef.current) {
          try { moreInfoRsiChartObjRef.current.remove(); } catch (e) {}
          moreInfoRsiChartObjRef.current = null;
        }
        if (moreInfoMacdChartObjRef.current) {
          try { moreInfoMacdChartObjRef.current.remove(); } catch (e) {}
          moreInfoMacdChartObjRef.current = null;
        }
        if (chart) chart.remove();
        moreInfoChartObjRef.current = null;
        moreInfoSeriesRef.current = null;
        moreInfoPriceLinesRef.current = [];
        setMoreInfoReady(false);
      };
    }, [activeTab, selectedNote, selectedCoin, selectedInterval, overlayFilters.showBB, overlayFilters.showVolProfile, moreInfoRetryTick]);

    // Drawing tools READ effect — fetches this user's saved horizontal lines for
    // [symbol, "more_info"] context. Separate from the chart-build effect above so adding/
    // deleting a line doesn't trigger a full candle refetch. Cleared before refetch so a
    // symbol change can't briefly show the PREVIOUS symbol's lines.
    useEffect(() => {
      let cancelled = false;
      setMoreInfoDrawings([]);
      if (activeTab !== "more_info" || !userId) return;
      const symbol = selectedNote?.coin || selectedCoin;
      fetchDrawings(userId, symbol, "more_info")
        .then((rows) => { if (!cancelled) setMoreInfoDrawings(rows); })
        .catch((e) => console.error("[chart_drawings-read]", e?.message || e));
      return () => { cancelled = true; };
    }, [activeTab, userId, selectedNote, selectedCoin]);

    // Drawing tools DRAW effect — redraws saved lines directly on the existing price series
    // (moreInfoSeriesRef), WITHOUT rebuilding the chart/refetching candles. Same teardown-
    // then-draw pattern as SignalChart.js.
    useEffect(() => {
      moreInfoDrawingLinesRef.current.forEach((pl) => { try { moreInfoSeriesRef.current?.removePriceLine(pl); } catch (e) {} });
      moreInfoDrawingLinesRef.current = [];
      const series = moreInfoSeriesRef.current;
      if (!series) return;
      moreInfoDrawings.forEach((d) => {
        const pl = series.createPriceLine({
          price: d.price, color: "#f97316", lineWidth: 1, lineStyle: 0,
          axisLabelVisible: true, title: "drawn",
        });
        moreInfoDrawingLinesRef.current.push(pl);
      });
    }, [moreInfoReady, moreInfoDrawings]);

    // Drawing tools CLICK-TO-PLACE effect — armed while moreInfoDrawMode is on. Mirrors
    // SignalChart.js: stays armed across multiple clicks (multi-line mode) so the user can
    // place several lines in a row; only disarms when they click "Draw Line" again.
    useEffect(() => {
      const chart = moreInfoChartObjRef.current;
      const series = moreInfoSeriesRef.current;
      if (!moreInfoDrawMode || !chart || !series || !userId) return;
      const symbol = selectedNote?.coin || selectedCoin;
      const handleClick = (param) => {
        if (!param.point) return;
        const price = series.coordinateToPrice(param.point.y);
        if (price == null) return;
        insertDrawing(userId, symbol, "more_info", price)
          .then((row) => setMoreInfoDrawings((prev) => [...prev, row]))
          .catch((e) => console.error("[chart_drawings-insert]", e?.message || e));
      };
      chart.subscribeClick(handleClick);
      return () => { try { chart.unsubscribeClick(handleClick); } catch (e) {} };
    }, [moreInfoDrawMode, moreInfoReady, userId, selectedNote, selectedCoin]);

    // Persist overlay filters (guarded — private mode / quota failures are non-fatal).
    useEffect(() => {
      try { localStorage.setItem("moreInfoOverlayFilters", JSON.stringify(overlayFilters)); }
      catch (e) { /* ignore */ }
    }, [overlayFilters]);

    // Per-level visibility + master switches are per-note — reset whenever the note changes.
    useEffect(() => {
      setHiddenOverlays(new Set());
      setOverlayMaster({ support: true, resistance: true, fvg: true });
    }, [selectedNote]);

    // Overlay redraw — runs on note/filter change, reads the series from refs, NO refetch.
    useEffect(() => {
      if (activeTab !== "more_info" || !moreInfoReady) return;
      const series = moreInfoSeriesRef.current;
      const chart = moreInfoChartObjRef.current;
      if (!series || !selectedNote) return;

      // (i) Idempotent teardown of prior overlays before redrawing.
      moreInfoPriceLinesRef.current.forEach((pl) => {
        try { series.removePriceLine(pl); } catch (e) {}
      });
      moreInfoPriceLinesRef.current = [];
      moreInfoFvgSeriesRef.current.forEach((s) => {
        try { if (chart) chart.removeSeries(s); } catch (e) {}
      });
      moreInfoFvgSeriesRef.current = [];
      try { series.setMarkers([]); } catch (e) {}

      // (ii) Class-color (indigo crypto / orange stock) — same rule the notification card uses.
      const accentColor = getProvider(selectedNote.coin) === "coinbase" ? "#818cf8" : "#f97316";

      const sr = selectedNote.supportResistance;

      // (iii) S/R lines — support green, resistance red (level-type colors, NOT class-color).
      if (sr) {
        if (overlayMaster.support) {
          (sr.support || []).forEach((z, i) => {
            const key = i === 0 ? "S" : `S${i + 1}`;
            if (hiddenOverlays.has(key)) return;
            const pl = series.createPriceLine({
              price: z.priceMid, color: "#4ade80", lineWidth: 1, lineStyle: 2,
              axisLabelVisible: true, title: key,
            });
            moreInfoPriceLinesRef.current.push(pl);
          });
        }
        if (overlayMaster.resistance) {
          (sr.resistance || []).forEach((z, i) => {
            const key = i === 0 ? "R" : `R${i + 1}`;
            if (hiddenOverlays.has(key)) return;
            const pl = series.createPriceLine({
              price: z.priceMid, color: "#ea580c", lineWidth: 1, lineStyle: 2,
              axisLabelVisible: true, title: key,
            });
            moreInfoPriceLinesRef.current.push(pl);
          });
        }
      }

      // (iv) FVG pool — UNCHANGED filter/sort (unfilled, sizePct>=0.3, nearest-first).
      // No slice — every pooled gap gets a chip (F1..FN); per-level skip happens in the draw.
      const fvgPool = (selectedNote.fvgs || [])
        .filter((g) => !g.filled && g.sizePct >= 0.3)
        .map((g) => {
          const m = (g.top + g.bottom) / 2;
          const cp = sr?.currentPrice ?? g.top;
          return { ...g, _d: Math.abs((m - cp) / cp) * 100 };
        })
        .sort((a, b) => a._d - b._d);

      // Draw each FVG as a soft yellow/gold filled band (baseline series).
      // baseValue anchored at g.bottom; flat data line at g.top → top-side fill paints
      // the band between bottom and top. Bottom-side fill transparent. Band spans from
      // the gap's formation time (g.time) to the latest candle time (moreInfoLastTimeRef).
      const rightTime = moreInfoLastTimeRef.current;
      if (chart && rightTime != null && overlayMaster.fvg) {
        fvgPool.forEach((g, i) => {
          const key = `F${i + 1}`;
          if (hiddenOverlays.has(key)) return;
          const bandSeries = chart.addBaselineSeries({
            baseValue: { type: "price", price: g.bottom },
            topFillColor1: "rgba(250, 204, 21, 0.18)",
            topFillColor2: "rgba(250, 204, 21, 0.18)",
            topLineColor: "rgba(250, 204, 21, 0.45)",
            bottomFillColor1: "rgba(0,0,0,0)",
            bottomFillColor2: "rgba(0,0,0,0)",
            bottomLineColor: "rgba(0,0,0,0)",
            lineWidth: 1,
            priceLineVisible: false,
            lastValueVisible: false,
            crosshairMarkerVisible: false,
          });
          // Guard against an inverted/degenerate span (g.time should be <= rightTime).
          const t0 = g.time;
          if (t0 == null || t0 > rightTime) {
            bandSeries.setData([{ time: rightTime, value: g.top }]);
            // Degenerate span (no real left edge) — skip the label to avoid anchoring to a
            // time past the visible data (marker wouldn't reliably render anyway).
          } else {
            bandSeries.setData([
              { time: t0, value: g.top },
              { time: rightTime, value: g.top },
            ]);
            // Label the band at its formation time, floating above the bar, gold to match.
            try {
              bandSeries.setMarkers([{
                time: t0, position: "aboveBar", color: "#fbbf24",
                shape: "circle", text: key,
              }]);
            } catch (e) {}
          }
          moreInfoFvgSeriesRef.current.push(bandSeries);
        });
      }

      // (v) Entry / Exit — fixed colors (amber / steel-blue), heavier; Entry solid, Exit dashed.
      if (overlayFilters.showEntryExit) {
        if (selectedNote.entry != null) {
          const pl = series.createPriceLine({
            price: selectedNote.entry, color: "#fbbf24", lineWidth: 2, lineStyle: 0,
            axisLabelVisible: true, title: "Entry",
          });
          moreInfoPriceLinesRef.current.push(pl);
        }
        if (selectedNote.exit != null) {
          const pl = series.createPriceLine({
            price: selectedNote.exit, color: "#60a5fa", lineWidth: 2, lineStyle: 2,
            axisLabelVisible: true, title: "Exit",
          });
          moreInfoPriceLinesRef.current.push(pl);
        }
      }

      // (vi) Signal marker — TIME-UNIT FIX: note.timestamp is ms, candle time is seconds.
      // Convert to seconds; if it doesn't land on a bar the marker silently won't show (acceptable).
      if (overlayFilters.showMarker && selectedNote.timestamp != null) {
        const markerTime = Math.floor(selectedNote.timestamp / 1000);
        try {
          series.setMarkers([{
            time: markerTime, position: "belowBar", color: accentColor,
            shape: "arrowUp", text: selectedNote.tier || "",
          }]);
        } catch (e) {}
      }
      // (vii) No fitContent() here — that belongs to the fetch effect at create time.
    }, [activeTab, selectedNote, overlayFilters, moreInfoReady, hiddenOverlays, overlayMaster]);


 
    const handleFollowTrade = (note) => {
      // Trade notes carry the structured fields the Followed card renders — push the OBJECT.
      // Non-trade (lean) notes and legacy strings fall back to the message string (rendered
      // by the Followed card's isStr branch); pushing a lean object would show all dashes.
      // PERSIST FIX (2026-07-11): follows previously lived only in React state and
      // vanished on refresh. Trade notes (they carry signals.id) now also write a
      // followed_trades row (RLS-owned); hydration below restores them on load.
      // Duplicate follows are no-ops locally and upsert-ignored in the DB.
      const isTradeNote = typeof note !== "string" && note?.type === "trade" && note?.id != null;
      if (isTradeNote) {
        setFollowedTrades((prev) =>
          prev.some((t) => typeof t !== "string" && t?.id === note.id) ? prev : [note, ...prev]);
        (async () => {
          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.user?.id) return;
            const { error } = await supabase
              .from("followed_trades")
              .upsert({ user_id: session.user.id, signal_id: note.id },
                      { onConflict: "user_id,signal_id", ignoreDuplicates: true });
            if (error) console.error("Follow persist failed:", error.message);
          } catch (e) {
            console.error("Follow persist failed:", e?.message || e);
          }
        })();
      } else {
        // Legacy/lean entries: local-only (no signal id to key on), as before.
        setFollowedTrades((prevTrades) => [
          typeof note === "string"
            ? note
            : typeof note?.message === "string"
            ? note.message
            : String(note?.message ?? ""),
          ...prevTrades,
        ]);
      }

      setFollowingTrade(true);
      setActiveTab("followed");
    };

    // Followed Trades hydration (persist fix 2026-07-11): restore follows from
    // followed_trades via the get_my_followed_trades RPC (SECURITY DEFINER —
    // returns the caller's followed signal rows, newest-followed first; the app
    // cannot SELECT signals directly). Re-runs when trade prefs change so the
    // dollar estProfit stays current, mirroring the notification loader.
    useEffect(() => {
      let cancelled = false;
      const loadFollowed = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id || cancelled) return;
        const { data, error } = await supabase.rpc("get_my_followed_trades");
        if (cancelled || error || !Array.isArray(data)) return;
        setFollowedTrades(data.map((row) => mapSignalRowToNote(row, tradeAmount, tradeFee)));
      };
      loadFollowed();
      return () => { cancelled = true; };
    }, [userId, tradeAmount, tradeFee]);


    useEffect(() => {
        const fetchDayChange = async () => {
          try {
            const changes = {};
            for (const coin of coins) {
                try {
                // TRUE rolling 24h change (fix 2026-07-11): the old version compared the
                // live price to yesterday's UTC DAILY close — "change since UTC midnight,"
                // which is near-zero right after 00:00 UTC (evening US time) and never a
                // real 24h move. Now: live price vs the 1h close nearest to exactly 24h
                // ago, matching the 24h High/Low card's rolling-window convention and how
                // Coinbase displays 24h %. Fetch 26 hourly candles to guarantee coverage.
                const hourlyRaw = await getCandles(coin, 3600, { limit: 26 });
                const hourly = normalizeCandles(hourlyRaw); // canonical newest-first, [time, low, high, open, close, volume]
                const currentPrice = parseFloat(livePrices[coin] || 0);

                if (Array.isArray(hourly) && hourly.length > 0 && currentPrice > 0) {
                  // Anchor = the candle whose timestamp is closest to now − 24h (seconds).
                  const target = Math.floor(Date.now() / 1000) - 86400;
                  let anchor = hourly[hourly.length - 1];
                  for (const c of hourly) {
                    if (Math.abs(c[0] - target) < Math.abs(anchor[0] - target)) anchor = c;
                  }
                  const anchorClose = anchor[4];
                  if (anchorClose > 0) {
                    changes[coin] = ((currentPrice - anchorClose) / anchorClose) * 100;
                  }
                  // No valid anchor close → leave unset; the card renders "—", never a fake 0.00%.
                }
              } catch (err) {
                // Fetch error → leave unset ("—"), NOT 0 — a fake 0.00% is misinformation.
                console.error(`Error fetching hourly candles for ${coin}:`, err);
              }
            }
            // Stocks (Alpaca): one batched snapshots request, change derived from
            // latestTrade vs prevDailyBar. Isolated try/catch — an Alpaca failure
            // must never block setDayChangeMap from publishing the crypto values.
            try {
              const syms = stockSymbolsRef.current;
              if (syms.length > 0) {
                const snaps = await fetchAlpacaSnapshots(syms);
                for (const [sym, s] of Object.entries(snaps)) {
                  if (s.prevClose > 0 && s.price > 0) {
                    changes[sym] = ((s.price - s.prevClose) / s.prevClose) * 100;
                  }
                }
              }
            } catch (stockErr) {
              console.warn("Stock day-change fetch failed (crypto unaffected):",
                           stockErr?.message || stockErr);
            }
            setDayChangeMap(changes);
          } catch (error) {
            console.error("Error fetching 24h change data:", error);
          }
        };
      
        fetchDayChange();
        const interval = setInterval(fetchDayChange, 60_000);
        return () => clearInterval(interval);
      }, [coins, livePrices]);
      
     

    useEffect(() => {
        const fetchPrices = async () => {
            try {
                const updatedPrices = {};
                for (const coin of coinsRef.current) {
                    const amount = await getSpotPrice(coin);
                    updatedPrices[coin] = parseFloat(amount).toFixed(4);
                }
                // Stocks (Alpaca): one batched latest-trade request, folded into the
                // same map. Isolated try/catch — an Alpaca failure must never block
                // setLivePrices from publishing the crypto prices above.
                try {
                  const syms = stockSymbolsRef.current;
                  if (syms.length > 0) {
                    const stockPrices = await fetchAlpacaLatestPrices(syms);
                    for (const [sym, p] of Object.entries(stockPrices)) {
                      updatedPrices[sym] = parseFloat(p).toFixed(4);
                    }
                  }
                } catch (stockErr) {
                  console.warn("Stock price fetch failed (crypto unaffected):",
                               stockErr?.message || stockErr);
                }
                console.log("Updated Prices:", updatedPrices);
                setLivePrices(updatedPrices);
            } catch (error) {
                if (error.code === 'ERR_NETWORK_IO_SUSPENDED') {
                    console.warn("⚠️ Network I/O Suspended — Possible Chrome 3rd-party cookie restriction or browser policy interference.");
                }
                console.error("Error fetching live prices:", error);
            }
        };

        fetchPrices();
        const interval = setInterval(fetchPrices, 10000);
        return () => clearInterval(interval);
    }, []);

    // Set scan interval constant (1 minute)
    const SCAN_INTERVAL = 60000; // 1 minute
   useEffect(() => {
        // Coin indicator summaries for Coin Updates (read-only, non-intrusive)
        let indicatorSummaries = {};
        // Option to enable new Coin Update logic
       const enableCoinUpdateDisplay = true;

        // New Coin Update summaries object
        let coinUpdateSummariesBySymbol = {};

        const fetchIndicators = async () => {
            try {
                for (const coin of [...coins, ...stockSymbols]) {
                    if (!followedCoins[coin]) {
                      continue;
                    }
                    if (getProvider(coin) === "alpaca" && !isStockMarketOpen()) {
                      continue;
                    }

                    // Pre-fetch all required timeframes for this coin
                    const timeframeCandlesMap = {};
                    for (const [label, seconds] of Object.entries(granularityMap)) {
                        if (!timeframes[label]) continue;
                        // DATA PATH ONLY — same provider routing + raw shape as the old
                        // inline branch; the scoring math below is untouched.
                        const raw = await getCandles(coin, seconds);
                        timeframeCandlesMap[label] = normalizeCandles(raw);
                    }

                    for (const [label, candles] of Object.entries(timeframeCandlesMap)) {
                        if (!timeframes[label]) continue;
                        // Re-introduce raw indicator values for legacy block
                        const closePrices = candles.map(c => c.close ?? c[4]).reverse();
                        const { ema9, rsiValue, macd, signal, atrValue } = calculateIndicators(candles);
                        // Build a minimal MACD histogram history for compatibility
                        const macdHistogramHistory = [];
                        macdHistogramHistory.push(macd - signal);

                        // Lower timeframe filter logic (unchanged)
                        const lowerTF = label === "15m" ? "5m" : label === "5m" ? "1m" : null;
                        if (lowerTF && timeframes[lowerTF]) {
                          try {
                            const lowerGranularity = granularityMap[lowerTF];
                            const lowerCandles = timeframeCandlesMap[lowerTF];
                            if (lowerCandles) {
                              const lowerCloses = lowerCandles.map(c => c[4]).reverse();
                              const lowerEma = (arr, period) => {
                                const k = 2 / (period + 1);
                                return arr.reduce((acc, val, i) => {
                                  if (i === 0) return [val];
                                  acc.push(val * k + acc[i - 1] * (1 - k));
                                  return acc;
                                }, []);
                              };
                              const emaShort = lowerEma(lowerCloses, 12);
                              const emaLong = lowerEma(lowerCloses, 26);
                              const macdLine = emaShort.map((val, i) => val - emaLong[i]);
                              const signalLine = lowerEma(macdLine.slice(emaShort.length - macdLine.length), 9);
                              const hist = macdLine[macdLine.length - 1] - signalLine[signalLine.length - 1];
                              const lowerRsiValue = rsi(lowerCloses);
                              if (hist < 0 && lowerRsiValue < 55) {
                                console.log(`⛔ Skipping ${coin} (${label}) — Lower TF (${lowerTF}) is bearish`);
                                continue;
                              }
                            }
                          } catch (err) {
                            console.warn(`⚠️ Failed lower TF check for ${coin} (${label}):`, err.message);
                          }
                        }

                        // Main indicator calculations (delegated to pure helper)
                        const result = evaluateCoinUpdate(coin, label, candles);
                        if (!result) {
                          console.log(`⛔ No update from evaluator for ${coin} (${label})`);
                          continue;
                        }

                        const emaShort = closePrices.reduce((acc, val, i) => {
                          const k = 2 / (12 + 1);
                          if (i === 0) return [val];
                          acc.push(val * k + acc[i - 1] * (1 - k));
                          return acc;
                        }, []);
                        const emaLong = closePrices.reduce((acc, val, i) => {
                          const k = 2 / (26 + 1);
                          if (i === 0) return [val];
                          acc.push(val * k + acc[i - 1] * (1 - k));
                          return acc;
                        }, []);
                        const macdLine = emaShort.map((val, i) => val - emaLong[i]);
                        const signalLine = macdLine.reduce((acc, val, i) => {
                          const k = 2 / (9 + 1);
                          if (i === 0) return [val];
                          acc.push(val * k + acc[i - 1] * (1 - k));
                          return acc;
                        }, []);
                        for (let i = 0; i < signalLine.length; i++) {
                          macdHistogramHistory.push(macdLine[i] - signalLine[i]);
                        }
                        macdHistNow = macdHistogramHistory[macdHistogramHistory.length - 1];
                        macdHistPrev = macdHistogramHistory[macdHistogramHistory.length - 2];

                        const latestVolume = candles[0][5]; // Current candle volume
                        let coinPrice;
                        if (getProvider(coin) === "coinbase") {
                            coinPrice = parseFloat(await getSpotPrice(coin));
                        } else {
                            // Alpaca symbols: latest candle close (normalized Coinbase
                            // positional format — close at index 4, same array the volume
                            // read above uses). No Coinbase spot endpoint for bare tickers.
                            coinPrice = parseFloat(candles[0][4]);
                        }
                        const macdHistogram = macd - signal;
                        const ema = (arr, period) => {
                            const k = 2 / (period + 1);
                            return arr.reduce((acc, val, i) => {
                                if (i === 0) return [val];
                                acc.push(val * k + acc[i - 1] * (1 - k));
                                return acc;
                            }, []);
                        };
                        const emaHistory = ema(closePrices, 9);
                        const emaSlope = ema9 - emaHistory[emaHistory.length - 2]; // Removed duplicate declaration
                        const volumeAvg = candles.slice(1, 11).reduce((sum, c) => sum + c[5], 0) / 10;
                        const volumeSurge = latestVolume > volumeAvg; // Removed duplicate declaration
                        const lastSwingHigh = Math.max(...candles.slice(-30).map(c => c[2]));
                        // Resistance levels (placeholder logic)
                        const resistanceLevels = candles.slice(-50).map(c => c[2]); // high prices
                        resistanceLevels.sort((a, b) => b - a); // descending
                        const localResistances = [...new Set(resistanceLevels.filter(r => r > coinPrice))];

                        const { confidenceScore, selectedExit, realisticProfit, confirmFromHigherTimeframes, stochRsiK } = scoreTrade({
                          macd,
                          signal,
                          rsiValue,
                          atrValue,
                          macdHistogramHistory,
                          coinPrice,
                          closePrices,
                          latestVolume,
                          volumeAvg,
                          emaSlope,
                          localResistances,
                          candles,
                          label,
                          timeframeCandlesMap
                        });
                        const formatNumber = (num) => num.toLocaleString();

                        // **✅ AI trade conditions with balanced trend confirmation**
                        console.log(`Trade Analysis for ${coin} (${label}):
                            - Entry Price: $${coinPrice.toLocaleString()}
                            - Potential Exit: $${selectedExit.toLocaleString()}
                            - Last 50-Candle High: $${lastSwingHigh.toLocaleString()}
                            Higher Timeframe Confirmation: ${confirmFromHigherTimeframes}`);

                        // --- Coin Update Logic (Read-Only, Non-Intrusive) ---
                        if (!indicatorSummaries[label]) indicatorSummaries[label] = {};
                        indicatorSummaries[label][coin] = {
                          macdSignal: macdHistogram > 0 ? "Positive" : "Negative",
                          macdAboveZero: macdLine[macdLine.length - 1] > 0,
                          rsi: rsiValue,
                          srsi: stochRsiK,
                          emaSlopeUp: ema9 > emaHistory[emaHistory.length - 2],
                          consensus:
                            (macdHistogram > 0 &&
                              rsiValue > 54 &&
                              rsiValue <= 70 &&
                              typeof stochRsiK === "number" &&
                              stochRsiK >= 40 &&
                              stochRsiK <= 75 &&
                              ema9 > emaHistory[emaHistory.length - 2])
                        };
                        // --- End Coin Update Logic ---

                        // --- Standalone Coin Update Summaries Array ---
                        // --- CoinUpdates START ---
                        // coinUpdateSummaries.push({
                        //   coin: coin,
                        //   timeframe: label,
                        //   macd: macdHistogram > 0 ? "Positive" : "Negative",
                        //   rsi: rsiValue,
                        //   srsi: stochRsiK,
                        //   macdAboveZero: macdLine[macdLine.length - 1] > 0,
                        //   emaSlope: ema9 - emaHistory[emaHistory.length - 2] > 0 ? "Increasing" : "Decreasing",
                        //   isBullish:
                        //     macdHistogram > 0 &&
                        //     rsiValue >= 54 && rsiValue <= 70 &&
                        //     typeof stochRsiK === "number" &&
                        //     stochRsiK >= 51 && stochRsiK <= 75,
                        //   isBearish:
                        //     macdHistogram < 0 && rsiValue < 50,
                        //   strongConsensus:
                        //     macdHistogram > 0 &&
                        //     rsiValue >= 54 && rsiValue <= 70 &&
                        //     typeof stochRsiK === "number" &&
                        //     stochRsiK >= 51 && stochRsiK <= 75 &&
                        //     ema9 - emaHistory[emaHistory.length - 2] > 0,
                        //   timestamp: Date.now()
                        // });
                        // --- CoinUpdates END ---

                        // --- Trade grading and notification logic below ---
                        {
                            // Early skip for mode/timeframe rules
                            if (!isTimeframeAllowedForMode(userMode, label)) {
                              console.log(`⏭ Skipping ${coin} (${label}) — not allowed in ${userMode} mode`);
                              continue;
                            }
                            const body = Math.abs(candles[0][4] - candles[0][1]);
                            const wick = candles[0][2] - candles[0][3];
                            const wickRatio = wick / (body || 1); // prevent divide by zero
                            if (volumeAvg < 5000 && wickRatio > 3) {
                              console.log(`⛔ Skipping ${coin} (${label}) — Illiquid or unstable`);
                              continue;
                            }
                            else {
                                console.log(`⛔ Skipped ${coin} (${label}) [${userMode.toUpperCase()} MODE]
                              Reason: Confidence Score = ${confidenceScore}
                              MACD = ${macd.toFixed(2)}, Histogram Now = ${macdHistNow.toFixed(2)}, Prev = ${macdHistPrev.toFixed(2)}
                              Volume Surge = ${volumeSurge}, EMA Slope = ${emaSlope.toFixed(3)}
                              RSI = ${rsiValue.toFixed(1)}, Profit = ${realisticProfit.toFixed(2)}%`);
                              }
                            if (
                                macd > signal &&
                                rsiValue < 70 &&
                                coinPrice > ema9 &&
                                realisticProfit >= profitTargetLow && realisticProfit <= profitTargetHigh
                            ) {
                                const tradeType = coinPrice > ema9 ? "📈 Short-Term Trade Opportunity" : "Quick Scalping Opportunity";
                                const estimatedProfit = ((tradeAmount * realisticProfit) / 100) - ((tradeAmount * tradeFee) / 100);
                                const profitPercentage = realisticProfit;
                                const key = `${coin}-${label}`;
                                const nowTimestamp = Date.now();
                                const previous = recentAlertsRef.current[key];
                                if (previous) {
                                  const timeDiffMinutes = (nowTimestamp - previous.timestamp) / (1000 * 60);
                                  const profitDiff = Math.abs(previous.profit - realisticProfit);
                                  if (profitDiff < 1.0 && timeDiffMinutes < 60) {
                                    console.log(`⏳ Skipping duplicate for ${coin} (${label}) - profit diff ${profitDiff.toFixed(2)}%, ${timeDiffMinutes.toFixed(1)} min apart`);
                                    continue;
                                  }
                                }
                                recentAlertsRef.current[key] = {
                                  timestamp: nowTimestamp,
                                  profit: realisticProfit
                                };
                                const now = new Date();
                                const options = { timeZone: "America/Chicago", hour12: true, hour: '2-digit', minute: '2-digit', second: '2-digit' };
                                const centralTime = now.toLocaleString('en-US', options);
                                // Updated trade score tagging logic
                                let alertTag = "";
                                if (confidenceScore >= CONFIG.tagTiers.breakout) {
                                  alertTag = "⚡ Breakout Candidate";
                                } else if (confidenceScore >= CONFIG.tagTiers.strong) {
                                  alertTag = "✅ Strong Trade Signal";
                                } else if (confidenceScore >= CONFIG.tagTiers.highPotential) {
                                  alertTag = "📊 High Potential Setup";
                                } else if (confidenceScore >= CONFIG.tagTiers.watchlist) {
                                  alertTag = "🔰 Watchlist Candidate";
                                } else if (confidenceScore >= CONFIG.tagTiers.risky) {
                                  alertTag = "🌗 Risky Setup";
                                } else {
                                  alertTag = "❌ Skip";
                                }
                                // Early skip using rules helper
                                if (shouldSkipByUserMode(userMode, alertTag, label)) {
                                  console.log(`⏳ Skipping ${coin} (${label}) due to ${userMode} rules for tag ${alertTag}`);
                                  continue;
                                }
                                // Server-fed notifications: the in-browser mode-cutoff gate
                                // (CONFIG.modeCutoffs vs confidenceScore) is retired along with the
                                // in-browser LIST source. Per-user mode/tier/grade filtering now
                                // lives server-side in get_my_notifications().
                                if (alertTag === "❌ Skip") {
                                  console.log(`❌ Skipping ${coin} (${label}) — Confidence score ${confidenceScore} too low`);
                                  continue;
                                }
                                if (isDuplicateTrade(notificationLog, coin, label, realisticProfit)) {
                                  console.log(`⛔ Skipped duplicate trade for ${coin} (${label}) — same profit within 1%`);
                                  continue;
                                }
                                const reasons = [
  macd > signal ? "MACD bullish" : null,
  macdHistogramHistory.length >= 3 && macdHistogramHistory[macdHistogramHistory.length - 1] > macdHistogramHistory[macdHistogramHistory.length - 2] && macdHistogramHistory[macdHistogramHistory.length - 2] > macdHistogramHistory[macdHistogramHistory.length - 3] ? "Momentum growing" : null,
  emaSlope > 0 ? "EMA rising" : null,
  latestVolume > volumeAvg ? "Volume surge" : null,
  rsiValue >= CONFIG.rsi.zones.low.min && rsiValue <= CONFIG.rsi.zones.high.max ? "Healthy RSI" : null,
  confirmFromHigherTimeframes ? "Higher TF agrees" : null
].filter(Boolean);
                                // `message` is still built (the body the deferred real-time push build
                                // will reuse) but is currently unread now that the in-browser popup that
                                // consumed it is disabled below — suppress the lone unused-var warning to
                                // hold the eslint baseline without cascade-deleting message/reasons/centralTime.
                                // eslint-disable-next-line no-unused-vars
                                const message = `${alertTag}! ${coin} (${label} timeframe):
Entry: $${formatNumber(coinPrice.toFixed(4))}
Exit: $${formatNumber(selectedExit.toFixed(4))}
Profit Potential: +${profitPercentage.toFixed(2)}%
Estimated Profit: $${estimatedProfit.toFixed(2)}
Confidence Score: ${confidenceScore}/15
                              Reason: ${reasons.join(", ")}
Timestamp: ${centralTime}`;
                                const gradeKey = `${coin}-${label}-${confidenceScore}`;
                                if (!shouldSendNewTrade(gradeTimestampMap, coin, label, confidenceScore)) {
                                  console.log(`⏳ Skipping ${coin} (${label}) Grade ${confidenceScore}/15 — already sent in last hour`);
                                } else {
                                  setGradeTimestampMap((prev) => ({ ...prev, [gradeKey]: Date.now() }));
                                  // Server-fed notifications: in-browser desktop popup DISABLED so the
                                  // server (get_my_notifications) is the single source of what is
                                  // notifiable. A server-driven popup belongs with the deferred
                                  // real-time push build.
                                  // new Notification("Trade Alert", { body: message });
                                }
                                // Server-fed notifications: the in-browser LIST fabrication
                                // (setNotificationLog push) is retired — the list is now read from
                                // get_my_notifications(). Desktop Notification + tradeLog untouched.
                                setTradeLog(prev => [...prev, { coin, entry: coinPrice, exit: selectedExit, result: "pending", timestamp: Date.now() }]);
                            }
                        }
                        // --- COIN UPDATE LOGIC (NEW) ---
                        // Place the Coin Update logic here, after all indicator and trade logic, for every coin/timeframe.
                        if (enableCoinUpdateDisplay) {
                          if (!coinUpdateSummariesBySymbol[coin]) coinUpdateSummariesBySymbol[coin] = {};
                          coinUpdateSummariesBySymbol[coin][label] = {
                            macdHistogram: macdHistogramHistory,
                            emaSlope,
                            rsi: rsiValue,
                            stochRsi: stochRsiK,
                            sentiment: (macdHistogram > 0 && rsiValue >= 54 && rsiValue <= 70) ? "bullish" : (macdHistogram < 0 && rsiValue < 50) ? "bearish" : "neutral",
                            price: coinPrice,
                            volume: latestVolume,
                            timestamp: new Date().toISOString()
                          };
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching indicators:", error);
            }
        };
        fetchIndicators();
        const interval = setInterval(fetchIndicators, SCAN_INTERVAL);
        return () => clearInterval(interval);
    }, [profitTargetLow, profitTargetHigh, tradeAmount, tradeFee, timeframes, userMode, coins, stockSymbols]);
    
    useEffect(() => {
        if (getProvider(selectedCoin) === "alpaca" && !isStockMarketOpen()) {
          return;
        }
        const price = parseFloat(livePrices[selectedCoin]);

        if (followingTrade && entryPrice && price) {
          const bearishThreshold = entryPrice * 0.99;
      
          // 🔁 Find the matching trade to monitor
          const trade = tradeLog.find(
            (log) =>
              log.coin === selectedCoin &&
              log.entry === entryPrice &&
              log.result === "pending"
          );
      
          if (trade) {
            const priceDifference = price - entryPrice;
          const tradeDurationMinutes = (Date.now() - (trade.timestamp || Date.now())) / 60000;
          
          // ⏱ Time-in-Trade Monitoring
          if (tradeDurationMinutes >= 10 && macdHistNow < macdHistPrev && price < exitPrice && price < entryPrice * 1.01) {
            const stallMsg = `⏱ ${selectedCoin} (${selectedTimeframe}) has stalled for over 10 minutes. Momentum fading — consider exiting early.`;
            new Notification("Trade Timeout", { body: stallMsg });
          
            setNotificationLog((prev) => [
              {
                message: stallMsg,
                coin: selectedCoin,
                timeframe: selectedTimeframe,
                type: "early-exit-warning",
              },
              ...prev,
            ]);
          }
      
            // 🚀 Momentum picking up — raise the exit target slightly
            if (price > exitPrice && priceDifference > 0.005 * entryPrice) {
              const newExit = price + 0.003 * entryPrice;
              const adjustedMsg = `📈 Momentum Update: ${selectedCoin} (${selectedTimeframe}) is gaining strength. New exit target: $${newExit.toFixed(2)}.`;
              new Notification("Exit Adjusted", { body: adjustedMsg });
      
              setNotificationLog((prev) => [
                {
                  message: adjustedMsg,
                  coin: selectedCoin,
                  timeframe: selectedTimeframe,
                  type: "adjusted",
                },
                ...prev,
              ]);
              setExitPrice(newExit);
            }
      
            // ⚠️ Weak momentum — suggest lowering expectations
            else if (price < exitPrice && price < entryPrice * 1.005) {
              const earlyExitMsg = `⚠️ Weakening Momentum: ${selectedCoin} (${selectedTimeframe}). Consider reducing your target or exiting early.`;
              new Notification("Exit Warning", { body: earlyExitMsg });
      
              setNotificationLog((prev) => [
                {
                  message: earlyExitMsg,
                  coin: selectedCoin,
                  timeframe: selectedTimeframe,
                  type: "early-exit-warning",
                },
                ...prev,
              ]);
            }
          }
      
          // ✅ Success Exit
          if (price >= exitPrice) {
            const successMsg = `🎉 Trade Successful! ${selectedCoin} (${selectedTimeframe}): Enter at $${entryPrice.toFixed(2)}, Exit at $${exitPrice.toFixed(2)}`;
            new Notification("Trade Success", { body: successMsg });
      
            setNotificationLog((prev) => [
              {
                message: successMsg,
                coin: selectedCoin,
                timeframe: selectedTimeframe,
                type: "success",
              },
              ...prev,
            ]);
      
            setTradeLog((prev) =>
              prev.map((log) =>
                log.entry === entryPrice ? { ...log, result: "success" } : log
              )
            );
      
            setFollowingTrade(false);
          }
      
          // ❌ Bearish Exit
          else if (price <= bearishThreshold) {
            const bearishMsg = `⚠️ Bearish Alert! ${selectedCoin} (${selectedTimeframe}): Entry at $${entryPrice.toFixed(
              2
            )}, Current Price: $${price.toFixed(2)}. Consider exiting.`;
            new Notification("Trade Alert", { body: bearishMsg });
      
            setNotificationLog((prev) => [
              {
                message: bearishMsg,
                coin: selectedCoin,
                timeframe: selectedTimeframe,
                type: "bearish",
              },
              ...prev,
            ]);
      
            setTradeLog((prev) =>
              prev.map((log) =>
                log.entry === entryPrice ? { ...log, result: "bearish" } : log
              )
            );
      
            setFollowingTrade(false);
          }
        }
      }, [livePrices, followingTrade, entryPrice, exitPrice, selectedCoin, selectedTimeframe, tradeLog]);
    
    const handleTimeframeToggle = (label) => {
        setTimeframes(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const filteredLogs =
      activeTab === "followed"
        ? followedTrades
        : tradeLog.filter((log) => log.result !== "pending");
    
    // if (isMobile) {
//   return (
//     <div style={{ padding: 20, color: "#ccc", backgroundColor: "#0d1117", minHeight: "100vh" }}>
//       <h2>📵 Desktop-Only Version</h2>
//       <p>This version of the Crypto Trade Advisor is optimized for desktop. Please visit this app on a larger screen for the best experience.</p>
//     </div>
//   );
// }

    // SIGNALS panel — grade-filtered view of notificationLog. App-body scope so the <aside> in the
    // main return below can read it. "All" passes everything (incl. null grades); a specific grade
    // matches exactly. Count, empty-state, and card map all read this.
    const filteredSignals = notificationLog.filter(
      // Grade filter matches the DISPLAYED grade (gradeLabel — A/B/C, Prime/Base, v1 names).
      (n) => !dismissedIds.has(n.id) && (signalGradeFilter === "All" || (n.gradeLabel || n.grade) === signalGradeFilter)
    );

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden", position: "relative", zIndex: 1, color: "#8b949e", fontFamily: "Arial", background: "transparent" }}>
        <RainCanvas />
        <header style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: "44px",
          minHeight: "44px",
          width: "100%",
          backgroundColor: "rgba(4,4,12,0.92)",
          borderBottom: "0.5px solid rgba(255,255,255,0.05)",
          padding: "0 16px",
          zIndex: 10,
          flexShrink: 0,
        }}>
          {/* LEFT: logo mark */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{
              width: "20px", height: "20px", borderRadius: "4px", flexShrink: 0,
              background: "linear-gradient(90deg, #f97316 50%, #818cf8 50%)",
            }} />
            <span style={{ fontSize: "13px", fontWeight: 600, color: "#c7d2fe", letterSpacing: "0.03em" }}>
              Trade Advisor
            </span>
          </div>

          {/* CENTER: ticker carousel */}
          <div style={{ flex: 1, overflow: "hidden", margin: "0 24px" }}>
            <div style={{ overflow: "hidden", display: "flex", alignItems: "center", height: "44px" }}>
              <div className="ticker-inner" style={{
                display: "flex", alignItems: "center", whiteSpace: "nowrap",
                animation: `ticker-scroll ${Math.max(20, (coins.length + stockSymbols.length) * 3)}s linear infinite`,
              }}>
                {[...coins, ...stockSymbols, ...coins, ...stockSymbols].map((sym, i) => {
                  const price = livePrices[sym];
                  const change = dayChangeMap[sym];
                  const hasPrice = price !== undefined && price !== null && price !== "";
                  const hasChange = change !== undefined && change !== null;
                  return (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
                      <span style={{ color: "#666", fontSize: "11px", fontWeight: 500, marginRight: "6px" }}>{sym}</span>
                      <span style={{ color: "#d0d0d0", fontSize: "12px" }}>
                        {hasPrice ? `$${parseFloat(price).toLocaleString()}` : "--"}
                      </span>
                      {hasChange && (
                        <span style={{ color: change > 0 ? "#4ade80" : change < 0 ? "#f87171" : "#666", fontSize: "11px", marginLeft: "4px" }}>
                          {change > 0 ? "+" : ""}{parseFloat(change).toFixed(2)}%
                        </span>
                      )}
                      <span style={{ color: "#333", margin: "0 12px" }}>·</span>
                    </span>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT: placeholder for future controls */}
          <div style={{ width: "80px" }} />
        </header>

        <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <aside style={{ width: menuCollapsed ? "46px" : "185px", backgroundColor: menuCollapsed ? "transparent" : "rgba(0,0,0,0.4)", padding: 0, transition: "width 0.22s ease", overflow: "hidden", borderRight: "1px solid rgba(129,140,248,0.35)", display: "flex", flexDirection: "column", alignItems: menuCollapsed ? "center" : "flex-start" }}>
            <button onClick={() => setMenuCollapsed(!menuCollapsed)} onMouseEnter={(e) => (e.currentTarget.style.color = "#818cf8")} onMouseLeave={(e) => (e.currentTarget.style.color = "#333")} style={{ backgroundColor: "transparent", color: "#333", border: "none", marginBottom: "20px", cursor: "pointer", display: "flex", alignItems: "center" }}>
                {menuCollapsed ? <IconMenu2 size={20} /> : <IconX size={20} />}
            </button>
            {[
              { key: "quick_view", label: "Watchlist", Icon: IconScan },
              { key: "dashboard", label: "Dashboard", Icon: IconLayoutDashboard },
              { key: "coin_updates", label: "Updates", Icon: IconChartCandle },
              { key: "followed", label: "Followed Trades", Icon: IconBookmark },
              { key: "history", label: "Trade History", Icon: IconHistory },
              { key: "alerts", label: "Notifications", Icon: IconBell },
              { key: "news", label: "News", Icon: IconNews },
              { key: "education", label: "Education", Icon: IconBook },
              { key: "settings", label: "Settings", Icon: IconSettings },
              { key: "admin", label: "Admin", Icon: IconShieldLock },
            ].filter(({ key }) => key !== "history" || SHOW_TRADE_HISTORY)
             .filter(({ key }) => key !== "admin" || userId === FOUNDER_UID).map(({ key, label, Icon }) => {
              const active = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    width: "100%",
                    padding: menuCollapsed ? "8px 0" : "10px 16px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    background: active ? "rgba(129,140,248,0.14)" : "transparent",
                    border: "none",
                    borderLeft: active ? "2px solid #818cf8" : "2px solid transparent",
                    cursor: "pointer",
                    position: "relative",
                    transition: "background 0.15s ease",
                    justifyContent: menuCollapsed ? "center" : "flex-start",
                  }}
                >
                  <span className="nav-icon" style={{ display: "flex", alignItems: "center" }}>
                    <Icon size={22} color={active ? "#818cf8" : "#555"} />
                  </span>
                  <span className="nav-label" style={{ display: menuCollapsed ? "none" : "inline", fontSize: "14px", color: active ? "#c7d2fe" : "#888" }}>
                    {label}
                  </span>
                  {key === "alerts" && notificationLog.filter((n) => !dismissedIds.has(n.id)).length > 0 && (
                    <span style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      backgroundColor: "#f97316",
                      color: "white",
                      borderRadius: "12px",
                      padding: "2px 6px",
                      fontSize: "11px",
                      fontWeight: "bold",
                      pointerEvents: "none",
                    }}>
                      {notificationLog.filter((n) => !dismissedIds.has(n.id)).length}
                    </span>
                  )}
                </button>
              );
            })}
            {/* Log out — action item (not a tab), grouped with the nav items */}
            <button
              onClick={() => supabase.auth.signOut()}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(129,140,248,0.08)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              style={{
                width: "100%",
                padding: menuCollapsed ? "8px 0" : "10px 16px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                background: "transparent",
                border: "none",
                borderLeft: "2px solid transparent",
                cursor: "pointer",
                transition: "background 0.15s ease",
                justifyContent: menuCollapsed ? "center" : "flex-start",
              }}
            >
              <span className="nav-icon" style={{ display: "flex", alignItems: "center" }}>
                <IconLogout size={22} color="#555" />
              </span>
              <span className="nav-label" style={{ display: menuCollapsed ? "none" : "inline", fontSize: "14px", color: "#888" }}>
                Log out
              </span>
            </button>
            </aside>

            <main style={{ flex: 1, overflowY: "auto", minWidth: 0 }}>

{activeTab === "alerts" && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "rgba(4,4,12,0.55)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)" }}>
    {/* Header restyle (founder ask 2026-07-09): serif face + warm gold, icon kept. */}
    <h3 style={{ color: "#e0c891", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "8px" }}><IconBell size={20} /> Notification History</h3>
    {/* Asset-class filter — All / Crypto / Stocks — + symbol search + grade filter */}
    <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "12px", marginBottom: "4px", alignItems: "center" }}>
      {["all", "crypto", "stock"].map(ac => (
        <button key={ac} onClick={() => setNotifClassFilter(ac)}
          style={{ padding: "6px 15px", borderRadius: "16px", fontSize: "11px", cursor: "pointer",
            border: notifClassFilter === ac ? "none" : "0.5px solid rgba(255,255,255,0.08)",
            background: notifClassFilter === ac ? "#f97316" : "rgba(255,255,255,0.04)",
            color: notifClassFilter === ac ? "#04040c" : "#aaa",
            fontWeight: 500, transition: "background 0.3s, color 0.3s" }}>
          {ac === "all" ? "All assets" : ac === "crypto" ? "Crypto" : "Stocks"}
        </button>
      ))}
      <input
        type="text"
        placeholder="Search symbol..."
        value={historySearch}
        onChange={(e) => setHistorySearch(e.target.value)}
        style={{ width: "140px", padding: "6px 12px", borderRadius: "16px", fontSize: "11px",
          background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)",
          color: "#ddd", outline: "none" }}
      />
      <select
        value={historyGradeFilter}
        onChange={(e) => setHistoryGradeFilter(e.target.value)}
        style={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", color: "#aaa", fontSize: "11px", borderRadius: "6px", padding: "2px 6px", cursor: "pointer" }}
      >
        {/* Options = DISPLAYED grades (gradeLabel). V1 dropped from the list (founder call
            2026-07-09); its fires remain reachable via "All grades". */}
        {/* All engines shipped 2026-07-10+ share the Prime/Strong/Base display
            vocabulary (V2 breakout, V3 trend line, V4/V5 when live), so the
            grade filter is engine-agnostic. Retired-buffet A/B/C/Watchlist
            options removed with the engine (rows hidden anyway). V1 stays
            reachable via "All grades". */}
        <option value="All">All grades</option>
        <option value="Prime">Prime</option>
        <option value="Strong">Strong</option>
        <option value="Base">Base</option>
      </select>
      {/* Kind-of-trade filter (founder ask 2026-07-09): filters on setupKind. Pullback +
          pattern kinds (bull flag, …) join this list when those engines/tags ship. */}
      <select
        value={historySetupFilter}
        onChange={(e) => setHistorySetupFilter(e.target.value)}
        style={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", color: "#aaa", fontSize: "11px", borderRadius: "6px", padding: "2px 6px", cursor: "pointer" }}
      >
        <option value="All">All trades</option>
        <option value="Trading Range">Trading Range</option>
        <option value="Breakout">Breakout</option>
        <option value="Trend Line">Trend Line</option>
      </select>
    </div>

    {notificationLog.length === 0 ? (
      <p style={{ color: "#ccc" }}>No alerts yet.</p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {notificationLog
          .map((note, i) => ({ note, i }))
          .filter(({ note }) => {
            // Symbol search (case-insensitive substring) AND grade filter (null grades pass only
            // under "All"), composed with the existing asset-class pill filter.
            if (dismissedIds.has(note.id)) return false; // soft-dismissed (per-user hide)
            if (historySearch && !note.coin.toUpperCase().includes(historySearch.toUpperCase())) return false;
            // Grade filter matches the DISPLAYED grade (gradeLabel — A/B/C, Prime/Base, v1 names).
            if (!(historyGradeFilter === "All" || (note.gradeLabel || note.grade) === historyGradeFilter)) return false;
            // Kind-of-trade filter (Trading Range / Breakout / Trend Line) on setupKind.
            if (!(historySetupFilter === "All" || note.setupKind === historySetupFilter)) return false;
            if (notifClassFilter === "all") return true;
            const isCryptoNote = getProvider(note.coin) === "coinbase";
            if (notifClassFilter === "crypto") return isCryptoNote;
            return !isCryptoNote; // "stock"
          })
          .map(({ note, i }) => {
          const isCrypto = getProvider(note.coin) === "coinbase";
          const accent = isCrypto ? "#818cf8" : "#f97316";
          const assetName = displayName(note.coin);
          // Mirror the original trade-message formatting exactly:
          // old message used formatNumber(x.toFixed(4)), formatNumber = n => n.toLocaleString().
          const formatNumber = (num) => num.toLocaleString();

          // ---- TRADE-type card (rebuilt) ----
          if (note.type === "trade") {
            // tier_label display commented out ("tier_label comment-out" build) — markup + its local
            // consts preserved for later revival/repurposing. The card's voice is now `grade`.
            // Per-tier icon + sentence-case label; fallback for pre-tier notifications.
            // const TIER_ICONS = {
            //   "Strong Trade Signal": IconCircleCheck,
            //   "Breakout Candidate": IconTrendingUp,
            //   "High Potential Setup": IconChartBar,
            //   "Watchlist Candidate": IconEye,
            //   "Risky Setup": IconAlertTriangle,
            // };
            // const TierIcon = (note.tier && TIER_ICONS[note.tier]) || IconBell;
            // const tierLabel = note.tier
            //   ? note.tier.charAt(0) + note.tier.slice(1).toLowerCase()
            //   : "Trade signal";
            /* former tier_label JSX, preserved for revival (Row 3 signal line):
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", color: tierColorFor(note.tier) }}>
                <TierIcon size={14} stroke={2} />
                <span style={{ fontSize: "12px", fontWeight: 500 }}>{tierLabel}</span>
              </div>
            */
            return (
              <div
                key={note.timestamp || `${note.coin}-${note.timeframe}-${i}`}
                style={{
                  backgroundColor: "rgba(22,22,22,0.82)",
                  // Asset-class border (founder ask 2026-07-09): crypto = indigo, stock = orange —
                  // same color language as the accent/chips, readable before the title is.
                  border: `1px solid ${isCrypto ? "rgba(129,140,248,0.55)" : "rgba(249,115,22,0.55)"}`,
                  borderRadius: "10px",
                  padding: "14px",
                  marginBottom: "10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "14px",
                }}
              >
                {/* LEFT: content */}
                <div style={{ flex: 1, minWidth: 0 }}>

                  {/* HEADER + SCORE */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>

                    {/* HEADER (left) */}
                    <div>
                      {/* Row 1: name + class pill + timeframe pill */}
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "18px", fontWeight: 500, color: "#e8e8e8" }}>
                          {assetName}
                        </span>
                        <span style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          padding: "2px 9px",
                          borderRadius: "6px",
                          backgroundColor: isCrypto ? "rgba(129,140,248,0.16)" : "rgba(249,115,22,0.16)",
                          color: isCrypto ? "#a5b4fc" : "#f9a86b",
                        }}>
                          {isCrypto ? "Crypto" : "Stock"}
                        </span>
                        <span style={{
                          fontSize: "11px",
                          fontWeight: 500,
                          padding: "2px 8px",
                          borderRadius: "6px",
                          backgroundColor: isCrypto ? "rgba(129,140,248,0.10)" : "rgba(249,115,22,0.10)",
                          color: isCrypto ? "#9aa4d8" : "#d18a4f",
                        }}>
                          {note.timeframe}
                        </span>
                      </div>
                      {/* Row 2: ticker */}
                      <div style={{ fontSize: "12px", color: "#999", marginTop: "3px" }}>
                        {note.coin}
                        {note.timestamp && (
                          <span style={{ color: "#555", marginLeft: "6px" }}>
                            · {new Date(note.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>
                      {/* tier_label line removed here — markup preserved in the commented const block above */}
                    </div>

                    {/* GRADE (right) */}
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ lineHeight: 1 }}>
                        {/* Uniform grade sizing (founder ask 2026-07-09): one size for every
                            grade — "Watchlist" and single letters render identically. */}
                        <span style={{ fontSize: "26px", fontWeight: 600, color: accent }}>
                          {note.gradeLabel || note.grade}
                        </span>
                      </div>
                      <div style={{ fontSize: "10px", color: "#888", textTransform: "uppercase", marginTop: "2px" }}>
                        Grade
                        {/* Engine-version chip (v1/v2/v3 parallel comparison) */}
                        <span style={{
                          marginLeft: "6px", padding: "1px 5px", borderRadius: "4px",
                          fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em",
                          color: engineChipStyle(note.version).fg,
                          backgroundColor: engineChipStyle(note.version).bg,
                        }}>
                          {engineChipStyle(note.version).text}
                        </span>
                      </div>
                      {/* Setup-type label — two lines, own row (no overflow): KIND of trade in
                          skinny italic serif, SIDE in smaller muted text beneath. */}
                      {note.setupKind && (
                        <div style={{ marginTop: "5px", textAlign: "right" }}>
                          <div style={{ fontSize: "13px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#ddd", lineHeight: 1.2 }}>
                            {note.setupKind}
                          </div>
                          {note.setupSide && (
                            <div style={{ fontSize: "12px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#999", lineHeight: 1.3 }}>
                              {note.setupSide}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                  </div>

                  {/* DATA ROWS */}
                  <div style={{ marginTop: "14px", borderTop: "0.5px solid rgba(255,255,255,0.06)", paddingTop: "12px", display: "flex", flexDirection: "column", gap: "9px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "13px", color: "#888" }}>Entry</span>
                      <span style={{ fontSize: "14px", fontWeight: 500, color: "#ddd" }}>${formatNumber(note.entry.toFixed(4))}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "13px", color: "#888" }}>Exit</span>
                      <span style={{ fontSize: "14px", fontWeight: 500, color: "#ddd" }}>${formatNumber(note.exit.toFixed(4))}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "13px", color: "#888" }}>Profit %</span>
                      <span style={{ fontSize: "14px", fontWeight: 500, color: "#4ade80" }}>+{note.profitPct.toFixed(2)}%</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "13px", color: "#888" }}>Est. profit</span>
                      <span style={{ fontSize: "14px", fontWeight: 500, color: "#4ade80" }}>${note.estProfit.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* REASON TAGS */}
                  {note.reasons && note.reasons.length > 0 && (
                    <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {note.reasons.map((r, ri) => (
                        <span key={ri} style={{
                          fontSize: "11px",
                          padding: "3px 9px",
                          borderRadius: "6px",
                          backgroundColor: "rgba(129,140,248,0.12)",
                          color: "#a5b4fc",
                        }}>
                          {r}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* CONFLUENCE TAGS — additive, tag-only descriptors (worker.cjs buildConfluenceTags).
                      Separate from REASON TAGS above (that slot is reserved for the Phase 4 AI reason-
                      string work on the roadmap); this one is deterministic HTF/volume/breakout state,
                      always present once a row has fingerprint.tags. Never affects grade/tier. */}
                  {note.tags && note.tags.length > 0 && (
                    <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {note.tags.map((t, ti) => (
                        <span key={ti} style={{
                          fontSize: "11px",
                          padding: "3px 9px",
                          borderRadius: "6px",
                          backgroundColor: "rgba(255,255,255,0.06)",
                          color: "#aaa",
                          border: "0.5px solid rgba(255,255,255,0.1)",
                        }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* BOTTOM ACTIONS — Follow full-width, Remove beside it */}
                  <div style={{ marginTop: "14px", display: "flex", gap: "8px" }}>
                    <button
                      onClick={() => handleFollowTrade(note)}
                      style={{
                        flex: 1,
                        minHeight: "44px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500,
                        backgroundColor: isCrypto ? "rgba(129,140,248,0.12)" : "rgba(249,115,22,0.12)",
                        color: isCrypto ? "#a5b4fc" : "#f9a86b",
                        border: `0.5px solid ${isCrypto ? "rgba(129,140,248,0.3)" : "rgba(249,115,22,0.3)"}`,
                      }}
                    >
                      <IconBookmark size={16} stroke={2} />
                      Follow trade
                    </button>
                    <button
                      onClick={() => {
                        setSelectedNote(note);
                        setSelectedCoin(note.coin);
                        setSelectedInterval(note.timeframe);
                        setActiveTab("more_info");
                      }}
                      style={{
                        flex: 1,
                        minHeight: "44px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500,
                        backgroundColor: "rgba(255,255,255,0.04)",
                        color: "#aaa",
                        border: "0.5px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <IconInfoCircle size={16} stroke={2} />
                      More Info
                    </button>
                    {/* Dismiss — per-user soft hide; global signals row is NEVER touched.
                        stopPropagation so the card's click handlers do not fire. Peer chrome
                        with More Info; icon-square (44px tap target), muted → copper hover. */}
                    <button
                      type="button"
                      aria-label="Dismiss notification"
                      onClick={(e) => { e.stopPropagation(); handleDismissNotification(note.id); }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#B87333"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#666"; }}
                      style={{
                        minWidth: "44px",
                        minHeight: "44px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 500,
                        backgroundColor: "rgba(255,255,255,0.04)",
                        color: "#666",
                        border: "0.5px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      <IconTrash size={16} stroke={2} />
                    </button>
                  </div>

                </div>

              </div>
            );
          }

          // ---- NON-TRADE notes: existing markup unchanged ----
          return (
          <div
            key={note.timestamp || `${note.coin}-${note.timeframe}-${i}`}
            style={{
              backgroundColor: "rgba(22,27,34,0.96)",
              padding: "15px",
              borderRadius: "8px",
              // Asset-class border — same rule as the trade card above.
              border: `1px solid ${isCrypto ? "rgba(129,140,248,0.55)" : "rgba(249,115,22,0.55)"}`,
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center"
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ marginRight: "10px", fontWeight: "bold", color: "#58a6ff" }}>
                {notificationLog.length - i}.
              </span>
              <div>
                <p
                  onClick={() => {
                    setSelectedCoin(note.coin);
                    setActiveTab("dashboard");
                  }}
                  style={{ cursor: "pointer", color: "#58a6ff" }}
                >
                  {note.message}
                </p>
                <p style={{ fontSize: "12px", color: "#aaa" }}>
                  Coin: {note.coin} | Timeframe: {note.timeframe} | Type: {note.type}
                </p>
                {note.type === "trade" && (note.fvgs?.length || note.supportResistance) && (
                  <div style={{ marginTop: "8px", padding: "10px", backgroundColor: "rgba(13,17,23,0.96)", borderRadius: "6px", border: "1px solid #2c2f33", fontSize: "12px" }}>
                    {note.fvgs?.length > 0 && (() => {
                      // Display ordering only: unfilled first, then filled, capped at 6 rows.
                      // Runs on a COPY — note.fvgs stays the complete array.
                      const shownFvgs = [...note.fvgs]
                        .sort((a, b) => (a.filled === b.filled ? 0 : a.filled ? 1 : -1))
                        .slice(0, 6);
                      return (
                      <div style={{ marginBottom: note.supportResistance ? "8px" : 0 }}>
                        <div style={{ color: "#58a6ff", fontWeight: "bold", marginBottom: "4px" }}>
                          Fair Value Gaps: {note.fvgs.length} ({note.fvgs.filter((g) => !g.filled).length} unfilled)
                        </div>
                        {shownFvgs.map((g, gi) => (
                          <div key={gi} style={{ color: "#aaa" }}>
                            {g.type === "bullish" ? "🟢" : "🔴"} {g.type} · ${g.bottom?.toLocaleString()}–${g.top?.toLocaleString()} · {g.sizePct?.toFixed(2)}% · {g.filled ? "filled" : "open"}
                          </div>
                        ))}
                        {note.fvgs.length > shownFvgs.length && (
                          <div style={{ color: "#6e7681", fontStyle: "italic" }}>
                            +{note.fvgs.length - shownFvgs.length} more
                          </div>
                        )}
                      </div>
                      );
                    })()}
                    {note.supportResistance && (
                      <div>
                        <div style={{ color: "#58a6ff", fontWeight: "bold", marginBottom: "4px" }}>
                          Support / Resistance (price ${note.supportResistance.currentPrice?.toLocaleString()})
                        </div>
                        {note.supportResistance.resistance?.map((z, zi) => (
                          <div key={`r${zi}`} style={{ color: "#aaa" }}>
                            R · mid ${z.priceMid?.toLocaleString()} · ${z.priceLow?.toLocaleString()}–${z.priceHigh?.toLocaleString()} · {z.touches} touches · strength {z.strength}
                          </div>
                        ))}
                        {note.supportResistance.support?.map((z, zi) => (
                          <div key={`s${zi}`} style={{ color: "#aaa" }}>
                            S · mid ${z.priceMid?.toLocaleString()} · ${z.priceLow?.toLocaleString()}–${z.priceHigh?.toLocaleString()} · {z.touches} touches · strength {z.strength}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <button
                onClick={() => handleFollowTrade(note)}
                style={{
                  backgroundColor: "#2563eb",
                  color: "white",
                  border: "none",
                  padding: "6px 12px",
                  borderRadius: "6px",
                  cursor: "pointer"
                }}
              >
                Follow Trade
              </button>
            </div>
          </div>
          );
        })}
      </div>
    )}
  </div>
)}

{activeTab === "more_info" && (() => {
  // Real caps from the note so steppers can't exceed available data (independent per side).
  const supportMax = selectedNote?.supportResistance?.support?.length || 0;
  const resistanceMax = selectedNote?.supportResistance?.resistance?.length || 0;
  const fvgPool = (selectedNote?.fvgs || [])
    .filter((g) => !g.filled && g.sizePct >= 0.3)
    .map((g) => {
      const zoneMid = (g.top + g.bottom) / 2;
      const cp = selectedNote?.supportResistance?.currentPrice ?? g.top;
      return { ...g, _distPct: Math.abs((zoneMid - cp) / cp) * 100 };
    })
    .sort((a, b) => a._distPct - b._distPct);
  const fvgMax = fvgPool.length;
  const accentColor = getProvider(selectedNote?.coin || selectedCoin) === "coinbase" ? "#818cf8" : "#f97316";
  // Live price for the More Info header card — livePrices is App state (interval poll, string values
  // keyed by full symbol), so the card re-renders on each poll automatically. Undefined/NaN (pre-first
  // poll or a symbol not in the followed list) → "--", NO fallback to the signal-time price.
  const livePrice = parseFloat(livePrices[selectedNote?.coin]);
  const livePriceStr = Number.isFinite(livePrice)
    ? `$${livePrice.toLocaleString("en-US", {
        minimumFractionDigits: livePrice >= 1 ? 2 : 4,
        maximumFractionDigits: livePrice >= 1 ? 2 : 4,
      })}`
    : "--";

  const toggleBtn = (on) => ({
    minHeight: "44px", padding: "0 14px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px",
    borderRadius: "6px", fontSize: "12px", fontWeight: 500, cursor: "pointer",
    backgroundColor: on ? `${accentColor}22` : "rgba(255,255,255,0.04)",
    border: `0.5px solid ${on ? accentColor : "rgba(255,255,255,0.1)"}`,
    color: on ? accentColor : "#aaa",
  });
  const labelStyle = { fontSize: "11px", color: "#888", textTransform: "uppercase", letterSpacing: "0.04em" };
  const countStyle = { minWidth: "16px", textAlign: "center", color: "#ddd", fontSize: "13px", fontWeight: 500 };
  const chipBtn = (on, color, disabled = false) => ({
    minHeight: "44px", minWidth: "38px", padding: "0 10px",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    borderRadius: "6px", fontSize: "12px", fontWeight: 500,
    cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.35 : 1,
    backgroundColor: on ? `${color}22` : "rgba(255,255,255,0.04)",
    border: `0.5px solid ${on ? color : "rgba(255,255,255,0.1)"}`,
    color: on ? color : "#888",
  });
  // Compact per-type master toggle — reuses the toggle visual at a smaller size.
  const masterBtn = (on) => ({ ...toggleBtn(on), minHeight: "24px", padding: "0 8px", fontSize: "11px", gap: "4px" });
  // Level keys per type for the current note (drive both master handler + chip render).
  const supportKeys = Array.from({ length: supportMax }, (_, i) => (i === 0 ? "S" : `S${i + 1}`));
  const resistanceKeys = Array.from({ length: resistanceMax }, (_, i) => (i === 0 ? "R" : `R${i + 1}`));
  const fvgKeys = Array.from({ length: fvgMax }, (_, i) => `F${i + 1}`);
  // Reusable overlay-group renderer — header (label + master) + chip container, behavior-identical
  // to the former inline S/R/FVG groups (same handlers/state/style helpers/aria). `vertical` only
  // flips flex-direction/wrap/alignment for the rail layout.
  const renderOverlayGroup = ({ label, keys, max, type, color, vertical, compact }) => (
    <div style={{ display: "flex", flexDirection: "column", alignItems: vertical ? "stretch" : "flex-start", gap: "4px", width: compact ? "48px" : "auto", flexShrink: 0 }}>
      <div style={{ display: "flex", flexDirection: compact ? "column" : "row", alignItems: "center", gap: compact ? "3px" : "6px", justifyContent: vertical && !compact ? "center" : "flex-start" }}>
        <span style={{ ...labelStyle, textAlign: "center" }}>{label}</span>
        {max > 0 && (
          <button aria-label={`Toggle all ${label}`} aria-pressed={overlayMaster[type]}
            onClick={() => (overlayMaster[type] ? masterOff(type) : masterOn(type, keys))}
            style={compact ? { ...masterBtn(overlayMaster[type]), width: "100%", padding: 0, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" } : masterBtn(overlayMaster[type])}>{overlayMaster[type] ? "On" : "Off"}</button>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: vertical ? "column" : "row", flexWrap: vertical ? "nowrap" : "wrap", alignItems: vertical ? "stretch" : "center", gap: compact ? "4px" : "6px" }}>
        {max === 0
          ? <span style={countStyle}>—</span>
          : keys.map((key) => (
              <button key={key} aria-label={`Toggle ${key}`}
                onClick={() => (overlayMaster[type] ? toggleOverlay(key) : reviveWithOne(type, keys, key))}
                style={compact
                  ? { ...chipBtn(overlayMaster[type] && !hiddenOverlays.has(key), color, !overlayMaster[type]), minWidth: 0, width: "100%", minHeight: 0, height: "28px", padding: "0", fontSize: "12px" }
                  : chipBtn(overlayMaster[type] && !hiddenOverlays.has(key), color, !overlayMaster[type])}>{key}</button>
            ))}
      </div>
    </div>
  );

  // Mobile dropdown form of an overlay group (founder ask 2026-07-10): a native <details>
  // accordion so the S/R and FVG level controls sit ABOVE the chart instead of crushing it
  // in a side rail. Same handlers/state as renderOverlayGroup — On/Off master + level chips.
  const overlayDropdown = ({ label, keys, max, type, color }) => (
    <details style={{ flex: "1 1 100px", minWidth: 0, background: "rgba(22,22,22,0.6)", border: "0.5px solid #303030", borderRadius: "8px" }}>
      <summary style={{ listStyle: "none", cursor: "pointer", padding: "9px 12px", fontSize: "12px", fontWeight: 600, color: "#ccc", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px" }}>
        <span>{label}</span>
        <span style={{ fontSize: "10px", color: overlayMaster[type] ? color : "#666", fontWeight: 700 }}>{max > 0 ? (overlayMaster[type] ? "ON ▾" : "OFF ▾") : "— ▾"}</span>
      </summary>
      <div style={{ padding: "4px 10px 10px", display: "flex", flexWrap: "wrap", gap: "6px", alignItems: "center" }}>
        {max > 0 && (
          <button aria-label={`Toggle all ${label}`} aria-pressed={overlayMaster[type]}
            onClick={() => (overlayMaster[type] ? masterOff(type) : masterOn(type, keys))}
            style={{ ...masterBtn(overlayMaster[type]), minHeight: "32px", padding: "0 12px" }}>{overlayMaster[type] ? "On" : "Off"}</button>
        )}
        {max === 0
          ? <span style={countStyle}>—</span>
          : keys.map((key) => (
              <button key={key} aria-label={`Toggle ${key}`}
                onClick={() => (overlayMaster[type] ? toggleOverlay(key) : reviveWithOne(type, keys, key))}
                style={{ ...chipBtn(overlayMaster[type] && !hiddenOverlays.has(key), color, !overlayMaster[type]), minHeight: "32px", padding: "0 12px" }}>{key}</button>
            ))}
      </div>
    </details>
  );

  return (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "rgba(4,4,12,0.55)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)" }}>
    {/* Back (left) + Dismiss (right) — standalone top row, above the card. The trash lives
        here, NOT inside the card. Dismiss soft-hides per-user; global signals row untouched;
        navigate back to Notifications so the user isn't left on a now-hidden signal. */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <button
        onClick={() => setActiveTab("alerts")}
        style={{
          minHeight: "44px",
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          padding: "0 14px",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 500,
          backgroundColor: "rgba(255,255,255,0.04)",
          color: "#aaa",
          border: "0.5px solid rgba(255,255,255,0.1)",
        }}
      >
        ← Back
      </button>
      <button
        type="button"
        aria-label="Dismiss signal"
        onClick={(e) => { e.stopPropagation(); handleDismissNotification(selectedNote?.id); setActiveTab("alerts"); }}
        onMouseEnter={(e) => { e.currentTarget.style.color = "#B87333"; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = "#666"; }}
        style={{
          minWidth: "44px",
          minHeight: "44px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
          background: "none",
          border: "none",
          color: "#666",
          cursor: "pointer",
        }}
      >
        <IconTrash size={18} stroke={2} />
      </button>
    </div>
    {selectedNote ? (
      <>
        <h3 style={{ color: "#e8e8e8", fontWeight: 500, marginTop: "14px", marginBottom: "12px" }}>
          {displayName(selectedNote?.coin || selectedCoin)} · {selectedNote?.timeframe || selectedInterval}
        </h3>
        {/* SETUP SUMMARY CARD — presentational; reads selectedNote fields already on page */}
        <div style={{
          backgroundColor: "rgba(20,20,20,0.6)",
          border: "1px solid #2a2a2a",
          borderLeft: `2px solid ${accentColor}`,
          borderRadius: "0 10px 10px 0",
          padding: "14px 16px",
          marginBottom: "14px",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "22px", fontWeight: 500, color: "#f0f0f0" }}>
                  {displayName(selectedNote.coin)}
                </span>
                <span style={{ fontSize: "10px", color: accentColor, backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", padding: "2px 8px", borderRadius: "6px" }}>
                  {getProvider(selectedNote.coin) === "coinbase" ? "Crypto" : "Stock"}
                </span>
                <span style={{ fontSize: "10px", color: "#e8a060", backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", padding: "2px 8px", borderRadius: "6px" }}>
                  {selectedNote.timeframe}
                </span>
              </div>
              <div style={{ fontSize: "11px", color: "#aaa", marginTop: "4px" }}>
                {selectedNote.coin} · {new Date(selectedNote.timestamp).toLocaleDateString([], { month: "short", day: "numeric" })} · {new Date(selectedNote.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "19px", fontWeight: 500, color: "#ddd", lineHeight: 1 }}>{livePriceStr}</div>
              <div style={{ fontSize: "11px", color: "#F87171", letterSpacing: "0.1em", marginTop: "4px" }}>Live</div>
            </div>
            {/* Grade cluster — setup label SIDE BY SIDE with the grade (founder ask
                2026-07-09) so the card keeps its normal height/width. */}
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              {selectedNote.setupKind && (
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "13px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#ddd", lineHeight: 1.2 }}>
                    {selectedNote.setupKind}
                  </div>
                  {selectedNote.setupSide && (
                    <div style={{ fontSize: "12px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#999", lineHeight: 1.3 }}>
                      {selectedNote.setupSide}
                    </div>
                  )}
                </div>
              )}
              <div style={{ textAlign: "right" }}>
                {/* Displayed grade (gradeLabel — A/B/C/Prime/…), not the stored v1-vocabulary
                    grade; matches every other card. Engine chip beside it. */}
                <div style={{ fontSize: "19px", fontWeight: 500, color: accentColor, lineHeight: 1 }}>
                  {selectedNote.gradeLabel || selectedNote.grade}
                  {selectedNote.version && (
                    <span style={{
                      marginLeft: "6px", padding: "1px 5px", borderRadius: "4px", verticalAlign: "middle",
                      fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em",
                      color: engineChipStyle(selectedNote.version).fg,
                      backgroundColor: engineChipStyle(selectedNote.version).bg,
                    }}>
                      {engineChipStyle(selectedNote.version).text}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "9px", color: "#666", letterSpacing: "0.1em", marginTop: "4px" }}>GRADE</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: "13px", paddingTop: "13px", borderTop: "1px solid #2a2a2a", display: "flex", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: "9px", color: "#666" }}>Entry</div>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#ddd" }}>${selectedNote.entry?.toFixed(2)}</div>
            </div>
            <div>
              <div style={{ fontSize: "9px", color: "#666" }}>Exit</div>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#ddd" }}>${selectedNote.exit?.toFixed(2)}</div>
            </div>
            <div>
              <div style={{ fontSize: "9px", color: "#666" }}>Profit %</div>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#4ade80" }}>+{selectedNote.profitPct?.toFixed(2)}%</div>
            </div>
            <div>
              <div style={{ fontSize: "9px", color: "#666" }}>Est. Profit</div>
              <div style={{ fontSize: "13px", fontWeight: 500, color: "#4ade80" }}>${selectedNote.estProfit?.toFixed(2)}</div>
            </div>
          </div>
        </div>
        {/* FILTER BAR — three cards SIDE BY SIDE (founder ask 2026-07-09): Chart ·
            Indicators · Drawing tools as columns, buttons stacking ~2 per row inside each,
            so the chart stays high on the page. Wraps to stacked cards on narrow screens. */}
        <div style={{ display: "flex", flexDirection: "row", flexWrap: "wrap", gap: "10px", marginBottom: "12px", alignItems: "stretch" }}>
        {/* CARD 1 — Chart. Card widths are proportional to column count + label length
            (founder ask: symmetry — Drawing > Chart > Indicators, no button overflow). */}
        <div style={{ flex: "2.2 1 220px", minWidth: "200px", padding: "8px 10px", backgroundColor: "rgba(22,22,22,0.4)", borderRadius: "8px" }}>
          <span style={{ fontSize: "10px", color: "#666", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: "8px" }}>Chart</span>
          {/* 2-row grid, columns grow as needed (founder ask: rows of two, add columns) */}
          <div style={{ display: "grid", gridAutoFlow: "column", gridTemplateRows: "repeat(2, minmax(0, auto))", gridAutoColumns: "minmax(0, 1fr)", gap: "10px" }}>
          {/* Entry/Exit toggle */}
          <button onClick={() => setOverlayFilters((f) => ({ ...f, showEntryExit: !f.showEntryExit }))}
            style={toggleBtn(overlayFilters.showEntryExit)}>Entry / Exit</button>
          {/* Marker toggle */}
          <button onClick={() => setOverlayFilters((f) => ({ ...f, showMarker: !f.showMarker }))}
            style={toggleBtn(overlayFilters.showMarker)}>Marker</button>
          {/* View Live Chart — moved into the Chart card (was after Vol Profile). Jumps to the
              in-app Dashboard tab on this signal's symbol/timeframe. Not a toggle. */}
          <button
            type="button"
            onClick={() => {
              setSelectedCoin(selectedNote.coin);
              setDashboardInterval(selectedNote.timeframe);
              setActiveTab("dashboard");
            }}
            style={toggleBtn(false)}
          >
            View Live Chart
          </button>
          </div>
        </div>
        {/* CARD 2 — Indicators (shortest labels → narrowest card) */}
        <div style={{ flex: "1.8 1 180px", minWidth: "170px", padding: "8px 10px", backgroundColor: "rgba(22,22,22,0.4)", borderRadius: "8px" }}>
          <span style={{ fontSize: "10px", color: "#666", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: "8px" }}>Indicators</span>
          <div style={{ display: "grid", gridAutoFlow: "column", gridTemplateRows: "repeat(2, minmax(0, auto))", gridAutoColumns: "minmax(0, 1fr)", gap: "10px" }}>
          {/* RSI subpanel toggle */}
          <button onClick={() => setOverlayFilters((f) => ({ ...f, showRSI: !f.showRSI }))}
            style={toggleBtn(overlayFilters.showRSI)}>RSI</button>
          {/* MACD subpanel toggle */}
          <button onClick={() => setOverlayFilters((f) => ({ ...f, showMACD: !f.showMACD }))}
            style={toggleBtn(overlayFilters.showMACD)}>MACD</button>
          {/* Bollinger Bands overlay toggle */}
          <button onClick={() => setOverlayFilters((f) => ({ ...f, showBB: !f.showBB }))}
            style={toggleBtn(overlayFilters.showBB)}>BB</button>
          {/* Volume Profile overlay toggle */}
          <button onClick={() => setOverlayFilters((f) => ({ ...f, showVolProfile: !f.showVolProfile }))}
            style={toggleBtn(overlayFilters.showVolProfile)}>Vol Profile</button>
          </div>
        </div>
        {/* CARD 3 — Drawing tools (4 columns + longest labels → widest card) */}
        <div style={{ flex: "4.5 1 420px", minWidth: "320px", padding: "8px 10px", backgroundColor: "rgba(22,22,22,0.4)", borderRadius: "8px" }}>
          <span style={{ fontSize: "10px", color: "#666", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: "8px" }}>Drawing tools</span>
          <div style={{ display: "grid", gridAutoFlow: "column", gridTemplateRows: "repeat(2, minmax(0, auto))", gridAutoColumns: "minmax(0, 1fr)", gap: "10px" }}>
          {/* Measure tool — click-drag price/%/candle-count readout, persisted per user ×
              symbol × context "more_info" via Supabase (synced across devices, not
              localStorage — see measureTool.js). Needs a signed-in user, same as Draw Line. */}
          {userId && (
            <button
              type="button"
              onClick={() => armMoreInfoTool(moreInfoMeasureMode ? null : "measure")}
              style={{
                minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                fontSize: "12px", fontWeight: 500,
                backgroundColor: moreInfoMeasureMode ? "rgba(129,140,248,0.13)" : "rgba(255,255,255,0.04)",
                border: `0.5px solid ${moreInfoMeasureMode ? "#818cf8" : "rgba(255,255,255,0.1)"}`,
                color: moreInfoMeasureMode ? "#818cf8" : "#aaa",
              }}
            >
              {moreInfoMeasureMode ? "Drag to measure…" : "Measure"}
            </button>
          )}
          {/* Drawing tools — "Draw Line" arms one-shot click-to-place on the price pane below;
              each saved line renders as a chip with its price + a delete ×. Context "more_info"
              keeps this chart's lines separate from the Dashboard chart's ("dashboard"). Only
              rendered when signed in (drawings are per-user). Mirrors SignalChart.js. */}
          {userId && (
            <>
              <button
                type="button"
                onClick={() => armMoreInfoTool(moreInfoDrawMode ? null : "draw")}
                style={{
                  minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                  backgroundColor: moreInfoDrawMode ? "rgba(249,115,22,0.13)" : "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${moreInfoDrawMode ? "#f97316" : "rgba(255,255,255,0.1)"}`,
                  color: moreInfoDrawMode ? "#f97316" : "#aaa",
                }}
              >
                {moreInfoDrawMode ? "Click to place (click to stop)" : "Horiz Line"}
              </button>
            </>
          )}
          {/* Trend Line tool — click-drag diagonal line between two arbitrary points, persisted
              as a list (like Draw Line) per user × symbol × context "more_info" via Supabase
              chart_advanced_drawings — see trendLineTool.js. Mirrors SignalChart.js. */}
          {userId && (
            <>
              <button
                type="button"
                onClick={() => armMoreInfoTool(moreInfoTrendLineMode ? null : "trend")}
                style={{
                  minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                  backgroundColor: moreInfoTrendLineMode ? "rgba(249,115,22,0.13)" : "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${moreInfoTrendLineMode ? "#f97316" : "rgba(255,255,255,0.1)"}`,
                  color: moreInfoTrendLineMode ? "#f97316" : "#aaa",
                }}
              >
                {moreInfoTrendLineMode ? "Drag to draw (click to stop)" : "Trend Line"}
              </button>
              {moreInfoTrendLines.map((l, i) => (
                <span key={l.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "#ccc", fontSize: "12px",
                }}>
                  Trend {i + 1}
                  <button
                    type="button"
                    aria-label="Delete trend line"
                    onClick={() => deleteMoreInfoTrendLine(l.id)}
                    style={{
                      background: "none", border: "none", color: "#888", cursor: "pointer",
                      fontSize: "13px", lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
          {/* Fib Retracement tool — click-drag with standard retracement levels, persisted
              as a list per user × symbol × context "more_info" via Supabase
              chart_advanced_drawings — see fibTool.js. Mirrors SignalChart.js. */}
          {userId && (
            <>
              <button
                type="button"
                onClick={() => armMoreInfoTool(moreInfoFibMode ? null : "fib")}
                style={{
                  minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                  backgroundColor: moreInfoFibMode ? "rgba(234,179,8,0.13)" : "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${moreInfoFibMode ? "#eab308" : "rgba(255,255,255,0.1)"}`,
                  color: moreInfoFibMode ? "#eab308" : "#aaa",
                }}
              >
                {moreInfoFibMode ? "Drag to draw (click to stop)" : "Fib Retracement"}
              </button>
              {moreInfoFibs.map((f, i) => (
                <span key={f.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "#ccc", fontSize: "12px",
                }}>
                  Fib {i + 1}
                  <button
                    type="button"
                    aria-label="Delete fib retracement"
                    onClick={() => deleteMoreInfoFib(f.id)}
                    style={{
                      background: "none", border: "none", color: "#888", cursor: "pointer",
                      fontSize: "13px", lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
          {/* Parallel Channel tool — click-drag main line + one more click for the offset,
              persisted as a list per user × symbol × context "more_info" via Supabase
              chart_advanced_drawings — see channelTool.js. Mirrors SignalChart.js. */}
          {userId && (
            <>
              <button
                type="button"
                onClick={() => armMoreInfoTool(moreInfoChannelMode ? null : "channel")}
                style={{
                  minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                  backgroundColor: moreInfoChannelMode ? "rgba(129,140,248,0.13)" : "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${moreInfoChannelMode ? "#818cf8" : "rgba(255,255,255,0.1)"}`,
                  color: moreInfoChannelMode ? "#818cf8" : "#aaa",
                }}
              >
                {!moreInfoChannelMode ? "Parallel Channel" : moreInfoChannelPhase === "awaiting-offset" ? "Click to set width" : "Drag to draw (click to stop)"}
              </button>
              {moreInfoChannels.map((c, i) => (
                <span key={c.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "#ccc", fontSize: "12px",
                }}>
                  Channel {i + 1}
                  <button
                    type="button"
                    aria-label="Delete channel"
                    onClick={() => deleteMoreInfoChannel(c.id)}
                    style={{
                      background: "none", border: "none", color: "#888", cursor: "pointer",
                      fontSize: "13px", lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
          {/* Ray tool — click-drag, extended past the second anchor to the pane edge,
              persisted as a list per user × symbol × context "more_info" via Supabase
              chart_advanced_drawings — see rayTool.js. Mirrors SignalChart.js. */}
          {userId && (
            <>
              <button
                type="button"
                onClick={() => armMoreInfoTool(moreInfoRayMode ? null : "ray")}
                style={{
                  minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                  backgroundColor: moreInfoRayMode ? "rgba(34,211,238,0.13)" : "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${moreInfoRayMode ? "#22d3ee" : "rgba(255,255,255,0.1)"}`,
                  color: moreInfoRayMode ? "#22d3ee" : "#aaa",
                }}
              >
                {!moreInfoRayMode ? "Ray" : moreInfoRayPhase === "placing" ? "Click to finish" : "Click to start"}
              </button>
              {moreInfoRays.map((r, i) => (
                <span key={r.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "#ccc", fontSize: "12px",
                }}>
                  Ray {i + 1}
                  <button
                    type="button"
                    aria-label="Delete ray"
                    onClick={() => deleteMoreInfoRay(r.id)}
                    style={{
                      background: "none", border: "none", color: "#888", cursor: "pointer",
                      fontSize: "13px", lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
          {/* Long Position tool — click-drag entry+risk, target auto-derived at a fixed
              2:1 reward:risk, persisted as a list per user × symbol × context "more_info"
              via Supabase chart_advanced_drawings — see longPositionTool.js. Mirrors
              SignalChart.js. Last of the original 4-tool "advanced drawing tools" plan. */}
          {userId && (
            <>
              <button
                type="button"
                onClick={() => armMoreInfoTool(moreInfoLongMode ? null : "long")}
                style={{
                  minHeight: "44px", padding: "0 14px", borderRadius: "6px", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                  backgroundColor: moreInfoLongMode ? "rgba(74,222,128,0.13)" : "rgba(255,255,255,0.04)",
                  border: `0.5px solid ${moreInfoLongMode ? "#4ade80" : "rgba(255,255,255,0.1)"}`,
                  color: moreInfoLongMode ? "#4ade80" : "#aaa",
                }}
              >
                Long Position
              </button>
              {moreInfoPositions.map((p, i) => (
                <span key={p.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "#ccc", fontSize: "12px",
                }}>
                  Long {i + 1}
                  <button
                    type="button"
                    aria-label="Delete long position"
                    onClick={() => deleteMoreInfoPosition(p.id)}
                    style={{
                      background: "none", border: "none", color: "#888", cursor: "pointer",
                      fontSize: "13px", lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
          {/* Drawing tools chips (Draw Line) — kept as its own fragment above, re-opened here
              only to preserve the original JSX nesting for moreInfoDrawings' chip list. */}
          {userId && (
            <>
              {moreInfoDrawings.map((d) => (
                <span key={d.id} style={{
                  display: "inline-flex", alignItems: "center", gap: "6px",
                  minHeight: "32px", padding: "0 8px 0 10px", borderRadius: "6px",
                  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
                  color: "#ccc", fontSize: "12px",
                }}>
                  {d.price.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  <button
                    type="button"
                    aria-label="Delete line"
                    onClick={() => {
                      deleteDrawing(userId, d.id)
                        .then(() => setMoreInfoDrawings((prev) => prev.filter((x) => x.id !== d.id)))
                        .catch((e) => console.error("[chart_drawings-delete]", e?.message || e));
                    }}
                    style={{
                      background: "none", border: "none", color: "#888", cursor: "pointer",
                      fontSize: "13px", lineHeight: 1, padding: 0,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </>
          )}
          </div>
        </div>
        </div>
        {/* MOBILE — S/R/FVG dropdowns above the full-width chart (founder ask 2026-07-10),
            replacing the side rails that squeezed the chart to nothing on phones. */}
        {isMobile && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
            {overlayDropdown({ label: "Support",    keys: supportKeys,    max: supportMax,    type: "support",    color: "#4ade80" })}
            {overlayDropdown({ label: "Resistance", keys: resistanceKeys, max: resistanceMax, type: "resistance", color: "#ea580c" })}
            {overlayDropdown({ label: "FVG",        keys: fvgKeys,        max: fvgMax,        type: "fvg",        color: "#fbbf24" })}
          </div>
        )}
        {/* FLANK ROW — left rail (S+R) | center column (price+RSI+MACD) | right rail (FVG).
            On mobile the side rails are hidden (dropdowns above replace them). */}
        <div style={{ display: "flex", alignItems: "flex-start" }}>
          {/* LEFT RAIL — Support + Resistance compact columns (desktop only) */}
          {!isMobile && (
          <div style={{ display: "flex", gap: "6px", flexShrink: 0, marginLeft: "-6px", marginRight: "8px" }}>
            {renderOverlayGroup({ label: "S", keys: supportKeys,    max: supportMax,    type: "support",    color: "#4ade80", vertical: true, compact: true })}
            {renderOverlayGroup({ label: "R", keys: resistanceKeys, max: resistanceMax, type: "resistance", color: "#ea580c", vertical: true, compact: true })}
          </div>
          )}
          {/* CENTER COLUMN — price + RSI + MACD, edges aligned */}
          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* JOINED PANEL — price + RSI + MACD as one framed unit (mirrors dashboard SignalChart
            structure: bare sibling panes, no per-pane header strips, one #3A3A3A border). RSI/MACD
            collapse via the existing overlayFilters toggles. Refs preserved verbatim so the build +
            overlay-draw effects (Entry/Exit, S/R, FVG, signal marker) keep attaching unchanged. */}
        <div style={{ border: "1px solid #3A3A3A", borderRadius: "10px", overflow: "hidden", marginBottom: "6px" }}>
          <div style={{ position: "relative", width: "100%", height: "360px" }}>
            <div ref={moreInfoChartRef} style={{ width: "100%", height: "100%" }} />
            <div ref={moreInfoPriceXhairRef} style={{ position: "absolute", top: 0, bottom: 0, width: "1px",
              background: "rgba(255,255,255,0.25)", pointerEvents: "none", display: "none", left: 0, zIndex: 3 }} />
            <div ref={moreInfoPriceLegendRef} style={{ position: "absolute", top: "8px", left: "8px", zIndex: 4,
              pointerEvents: "none", font: "12px system-ui", background: "rgba(10,10,10,0.7)",
              borderRadius: "4px", padding: "4px 8px", whiteSpace: "nowrap" }} />
            <MeasureOverlay measurement={moreInfoMeasurement} stats={moreInfoMeasureStats} paneHeight={360} onClear={clearMoreInfoMeasurement} />
            <TrendLineOverlay lines={moreInfoTrendProjectedLines} />
            <FibOverlay fibs={moreInfoProjectedFibs} />
            <ChannelOverlay channels={moreInfoProjectedChannels} />
            <RayOverlay rays={moreInfoProjectedRays} />
            <LongPositionOverlay positions={moreInfoProjectedPositions} onDelete={deleteMoreInfoPosition} />
            {/* Load-failure overlay — stopgap for the rate-limit blank-chart bug (see
                docs/known-issue-rate-limiting-blank-charts.md). Same pattern as SignalChart.js. */}
            {moreInfoLoadError && (
              <div style={{
                position: "absolute", inset: 0, zIndex: 6, display: "flex",
                flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px",
                background: "rgba(17,17,17,0.88)",
              }}>
                <span style={{ color: "#f87171", fontSize: "13px" }}>{moreInfoLoadError}</span>
                <button
                  onClick={() => setMoreInfoRetryTick((n) => n + 1)}
                  style={{
                    minHeight: "36px", padding: "0 16px", borderRadius: "6px", cursor: "pointer",
                    border: "1px solid rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.12)",
                    color: "#f87171", fontSize: "12px", fontWeight: 600,
                  }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
          <div style={{ position: "relative", width: "100%", height: overlayFilters.showRSI ? "150px" : "0px", overflow: "hidden", marginTop: overlayFilters.showRSI ? "2px" : "0px" }}>
            <div ref={moreInfoRsiChartRef} style={{ width: "100%", height: "100%" }} />
            <div ref={moreInfoRsiXhairRef} style={{ position: "absolute", top: 0, bottom: 0, width: "1px",
              background: "rgba(255,255,255,0.25)", pointerEvents: "none", display: "none", left: 0, zIndex: 3 }} />
            <div ref={moreInfoRsiLegendRef} style={{ position: "absolute", top: "8px", left: "8px", zIndex: 4,
              pointerEvents: "none", font: "12px system-ui", background: "rgba(10,10,10,0.7)",
              borderRadius: "4px", padding: "4px 8px", whiteSpace: "nowrap" }} />
          </div>
          <div style={{ position: "relative", width: "100%", height: overlayFilters.showMACD ? "150px" : "0px", overflow: "hidden", marginTop: overlayFilters.showMACD ? "2px" : "0px" }}>
            <div ref={moreInfoMacdChartRef} style={{ width: "100%", height: "100%" }} />
            <div ref={moreInfoMacdXhairRef} style={{ position: "absolute", top: 0, bottom: 0, width: "1px",
              background: "rgba(255,255,255,0.25)", pointerEvents: "none", display: "none", left: 0, zIndex: 3 }} />
            <div ref={moreInfoMacdLegendRef} style={{ position: "absolute", top: "8px", left: "8px", zIndex: 4,
              pointerEvents: "none", font: "12px system-ui", background: "rgba(10,10,10,0.7)",
              borderRadius: "4px", padding: "4px 8px", whiteSpace: "nowrap" }} />
          </div>
        </div>
          </div>
          {/* RIGHT RAIL — FVG (desktop only; mobile uses the dropdown above) */}
          {!isMobile && (
          <div style={{ flexShrink: 0, marginLeft: "24px" }}>
            {renderOverlayGroup({ label: "FVG", keys: fvgKeys, max: fvgMax, type: "fvg", color: "#fbbf24", vertical: true, compact: true })}
          </div>
          )}
        </div>
      </>
    ) : (
      <p style={{ color: "#666", marginTop: "14px" }}>No signal selected.</p>
    )}
  </div>
  );
})()}

{activeTab === "coin_updates" && (
  <div style={{
    marginTop: "20px",
    padding: "20px",
    backgroundColor: "rgba(4,4,12,0.55)",
    borderRadius: "10px",
    border: "0.5px solid rgba(255,255,255,0.05)",
    width: "100%",
    boxSizing: "border-box"
  }}>
    {/* Themed header (founder ask 2026-07-09) — matches Notification History et al. */}
    <h3 style={{ color: "#e0c891", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "8px", marginTop: 0, marginBottom: "14px" }}>
      <IconChartCandle size={20} /> Updates
    </h3>
    <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
      {["all", "bullish", "neutral", "bearish"].map(mode => (
        <button
          key={mode}
          onClick={() => setCoinUpdateFilter(mode)}
          style={{
            padding: "6px 15px",
            borderRadius: "16px",
            fontSize: "11px",
            cursor: "pointer",
            border: coinUpdateFilter === mode ? "none" : "0.5px solid rgba(255,255,255,0.08)",
            background: coinUpdateFilter === mode ? "#818cf8" : "rgba(255,255,255,0.04)",
            color: coinUpdateFilter === mode ? "#04040c" : "#aaa",
            fontWeight: 500,
            transition: "background 0.3s, color 0.3s"
          }}
        >
          {mode.charAt(0).toUpperCase() + mode.slice(1)}
        </button>
      ))}
    </div>
    {/* Timeframe toggle bar */}
    <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
      {["all", ...Object.keys(granularityMap)].map(tf => (
        <button
          key={tf}
          onClick={() => setTimeframeFilter(tf)}
          style={{
            padding: "6px 15px",
            borderRadius: "16px",
            fontSize: "11px",
            cursor: "pointer",
            border: timeframeFilter === tf ? "none" : "0.5px solid rgba(255,255,255,0.08)",
            background: timeframeFilter === tf ? "#818cf8" : "rgba(255,255,255,0.04)",
            color: timeframeFilter === tf ? "#04040c" : "#aaa",
            fontWeight: 500,
            transition: "background 0.3s, color 0.3s"
          }}
        >
          {tf.toUpperCase()}
        </button>
      ))}
    </div>
    {/* Asset-class toggle bar */}
    <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
      {["all", "crypto", "stock"].map(ac => (
        <button
          key={ac}
          onClick={() => setAssetClassFilter(ac)}
          style={{
            padding: "6px 15px",
            borderRadius: "16px",
            fontSize: "11px",
            cursor: "pointer",
            border: assetClassFilter === ac ? "none" : "0.5px solid rgba(255,255,255,0.08)",
            background: assetClassFilter === ac ? "#f97316" : "rgba(255,255,255,0.04)",
            color: assetClassFilter === ac ? "#04040c" : "#aaa",
            fontWeight: 500,
            transition: "background 0.3s, color 0.3s"
          }}
        >
          {ac === "all" ? "All assets" : ac === "crypto" ? "Crypto" : "Stocks"}
        </button>
      ))}
    </div>
    {/* old plain "Updates" sub-label removed (founder ask 2026-07-09) — the themed
        serif-gold header at the top of this tab is the one title. */}
    {coinUpdateNotifications.length === 0 ? (
      <p style={{ color: "#ccc" }}>No updates at this time.</p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 }}>
        {coinUpdateNotifications
          .filter(note => {
            // Filter by selected timeframe
            if (timeframeFilter !== "all" && note.timeframe !== timeframeFilter) return false;
            // Filter by selected asset class
            if (assetClassFilter !== "all") {
              const isCrypto = getProvider(note.coin) === "coinbase";
              if (assetClassFilter === "crypto" && !isCrypto) return false;
              if (assetClassFilter === "stock" && isCrypto) return false;
            }
            const msg = Array.isArray(note.message)
              ? note.message
                  .map(line => (typeof line === "object" ? line.text : line))
                  .join(" ")
                  .toLowerCase()
              : (note.message || "").toString().toLowerCase();
            if (coinUpdateFilter === "all") return true;
            if (coinUpdateFilter === "bullish") return msg.includes("bullish");
            if (coinUpdateFilter === "bearish") return msg.includes("bearish");
            if (coinUpdateFilter === "neutral")
              return !msg.includes("bullish") && !msg.includes("bearish");
            return false;
          })
          .map((note, idx) => {
            // Read direction from message[0] — do NOT recompute sentiment.
            const head = Array.isArray(note.message) ? note.message[0] : null;
            const dirColor = head && typeof head === "object" ? head.color : "#888";
            // Map the existing color words to Prism word/band/dot.
            const prism = {
              green:     { word: "#34e88a", band: "rgba(34,230,122,0.20)",  dot: "#22e67a" },
              red:       { word: "#ff6b6b", band: "rgba(255,77,79,0.20)",   dot: "#ff5a5a" },
              goldenrod: { word: "#fde047", band: "rgba(250,204,21,0.18)",  dot: "#facc15" },
            };
            const tone = prism[dirColor] || { word: "#888", band: "rgba(255,255,255,0.04)", dot: "#888" };
            // Derive the direction WORD from head.text ("… Bullish Update").
            const headText = head && typeof head === "object" ? head.text : "";
            const dirWord =
              /Bullish/.test(headText) ? "Bullish" :
              /Bearish/.test(headText) ? "Bearish" :
              /Neutral/.test(headText) ? "Neutral" : "";
            return (
            <div
              key={idx}
              onClick={() => {
                setSelectedCoin(note.coin);
                setActiveTab("dashboard");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  setSelectedCoin(note.coin);
                  setActiveTab("dashboard");
                }
              }}
              role="button"
              tabIndex={0}
              style={{
                // Restyle 2026-07-09 (founder ask): same data/handlers, new skin —
                // class-colored border + app-signature serif accents.
                backgroundColor: "rgba(22,22,22,0.82)",
                borderRadius: "10px",
                border: `1px solid ${getProvider(note.coin) === "coinbase" ? "rgba(129,140,248,0.45)" : "rgba(249,115,22,0.45)"}`,
                overflow: "hidden",
                cursor: "pointer"
              }}
            >
              {/* Header band — sentiment-tinted, ticker + full name + pills, serif verdict */}
              <div style={{
                background: tone.band,
                padding: "9px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap"
              }}>
                <span style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: tone.dot,
                  flexShrink: 0
                }} />
                <span style={{ fontSize: "15px", fontWeight: 600, color: "#f0f0f0", letterSpacing: "0.01em" }}>
                  {note.coin}
                </span>
                {displayName(note.coin) !== note.coin && (
                  <span style={{ fontSize: "11px", color: "#aaa" }}>
                    {displayName(note.coin)}
                  </span>
                )}
                {assetTag(note.coin)}
                <span style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#c7d2fe",
                  background: "rgba(129,140,248,0.12)",
                  border: "0.5px solid rgba(129,140,248,0.3)",
                  padding: "2px 8px",
                  borderRadius: "5px"
                }}>
                  {note.timeframe}
                </span>
                <span style={{ marginLeft: "auto", color: tone.word, fontSize: "15px", fontWeight: 600, fontFamily: "Georgia, 'Times New Roman', serif", fontStyle: "italic" }}>
                  {dirWord}
                </span>
              </div>
              {/* Body — 5-column metric grid + timestamp */}
              <div style={{ padding: "12px 14px" }}>
                {note.metrics ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "6px" }}>
                    {[
                      { label: "MACD", value: note.metrics.macdHist.toFixed(2), tag: macdMomentumTag(note.metrics).tag, tagColor: macdMomentumTag(note.metrics).color },
                      { label: "RSI", value: note.metrics.rsiValue.toFixed(1), tag: rsiBandTag(note.metrics.rsiValue).tag, tagColor: rsiBandTag(note.metrics.rsiValue).color },
                      { label: "STOCH", value: typeof note.metrics.stochRsi === "number" ? note.metrics.stochRsi.toFixed(1) : "—", tag: typeof note.metrics.stochRsi === "number" ? stochBandTag(note.metrics.stochRsi).tag : null, tagColor: typeof note.metrics.stochRsi === "number" ? stochBandTag(note.metrics.stochRsi).color : null },
                      { label: "9 EMA", value: note.metrics.emaTrend, color:
                          note.metrics.emaTrend === "Increasing" ? "#34e88a" :   // bullish green
                          note.metrics.emaTrend === "Decreasing" ? "#ff6b6b" :   // bearish red
                          note.metrics.emaTrend === "Neutral"    ? "#fde047" :   // amber neutral
                          "#fff" },                                              // fallback (unknown)
                      { label: "AVG", value: note.metrics.avgMove != null ? note.metrics.avgMove.toFixed(2) + "%" : "—" },
                    ].map(cell => (
                      <div key={cell.label}>
                        <div style={{ fontSize: "9px", color: "#777", letterSpacing: "0.8px", marginBottom: "5px", textTransform: "uppercase" }}>
                          {cell.label}
                        </div>
                        {cell.tag ? (
                          <>
                            {/* Verdict phrase — Avenir Next (founder-picked specimen 2026-07-09);
                                system font on Apple devices, Montserrat/Segoe fallbacks elsewhere. */}
                            <div style={{ fontSize: "15px", color: cell.tagColor || "#eee", fontWeight: 500, fontFamily: "'Avenir Next LT Pro', 'Avenir Next', 'Avenir', 'Montserrat', 'Segoe UI', sans-serif", letterSpacing: "0.02em", lineHeight: 1.25 }}>
                              {cell.tag}
                            </div>
                            <div style={{ fontSize: "12px", color: "#999", fontWeight: 500, marginTop: "3px" }}>
                              {cell.value}
                            </div>
                          </>
                        ) : cell.label === "AVG" ? (
                          <div style={{ fontSize: "16px", color: cell.color || "#eee", fontWeight: 600 }}>
                            {cell.value}
                          </div>
                        ) : (
                          /* 9 EMA verdict word — same Avenir Next stack as the other verdicts */
                          <div style={{ fontSize: "15px", color: cell.color || "#eee", fontWeight: 500, fontFamily: "'Avenir Next LT Pro', 'Avenir Next', 'Avenir', 'Montserrat', 'Segoe UI', sans-serif", letterSpacing: "0.02em" }}>
                            {cell.value}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : Array.isArray(note.message) ? (
                  note.message.slice(1).map((line, i) =>
                    typeof line === "object" ? (
                      <div key={i} style={{ color: line.color, fontWeight: "bold" }}>
                        {line.text}
                      </div>
                    ) : (
                      <div key={i} style={{ marginLeft: "8px", color: "#ccc" }}>
                        {line}
                      </div>
                    )
                  )
                ) : null}
                <div style={{ fontSize: "12px", color: "#888", marginTop: "4px" }}>
                  {note.time}
                </div>
              </div>
            </div>
            );
          })}
      </div>
    )}
  </div>
)}

             
             {activeTab === "followed" && (
  <div
    style={{
      marginTop: "20px",
      padding: "20px",
      backgroundColor: "rgba(4,4,12,0.55)",
      borderRadius: "10px",
      border: "0.5px solid rgba(255,255,255,0.05)"
    }}
  >
    {/* Header restyle (founder ask 2026-07-09) — matches Notification History. */}
    <h3 style={{ color: "#e0c891", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "8px" }}>
      <IconBookmark size={20} /> Followed Trades
    </h3>
    {followedTrades.length === 0 ? (
      <p style={{ color: "#ccc", display: "flex", alignItems: "center", gap: "8px" }}>
        <IconBookmark size={18} /> No followed trades yet.
      </p>
    ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        {followedTrades.map((trade, i) => {
          const isStr = typeof trade === "string";

          // Shared Unfollow button — index-based, entry-type-agnostic (same in both branches).
          const unfollowBtn = (
            <button
              onClick={(e) => {
                // Prevent the parent div's onClick from firing
                e.stopPropagation();
                setFollowedTrades((prev) => prev.filter((_, index) => index !== i));
                // Persist fix (2026-07-11): trade entries also delete their
                // followed_trades row (RLS-owned). Legacy strings are local-only.
                if (typeof trade !== "string" && trade?.id != null) {
                  (async () => {
                    try {
                      const { data: { session } } = await supabase.auth.getSession();
                      if (!session?.user?.id) return;
                      const { error } = await supabase
                        .from("followed_trades")
                        .delete()
                        .eq("user_id", session.user.id)
                        .eq("signal_id", trade.id);
                      if (error) console.error("Unfollow persist failed:", error.message);
                    } catch (err) {
                      console.error("Unfollow persist failed:", err?.message || err);
                    }
                  })();
                }
              }}
              style={{
                backgroundColor: "#ff4d4d",
                color: "white",
                border: "none",
                padding: "6px 10px",
                borderRadius: "6px",
                cursor: "pointer"
              }}
            >
              ❌ Unfollow
            </button>
          );

          // ---- LEGACY / lean STRING entry: minimal fallback card (preserves old look + behavior) ----
          if (isStr) {
            const match = trade.match(/([A-Z]+-USD)/);
            const parsedCoin = match ? match[1] : null;
            return (
              <div
                key={i}
                onClick={() => {
                  if (parsedCoin) { setSelectedCoin(parsedCoin); }
                  setActiveTab("dashboard");
                }}
                style={{
                  backgroundColor: "rgba(44,47,51,0.82)",
                  padding: "10px",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer"
                }}
              >
                <p style={{ margin: 0, color: "#8b949e" }}>{trade}</p>
                {unfollowBtn}
              </div>
            );
          }

          // ---- STRUCTURED OBJECT entry: full card mirroring the History trade card ----
          const isCrypto = getProvider(trade.coin) === "coinbase";
          const accent = isCrypto ? "#818cf8" : "#f97316";
          const assetName = displayName(trade.coin);
          const formatNumber = (num) => num.toLocaleString();
          const TIER_ICONS = {
            "Strong Trade Signal": IconCircleCheck,
            "Breakout Candidate": IconTrendingUp,
            "High Potential Setup": IconChartBar,
            "Watchlist Candidate": IconEye,
            "Risky Setup": IconAlertTriangle,
          };
          const TierIcon = (trade.tier && TIER_ICONS[trade.tier]) || IconBell;
          const tierLabel = trade.tier
            ? trade.tier.charAt(0) + trade.tier.slice(1).toLowerCase()
            : "Trade signal";

          return (
            <div
              key={i}
              style={{
                backgroundColor: "rgba(22,22,22,0.82)",
                // Asset-class border — same rule as the notification cards.
                border: `1px solid ${isCrypto ? "rgba(129,140,248,0.55)" : "rgba(249,115,22,0.55)"}`,
                borderRadius: "10px",
                padding: "14px",
                marginBottom: "10px",
              }}
            >
              {/* HEADER + SCORE */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>

                {/* HEADER (left) */}
                <div>
                  {/* Row 1: name + class pill + timeframe pill */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "18px", fontWeight: 500, color: "#e8e8e8" }}>
                      {assetName}
                    </span>
                    <span style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      padding: "2px 9px",
                      borderRadius: "6px",
                      backgroundColor: isCrypto ? "rgba(129,140,248,0.16)" : "rgba(249,115,22,0.16)",
                      color: isCrypto ? "#a5b4fc" : "#f9a86b",
                    }}>
                      {isCrypto ? "Crypto" : "Stock"}
                    </span>
                    <span style={{
                      fontSize: "11px",
                      fontWeight: 500,
                      padding: "2px 8px",
                      borderRadius: "6px",
                      backgroundColor: isCrypto ? "rgba(129,140,248,0.10)" : "rgba(249,115,22,0.10)",
                      color: isCrypto ? "#9aa4d8" : "#d18a4f",
                    }}>
                      {trade.timeframe}
                    </span>
                  </div>
                  {/* Row 2: ticker · time */}
                  <div style={{ fontSize: "12px", color: "#999", marginTop: "3px" }}>
                    {trade.coin}
                    {trade.timestamp && (
                      <span style={{ color: "#555", marginLeft: "6px" }}>
                        · {new Date(trade.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </span>
                    )}
                  </div>
                  {/* Row 3: tier line */}
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "8px", color: tierColorFor(trade.tier) }}>
                    <TierIcon size={14} stroke={2} />
                    <span style={{ fontSize: "12px", fontWeight: 500 }}>{tierLabel}</span>
                  </div>
                </div>

                {/* GRADE (right) — mirrors the notification card: uniform 26px grade, engine
                    chip, two-line setup label. Replaces the old confidence/15 score block. */}
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ lineHeight: 1 }}>
                    <span style={{ fontSize: "26px", fontWeight: 600, color: accent }}>
                      {trade.gradeLabel || trade.grade || "—"}
                    </span>
                  </div>
                  <div style={{ fontSize: "10px", color: "#888", textTransform: "uppercase", marginTop: "2px" }}>
                    Grade
                    {trade.version && (
                      <span style={{
                        marginLeft: "6px", padding: "1px 5px", borderRadius: "4px",
                        fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em",
                        color: engineChipStyle(trade.version).fg,
                        backgroundColor: engineChipStyle(trade.version).bg,
                      }}>
                        {engineChipStyle(trade.version).text}
                      </span>
                    )}
                  </div>
                  {trade.setupKind && (
                    <div style={{ marginTop: "5px", textAlign: "right" }}>
                      <div style={{ fontSize: "13px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#ddd", lineHeight: 1.2 }}>
                        {trade.setupKind}
                      </div>
                      {trade.setupSide && (
                        <div style={{ fontSize: "12px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#999", lineHeight: 1.3 }}>
                          {trade.setupSide}
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>

              {/* DATA ROWS */}
              <div style={{ marginTop: "14px", borderTop: "0.5px solid rgba(255,255,255,0.06)", paddingTop: "12px", display: "flex", flexDirection: "column", gap: "9px" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#888" }}>Entry</span>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#ddd" }}>{trade.entry != null ? `$${formatNumber(trade.entry.toFixed(4))}` : "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#888" }}>Exit</span>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#ddd" }}>{trade.exit != null ? `$${formatNumber(trade.exit.toFixed(4))}` : "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#888" }}>Profit %</span>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#4ade80" }}>{trade.profitPct != null ? `+${trade.profitPct.toFixed(2)}%` : "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: "13px", color: "#888" }}>Est. profit</span>
                  <span style={{ fontSize: "14px", fontWeight: 500, color: "#4ade80" }}>{trade.estProfit != null ? `$${trade.estProfit.toFixed(2)}` : "—"}</span>
                </div>
              </div>

              {/* REASON pills */}
              {trade.reasons && trade.reasons.length > 0 && (
                <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {trade.reasons.map((r, ri) => (
                    <span key={ri} style={{
                      fontSize: "11px",
                      padding: "3px 9px",
                      borderRadius: "6px",
                      backgroundColor: "rgba(129,140,248,0.12)",
                      color: "#a5b4fc",
                    }}>
                      {r}
                    </span>
                  ))}
                </div>
              )}

              {/* ACTIONS — Unfollow + View Live Chart (founder ask 2026-07-09) */}
              <div style={{ marginTop: "14px", display: "flex", gap: "8px" }}>
                {unfollowBtn}
                <button
                  type="button"
                  onClick={() => {
                    setSelectedCoin(trade.coin);
                    setDashboardInterval(trade.timeframe);
                    setActiveTab("dashboard");
                  }}
                  style={{
                    minHeight: "44px", padding: "6px 12px", borderRadius: "6px", cursor: "pointer",
                    fontSize: "12px", fontWeight: 500,
                    backgroundColor: "rgba(255,255,255,0.04)",
                    border: "0.5px solid rgba(255,255,255,0.1)",
                    color: "#aaa",
                  }}
                >
                  View Live Chart
                </button>
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
)}
              
              {/* NEWS tab — placeholder (founder ask 2026-07-09): market news for stocks +
                  crypto lands here later. Header matches the serif-gold treatment. */}
              {activeTab === "news" && (
                <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "rgba(4,4,12,0.55)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)" }}>
                  <h3 style={{ color: "#e0c891", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "8px" }}>
                    <IconNews size={20} /> News
                  </h3>
                  <p style={{ color: "#888", fontSize: "14px", marginTop: "14px" }}>
                    The latest market news for stocks and crypto will live here. Coming soon.
                  </p>
                </div>
              )}

              {/* EDUCATION tab — placeholder (founder ask 2026-07-09): trading education
                  videos (YouTube links) land here later. */}
              {activeTab === "education" && (
                <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "rgba(4,4,12,0.55)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)" }}>
                  <h3 style={{ color: "#e0c891", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "8px" }}>
                    <IconBook size={20} /> Education
                  </h3>
                  <p style={{ color: "#888", fontSize: "14px", marginTop: "14px" }}>
                    Trading education — videos and lessons will live here. Coming soon.
                  </p>
                </div>
              )}

              {SHOW_TRADE_HISTORY && activeTab === "history" && (
  <div style={{ marginTop: "20px", padding: "20px", backgroundColor: "rgba(4,4,12,0.55)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)" }}>
    <h3 style={{ color: "#58a6ff", display: "flex", alignItems: "center", gap: "8px" }}>
      <IconHistory size={20} /> Trade History
    </h3>
    {tradeLog.filter(log => log.result !== "pending").length === 0 ? (
      <p style={{ color: "#ccc", display: "flex", alignItems: "center", gap: "8px" }}>
        <IconHistory size={18} /> No trades followed yet.
      </p>
    ) : (
      <>
        {(() => {
          const completedTrades = tradeLog.filter(log => log.result !== "pending");
          const successTrades = completedTrades.filter(log => log.result === "success");
          const successRate = ((successTrades.length / completedTrades.length) * 100).toFixed(2);
          return <p style={{ color: "#ccc" }}>Success Rate: {successRate}%</p>;
        })()}
        {tradeLog.filter(log => log.result !== "pending").map((log, i) => (
          <div key={i} style={{
            backgroundColor: "rgba(44,47,51,0.82)",
            padding: "10px",
            borderRadius: "8px",
            marginBottom: "10px",
            display: "flex",
            alignItems: "center"
          }}>
            <div style={{ marginRight: "10px", fontWeight: "bold", color: "#58a6ff" }}>{i + 1}.</div>
            <div>
              <p style={{ margin: 0, color: "#8b949e" }}>
                {log.result === "success" ? "✅ Successful Trade" : log.result === "bearish" ? "⚠️ Bearish Exit" : "Trade"}
              </p>
              <p style={{ margin: 0, color: "#8b949e" }}>
                {log.coin} | Entry: ${log.entry.toFixed(2)} | Exit: ${log.exit.toFixed(2)}
              </p>
            </div>
          </div>
        ))}
      </>
    )}
  </div>
)}
 
 {activeTab === "quick_view" && (() => {
    // Compact two-column card view. Reskin only — same state/handlers as before.
    const renderCard = (symbol, accent, isStock) => {
      const followed = !!followedCoins[symbol];
      const rawPrice = livePrices[symbol];                       // STRING (toFixed(4)) or undefined
      const price = rawPrice != null
        ? `$${Number(rawPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : "—";
      const change = dayChangeMap[symbol];                       // NUMBER or undefined
      const hasChange = change != null;
      const changeColor = (change ?? 0) >= 0 ? "#4ade80" : "#f87171";
      return (
        <div
          key={symbol}
          onClick={(e) => {
            if (e.target.closest("label") || e.target.closest("button")) return;
            setSelectedCoin(symbol);
            setActiveTab("dashboard");
          }}
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: "1fr auto 1fr",
            alignItems: "center",
            gap: "8px",
            padding: "11px 13px",
            borderRadius: "10px",
            background: "rgba(21,21,27,0.96)",
            border: "0.5px solid #2c2c34",
            borderLeft: `2px solid ${accent}`,
            cursor: "pointer",
            opacity: followed ? 1 : 0.45,
            transition: "opacity .2s",
          }}
        >
          {/* START zone: name on top, ticker + follow toggle below */}
          <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
            <span style={{ color: "#e6e6e6", fontSize: "15px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {displayName(symbol)}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "7px", minWidth: 0 }}>
              <span style={{ color: "#777", fontSize: "11px", fontWeight: 500 }}>{shortTicker(symbol)}</span>
              <label
                onClick={(e) => e.stopPropagation()}
                style={{ position: "relative", display: "inline-block", width: "26px", height: "14px", flexShrink: 0 }}
              >
                <input
                  type="checkbox"
                  checked={followed}
                  onChange={() => {
                    const next = !followedCoins[symbol];
                    setFollowedCoins(prev => ({ ...prev, [symbol]: next }));
                    if (userId) setFollowFlag(userId, symbol, next);          // Phase 3.3: persist pause/unpause
                  }}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{ position: "absolute", cursor: "pointer", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: followed ? accent : "#555", borderRadius: "14px", transition: ".3s" }}>
                  <span style={{ position: "absolute", height: "10px", width: "10px", left: followed ? "14px" : "2px", bottom: "2px", backgroundColor: "#fff", borderRadius: "50%", transition: ".3s" }} />
                </span>
              </label>
            </span>
          </div>
          {/* CENTER zone: 24h % dead-center of the card (middle auto column) */}
          <span style={{ justifySelf: "center", textAlign: "center", color: hasChange ? changeColor : "#666", fontSize: "14px", fontWeight: 500 }}>
            {hasChange ? `${change.toFixed(2)}%` : "—"}
          </span>
          {/* END zone: price, right-aligned */}
          <span style={{ justifySelf: "end", textAlign: "right", color: "#d0d0d0", fontSize: "14px", fontWeight: 500 }}>
            {price}
          </span>
          {/* remove × (top-right corner) — stocks via removeStock, crypto via removeCrypto */}
          <button
            onClick={() => (isStock ? removeStock(symbol) : removeCrypto(symbol))}
            title="Remove"
            style={{ position: "absolute", top: "4px", right: "6px", background: "none", border: "none", color: "#f87171", fontSize: "13px", lineHeight: 1, cursor: "pointer", padding: 0 }}
          >
            ×
          </button>
        </div>
      );
    };

    const sectionHeader = (label, accent, accentSoft) => (
      <div style={{ marginBottom: "10px" }}>
        <div style={{ color: accent, fontSize: "12px", fontWeight: 500, letterSpacing: "0.3px", marginBottom: "6px" }}>{label}</div>
        <div style={{ height: "1px", background: accentSoft }} />
      </div>
    );

    const searchBar = (accent, accentBg, accentBorder, value, onChange, onKeyDown) => (
      <div style={{ position: "relative", marginBottom: "12px" }}>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.5" style={{ position: "absolute", left: "9px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" />
        </svg>
        <input
          type="text"
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder="Search…"
          style={{ width: "100%", boxSizing: "border-box", padding: "6px 8px 6px 26px", background: accentBg, border: `0.5px solid ${accentBorder}`, borderRadius: "10px", color: "#e0e0e0", fontSize: "12px", outline: "none", fontFamily: "inherit" }}
        />
      </div>
    );

    // Crypto search FILTERS visible cards (no add this build).
    const cryptoQuery = cryptoInput.trim().toLowerCase();
    const visibleCoins = coins.filter(c =>
      !cryptoQuery ||
      displayName(c).toLowerCase().includes(cryptoQuery) ||
      shortTicker(c).toLowerCase().includes(cryptoQuery)
    );

    // Direction filter by sign of 24h change. Missing change ("—") has no sign →
    // hidden under positive/negative. 0.00 counts as positive (>= 0). Ignores follow-state.
    const passesDirection = (sym) => {
      if (directionFilter === "all") return true;
      const ch = dayChangeMap[sym];
      if (ch == null) return false;            // "—" hidden under pos/neg
      return directionFilter === "positive" ? ch >= 0 : ch < 0;
    };

    // Apply direction filter on top of the existing per-section lists.
    const cryptoList = visibleCoins.filter(passesDirection);
    const stockList = stockSymbols.filter(passesDirection);

    // Type filter controls whole-section visibility — exactly one shows at a time now (sub-tabs).
    const showCrypto = assetTypeFilter === "crypto";
    const showStocks = assetTypeFilter === "stocks";

    // Small segmented pill group for the top filter bar.
    const pillGroup = (label, options, value, onSelect) => (
      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
        <span style={{ color: "#777", fontSize: "11px", fontWeight: 500 }}>{label}</span>
        <div style={{ display: "flex", gap: "4px" }}>
          {options.map(opt => {
            const active = value === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => onSelect(opt.value)}
                style={{
                  minHeight: "28px",
                  padding: "5px 10px",
                  borderRadius: "8px",
                  border: active ? "0.5px solid #818cf8" : "0.5px solid #2c2c34",
                  background: active ? "#818cf8" : "#1b1b21",
                  color: active ? "#0f0f12" : "#9a9aa5",
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );

    const emptyMsg = (
      <p style={{ color: "#888", fontSize: "0.9rem" }}>Nothing matches.</p>
    );

    return (
      // Outer wrapper carries the panel chrome (was on the two-column row); holds
      // [themed header] then [filter bar] then [the unchanged two-column row].
      <div style={{ marginTop: "20px", padding: "14px", display: "flex", flexDirection: "column", gap: "14px" }}>
        {/* Themed header (founder ask 2026-07-09) — matches Notification History et al. */}
        <h3 style={{ color: "#e0c891", fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 600, letterSpacing: "0.04em", display: "flex", alignItems: "center", gap: "8px", margin: 0 }}>
          <IconScan size={20} /> Watchlist
        </h3>
        {/* Sub-tabs — Crypto / Stocks. Each renders its list full-width (no more side-by-side
            two-column squeeze). Accent matches the section it opens, same visual language as
            the 1h/6h/1d dashboard toggle. */}
        <div style={{ display: "flex", gap: "8px" }}>
          {[
            { value: "crypto", label: "Crypto", accent: "#818cf8" },
            { value: "stocks", label: "Stocks", accent: "#f97316" },
          ].map(({ value, label, accent }) => {
            const selected = assetTypeFilter === value;
            return (
              <button
                key={value}
                onClick={() => setAssetTypeFilter(value)}
                style={{
                  minHeight: "44px",
                  minWidth: "44px",
                  padding: "0 20px",
                  background: "rgba(28,28,28,0.96)",
                  border: selected ? `1px solid ${accent}` : "0.5px solid #303030",
                  borderRadius: "8px",
                  color: selected ? accent : "#888",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
        {/* Direction filter (unchanged) */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
          {pillGroup("Direction:", [
            { value: "all", label: "All" },
            { value: "positive", label: "Positive" },
            { value: "negative", label: "Negative" },
          ], directionFilter, setDirectionFilter)}
        </div>
        {/* GLOBAL search (filters crypto cards via cryptoInput) + Add button (opens popup) */}
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {searchBar("#818cf8", "rgba(129,140,248,0.07)", "rgba(129,140,248,0.3)", cryptoInput, (e) => setCryptoInput(e.target.value), undefined)}
          </div>
          <button
            onClick={() => setShowAddPopup(true)}
            style={{
              background: "rgba(129,140,248,0.14)",
              border: "0.5px solid rgba(129,140,248,0.3)",
              color: "#c7d2fe",
              borderRadius: "10px",
              padding: "6px 14px",
              fontSize: "12px",
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
              minHeight: "44px",
              fontFamily: "inherit",
            }}
          >
            + Add
          </button>
        </div>
        {/* Phase 3.3c: per-tier follow-count summary (presentational; honest over-cap, no clamp) */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", fontSize: "14px", fontWeight: 600 }}>
          <span style={{ color: "#666" }}>{caps.label}</span>
          <span style={{ color: "#818cf8" }}>{caps.crypto === Infinity ? `${coins.length} crypto` : `${coins.length}/${caps.crypto} crypto`}</span>
          <span style={{ color: "#444" }}>·</span>
          <span style={{ color: "#f97316" }}>{caps.stock === Infinity ? `${stockSymbols.length} stocks` : `${stockSymbols.length}/${caps.stock} stocks`}</span>
        </div>
        {/* two-column row (panel chrome moved to wrapper above) */}
        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          {/* LEFT — CRYPTO */}
          {showCrypto && (
            <div style={{ flex: 1, minWidth: 0 }}>
              {sectionHeader("CRYPTO", "#818cf8", "rgba(129,140,248,0.25)")}
              {cryptoList.length === 0 ? emptyMsg : (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                  {cryptoList.map(c => renderCard(c, "#818cf8", false))}
                </div>
              )}
            </div>
          )}
          {/* RIGHT — STOCKS (add now lives in the popup; no per-section search bar) */}
          {showStocks && (
            <div style={{ flex: 1, minWidth: 0 }}>
              {sectionHeader("STOCKS", "#f97316", "rgba(249,115,22,0.25)")}
              {stockSymbols.length === 0 ? (
                <p style={{ color: "#888", fontSize: "0.9rem" }}>No stocks followed yet.</p>
              ) : stockList.length === 0 ? emptyMsg : (
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))", gap: "8px" }}>
                  {stockList.map(s => renderCard(s, "#f97316", true))}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Add-asset popup — typeahead over ASSET_LIBRARY (net-new overlay) */}
        {showAddPopup && (() => {
          const q = addQuery.trim().toLowerCase();
          const results = ASSET_LIBRARY.filter(a =>
            (addTypeFilter === "all" || a.type === addTypeFilter) &&
            (!q || a.name.toLowerCase().includes(q) || a.symbol.toLowerCase().includes(q))
          );
          return (
            <div
              onClick={() => setShowAddPopup(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ width: "100%", maxWidth: "440px", maxHeight: "80vh", background: "#0b0b14", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: "16px", display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                {/* header (pinned) */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  <span style={{ color: "#e0e0e0", fontWeight: 600, fontSize: "15px" }}>Add asset</span>
                  <button
                    onClick={() => setShowAddPopup(false)}
                    title="Close"
                    style={{ minWidth: "44px", minHeight: "44px", background: "none", border: "none", color: "#888", fontSize: "20px", lineHeight: 1, cursor: "pointer", padding: 0 }}
                  >
                    ×
                  </button>
                </div>
                {/* search input (pinned) */}
                <div style={{ padding: "10px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  <input
                    type="text"
                    value={addQuery}
                    onChange={(e) => setAddQuery(e.target.value)}
                    autoFocus
                    placeholder="Search name or symbol…"
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: "rgba(129,140,248,0.07)", border: "0.5px solid rgba(129,140,248,0.3)", borderRadius: "10px", color: "#e0e0e0", fontSize: "13px", outline: "none", fontFamily: "inherit" }}
                  />
                </div>
                {/* type filter (pinned) — stacks with the search above */}
                <div style={{ display: "flex", gap: "6px", padding: "0 16px 10px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  {[
                    { value: "all", label: "All" },
                    { value: "crypto", label: "Crypto" },
                    { value: "stock", label: "Stocks" },
                  ].map(opt => {
                    const active = addTypeFilter === opt.value;
                    const accent = opt.value === "stock" ? "#f97316" : "#818cf8";
                    return (
                      <button
                        key={opt.value}
                        onClick={() => setAddTypeFilter(opt.value)}
                        style={{
                          padding: "5px 12px",
                          minHeight: "32px",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          color: active ? accent : "#888",
                          background: active
                            ? (opt.value === "stock" ? "rgba(249,115,22,0.14)" : "rgba(129,140,248,0.14)")
                            : "transparent",
                          border: active
                            ? `0.5px solid ${opt.value === "stock" ? "rgba(249,115,22,0.3)" : "rgba(129,140,248,0.3)"}`
                            : "0.5px solid rgba(255,255,255,0.08)",
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                {/* results (scroll) */}
                <div style={{ overflowY: "auto", padding: "6px 8px" }}>
                  {results.length === 0 ? (
                    <p style={{ color: "#888", fontSize: "0.9rem", padding: "12px" }}>No matches.</p>
                  ) : results.map(a => {
                    const added = coins.includes(a.symbol) || stockSymbols.includes(a.symbol);
                    return (
                      <div key={a.symbol} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", minHeight: "44px", padding: "6px 10px", borderRadius: "8px", opacity: added ? 0.5 : 1 }}>
                        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                          <span style={{ color: "#e0e0e0", fontSize: "14px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</span>
                          <span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                            <span style={{ color: "#666", fontSize: "11px", fontWeight: 500 }}>{shortTicker(a.symbol)}</span>
                            {assetTag(a.symbol)}
                          </span>
                        </div>
                        {added ? (
                          <span style={{ flexShrink: 0, padding: "6px 12px", borderRadius: "8px", background: "#1b1b21", border: "0.5px solid #2c2c34", color: "#777", fontSize: "12px", fontWeight: 600 }}>Added</span>
                        ) : (() => {
                          // Phase 3.3b: block-new-only cap. At the per-class tier cap, the Add button
                          // is disabled (cannot fire) and shows a "Limit reached" hint. Already-added
                          // assets are unaffected (handled by the `added` branch above).
                          const atCap = a.type === "crypto" ? cryptoAtCap : stockAtCap;
                          return atCap ? (
                            <span
                              title={`${caps.label} plan limit reached`}
                              style={{ flexShrink: 0, padding: "6px 12px", borderRadius: "8px", background: "#1b1b21", border: "0.5px solid #2c2c34", color: "#777", fontSize: "12px", fontWeight: 600, cursor: "not-allowed", whiteSpace: "nowrap" }}
                            >
                              Limit reached
                            </span>
                          ) : (
                            <button
                              onClick={() => (a.type === "crypto" ? addCrypto(a.symbol) : addStock(a.symbol))}
                              style={{ flexShrink: 0, minHeight: "44px", padding: "6px 16px", borderRadius: "8px", background: "rgba(129,140,248,0.14)", border: "0.5px solid rgba(129,140,248,0.3)", color: "#c7d2fe", fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                            >
                              Add
                            </button>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
        {/* Build D: delisted-asset notice — mirrors the add-popup overlay pattern */}
        {showDelistedNotice && delistedList.length > 0 && (
          <div
            onClick={() => setShowDelistedNotice(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ width: "100%", maxWidth: "440px", maxHeight: "80vh", background: "#0b0b14", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: "16px", display: "flex", flexDirection: "column", overflow: "hidden" }}
            >
              {/* header (pinned) */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                <span style={{ color: "#e0e0e0", fontWeight: 600, fontSize: "15px" }}>
                  {delistedList.length > 1 ? "Assets no longer available" : "Asset no longer available"}
                </span>
                <button
                  onClick={() => setShowDelistedNotice(false)}
                  title="Close"
                  style={{ minWidth: "44px", minHeight: "44px", background: "none", border: "none", color: "#888", fontSize: "20px", lineHeight: 1, cursor: "pointer", padding: 0 }}
                >
                  ×
                </button>
              </div>
              {/* body (scroll) */}
              <div style={{ padding: "14px 16px", overflowY: "auto" }}>
                <p style={{ color: "#9a9aa5", fontSize: "13px", margin: "0 0 12px", lineHeight: 1.4 }}>
                  {delistedList.length > 1
                    ? "These assets were removed from your watchlist because they're no longer available:"
                    : "This asset was removed from your watchlist because it's no longer available:"}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {delistedList.map(sym => (
                    <div key={sym} style={{ display: "flex", alignItems: "center", padding: "8px 10px", borderRadius: "8px", background: "#1b1b21", border: "0.5px solid #2c2c34" }}>
                      <span style={{ color: "#e0e0e0", fontSize: "13px", fontWeight: 600 }}>{sym}</span>
                    </div>
                  ))}
                </div>
              </div>
              {/* action (pinned) */}
              <div style={{ padding: "10px 16px", borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
                <button
                  onClick={() => { setShowDelistedNotice(false); setShowAddPopup(true); }}
                  style={{ width: "100%", minHeight: "44px", borderRadius: "10px", background: "rgba(129,140,248,0.14)", border: "0.5px solid rgba(129,140,248,0.3)", color: "#c7d2fe", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Add another
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })()}
               
               
                   {activeTab === "settings" && (
                    <div style={{ marginTop: "20px", padding: "14px", maxWidth: "760px" }}>
                      {/* Settings landing: card grid (settingsSubtab === null) */}
                      {settingsSubtab === null && (
                        <>
                          <h2 style={{ color: "#e0e0e0", fontSize: "20px", fontWeight: 600, margin: "0 0 4px" }}>Settings</h2>
                          <p style={{ color: "#888", fontSize: "13px", margin: "0 0 18px" }}>Manage your account and preferences.</p>
                          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "12px" }}>
                            {[
                              { key: "account", label: "Account", desc: "Your email and sign-out.", Icon: IconUser },
                              { key: "preferences", label: "Preferences", desc: "Profit range, amount, fees, and trade mode.", Icon: IconAdjustments },
                              { key: "notifications", label: "Notifications", desc: "Choose which signal grades you receive.", Icon: IconBell },
                              { key: "trading_logic", label: "Trading Logic", desc: "How our engines find and grade trades.", Icon: IconChartCandle },
                              { key: "terms", label: "Terms of Service", desc: "Coming soon.", Icon: IconFileText },
                              { key: "privacy", label: "Privacy", desc: "Coming soon.", Icon: IconShieldLock },
                            ].map(({ key, label, desc, Icon }) => (
                              <div
                                key={key}
                                onClick={() => setSettingsSubtab(key)}
                                style={{ display: "flex", flexDirection: "column", gap: "10px", minHeight: "44px", background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px", cursor: "pointer" }}
                                onMouseEnter={(e) => { e.currentTarget.style.border = "0.5px solid rgba(129,140,248,0.3)"; }}
                                onMouseLeave={(e) => { e.currentTarget.style.border = "0.5px solid rgba(255,255,255,0.06)"; }}
                              >
                                <div style={{ width: "40px", height: "40px", borderRadius: "10px", background: "rgba(129,140,248,0.14)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Icon size={20} color="#818cf8" />
                                </div>
                                <div style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600 }}>{label}</div>
                                <div style={{ color: "#888", fontSize: "13px" }}>{desc}</div>
                                <div style={{ color: "#818cf8", fontSize: "13px", marginTop: "auto" }}>Open →</div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {/* Section view: back control above the selected section (settingsSubtab !== null) */}
                      {settingsSubtab !== null && (
                        <button
                          onClick={() => setSettingsSubtab(null)}
                          style={{ minHeight: "44px", padding: "8px 4px", marginBottom: "10px", background: "none", border: "none", color: "#818cf8", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                        >
                          ← Settings
                        </button>
                      )}
                      {settingsSubtab === "account" && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px" }}>
                          <h3 style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600, margin: "0 0 14px" }}>Account</h3>
                          <div style={{ color: "#888", fontSize: "13px", marginBottom: "18px" }}>
                            Signed in as: <span style={{ color: "#e0e0e0" }}>{userEmail || "\u2014"}</span>
                          </div>
                          <button
                            onClick={() => supabase.auth.signOut()}
                            style={{ display: "flex", alignItems: "center", gap: "8px", minHeight: "44px", padding: "8px 16px", borderRadius: "8px", background: "rgba(249,115,22,0.12)", border: "0.5px solid rgba(249,115,22,0.3)", color: "#f97316", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                          >
                            <IconLogout size={18} /> Sign out
                          </button>
                        </div>
                      )}
                      {settingsSubtab === "preferences" && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px" }}>
                          <h3 style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600, margin: "0 0 14px" }}>Preferences</h3>
                        <div style={{ display: "flex", gap: "10px", marginBottom: "4px", flexWrap: "wrap" }}>
                            <div>
                                <label>Min Profit %</label><br />
                                <input type="number" step="1" value={profitTargetLow} onChange={(e) => setProfitTargetLow(parseFloat(e.target.value))} style={{ background: "#141414", border: "0.5px solid #2a2a2a", borderRadius: "7px", color: "#e0e0e0", fontSize: "13px", fontWeight: 500, padding: "5px 10px", fontFamily: "inherit", appearance: "none", outline: "none" }} />
                            </div>
                            <div>
                                <label>Max Profit %</label><br />
                                <input type="number" step="1" value={profitTargetHigh} onChange={(e) => setProfitTargetHigh(parseFloat(e.target.value))} style={{ background: "#141414", border: "0.5px solid #2a2a2a", borderRadius: "7px", color: "#e0e0e0", fontSize: "13px", fontWeight: 500, padding: "5px 10px", fontFamily: "inherit", appearance: "none", outline: "none" }} />
                            </div>
                            <div>
                                <label>Amount ($)</label><br />
                                <input type="number" value={tradeAmount} onChange={(e) => setTradeAmount(parseFloat(e.target.value))} style={{ background: "#141414", border: "0.5px solid #2a2a2a", borderRadius: "7px", color: "#e0e0e0", fontSize: "13px", fontWeight: 500, padding: "5px 10px", fontFamily: "inherit", appearance: "none", outline: "none" }} />
                            </div>
                            <div>
                                <label>Fees (%)</label><br />
                                <input type="number" step="0.1" value={tradeFee} onChange={(e) => setTradeFee(parseFloat(e.target.value))} style={{ background: "#141414", border: "0.5px solid #2a2a2a", borderRadius: "7px", color: "#e0e0e0", fontSize: "13px", fontWeight: 500, padding: "5px 10px", fontFamily: "inherit", appearance: "none", outline: "none" }} />
                            </div>
                            <div>
                                <label>Trader Mode</label><br />
                                <select
                                    value={userMode}
                                    onChange={(e) => setUserMode(e.target.value)}
                                    style={{
                                        background: "#141414",
                                        border: "0.5px solid #2a2a2a",
                                        borderRadius: "7px",
                                        color: "#e0e0e0",
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        padding: "5px 28px 5px 10px",
                                        fontFamily: "inherit",
                                        appearance: "none",
                                        outline: "none",
                                        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23555' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E\")",
                                        backgroundRepeat: "no-repeat",
                                        backgroundPosition: "right 10px center"
                                    }}
                                >
                                    <option value="Conservative">Conservative</option>
                                    <option value="Moderate">Moderate</option>
                                    <option value="Aggressive">Aggressive</option>
                                </select>
                            </div>
                        </div>
                        </div>
                      )}
                      {settingsSubtab === "notifications" && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px" }}>
                          <h3 style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600, margin: "0 0 4px" }}>Notifications</h3>
                          <p style={{ color: "#888", fontSize: "13px", margin: "0 0 14px" }}>Only the grades you turn on will appear in your notifications — per engine.</p>
                          {/* Per-engine toggle sections (founder ask 2026-07-09). One shared row
                              renderer; V2/V3 toggle display grades, V1 is a single legacy switch. */}
                          {(() => {
                            const toggleRow = (label, on, onClick, accent) => (
                              <button
                                key={label}
                                type="button"
                                role="switch"
                                aria-checked={on}
                                onClick={onClick}
                                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "44px", padding: "8px 14px", borderRadius: "9px", background: on ? `${accent}1f` : "#141414", border: on ? `0.5px solid ${accent}66` : "0.5px solid #2a2a2a", color: "#e0e0e0", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit", width: "100%", textAlign: "left" }}
                              >
                                <span>{label}</span>
                                <span aria-hidden="true" style={{ width: "40px", height: "22px", borderRadius: "11px", background: on ? accent : "#2a2a2a", position: "relative", flexShrink: 0 }}>
                                  <span style={{ position: "absolute", top: "2px", left: on ? "20px" : "2px", width: "18px", height: "18px", borderRadius: "50%", background: "#fff" }} />
                                </span>
                              </button>
                            );
                            const engineChip = (text, bg, fg) => (
                              <span style={{ padding: "1px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em", color: fg, backgroundColor: bg }}>{text}</span>
                            );
                            const toggleIn = (engine, grade) => setNotifPrefs((prev) => ({
                              ...prev,
                              [engine]: prev[engine].includes(grade) ? prev[engine].filter((x) => x !== grade) : [...prev[engine], grade],
                            }));
                            return (
                              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                                {/* V2 (buffet) retired 2026-07-10 — toggles hidden while it
                                    runs silent in the background. Unhide by flipping V2_RETIRED. */}
                                {!V2_RETIRED && (
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                    {engineChip("V2", "#f97316", "#0b0b14")}
                                    <span style={{ color: "#999", fontSize: "12px", fontWeight: 600 }}>Version 2 — Trading Range &amp; Breakout</span>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {["A", "B", "C", "Watchlist"].map((g) => toggleRow(g, notifPrefs.v2.includes(g), () => toggleIn("v2", g), "#f97316"))}
                                  </div>
                                </div>
                                )}
                                {/* NEW V2 (breakout-hold, engine id v2b) — Claude-piloted, 2026-07-10 */}
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                    {engineChip("V2", "#f97316", "#0b0b14")}
                                    <span style={{ color: "#999", fontSize: "12px", fontWeight: 600 }}>Version 2 — Breakout</span>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {["Prime", "Strong", "Base"].map((g) => toggleRow(g, (notifPrefs.v2b || []).includes(g), () => toggleIn("v2b", g), "#f97316"))}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                    {engineChip("V3", "#2dd4bf", "#0b0b14")}
                                    <span style={{ color: "#999", fontSize: "12px", fontWeight: 600 }}>Version 3 — Trend Line</span>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {["Prime", "Strong", "Base"].map((g) => toggleRow(g, notifPrefs.v3.includes(g), () => toggleIn("v3", g), "#2dd4bf"))}
                                  </div>
                                </div>
                                {/* V4 (trading range, range-low) — Claude-piloted, 2026-07-10 */}
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                    {engineChip("V4", "#a78bfa", "#0b0b14")}
                                    <span style={{ color: "#999", fontSize: "12px", fontWeight: 600 }}>Version 4 — Trading Range</span>
                                  </div>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {["Prime", "Strong", "Base"].map((g) => toggleRow(g, (notifPrefs.v4 || []).includes(g), () => toggleIn("v4", g), "#a78bfa"))}
                                  </div>
                                </div>
                                <div>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                    {engineChip("V1", "rgba(129,140,248,0.18)", "#c7d2fe")}
                                    <span style={{ color: "#999", fontSize: "12px", fontWeight: 600 }}>Version 1 — Legacy</span>
                                  </div>
                                  {/* Strong+Room is v1's ONLY exposed toggle (founder call
                                      2026-07-09) — the rest of v1 is retired from the UI. */}
                                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                                    {["Strong+Room"].map((g) => toggleRow(g, notifPrefs.v1.includes(g), () => toggleIn("v1", g), "#818cf8"))}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {notifPrefs.v1.length === 0 && notifPrefs.v3.length === 0 && (notifPrefs.v2b || []).length === 0 && (notifPrefs.v4 || []).length === 0 && (notifPrefs.v5 || []).length === 0 && (V2_RETIRED || notifPrefs.v2.length === 0) && (
                            <p style={{ color: "#f97316", fontSize: "12px", margin: "12px 0 0" }}>Everything is off — you won't receive any notifications.</p>
                          )}
                        </div>
                      )}
                      {/* TRADING LOGIC (founder ask 2026-07-09) — deliberately high-level:
                          explains the philosophy and grading of each engine WITHOUT exposing
                          thresholds, formulas, windows, or pins. The edge stays proprietary. */}
                      {settingsSubtab === "trading_logic" && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px" }}>
                          <h3 style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600, margin: "0 0 4px" }}>Trading Logic</h3>
                          <p style={{ color: "#888", fontSize: "13px", margin: "0 0 18px", lineHeight: 1.6 }}>
                            Every signal you receive comes from one of our engines. Each engine hunts exactly one kind of trade,
                            and every rule it follows is deterministic — pure chart math, validated against years of real market
                            history before it ever reaches your phone. No engine is ever influenced by AI, sentiment, or news.
                          </p>
                          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                            {[
                              {
                                chip: "V1", accent: "#818cf8", chipBg: "rgba(129,140,248,0.18)", chipFg: "#c7d2fe",
                                title: "Legacy",
                                intro: "Our original engine — a broad, multi-factor scorer that reads momentum, market structure, and support/resistance confluence together. It taught us most of what the newer engines know, and it is being retired in stages as its territory is carved into specialists.",
                                grades: [
                                  { g: "Strong+Room", d: "Its highest-conviction read — strong confluence with clear room to the target. The only V1 grade still surfaced." },
                                ],
                              },
                              {
                                chip: "V2", retired: true, accent: "#f97316", chipBg: "#f97316", chipFg: "#0b0b14",
                                title: "Trading Range & Breakout",
                                intro: "Hunts strength arriving at the top of a trading range. It maps the chart's real support and resistance structure, then waits for price to press into the ceiling with genuine buyer commitment — confirmed through a small set of battle-tested candle and volume patterns. The target is always the mapped level itself, never a hope.",
                                grades: [
                                  { g: "A", d: "The strongest validated pattern arrived with ideal volume fuel behind it." },
                                  { g: "B", d: "A proven setup with solid confirmation." },
                                  { g: "C", d: "A valid entry with lighter evidence." },
                                  { g: "Watchlist", d: "Worth eyes, but not yet conviction." },
                                ],
                              },
                              {
                                chip: "V2", accent: "#f97316", chipBg: "#f97316", chipFg: "#0b0b14",
                                title: "Breakout",
                                intro: "Hunts the break that holds. It waits for price to close above a real mapped ceiling with genuine force behind the move, then demands proof: every candle since the break must hold above the old ceiling while selling pressure visibly dies. Only after the market has kept the level does it enter — the failed breakouts that trap buyers never pass the hold test. The broken ceiling becomes the floor under the stop, and the target is always the next mapped level, never a hope.",
                                grades: [
                                  { g: "Prime", d: "The break, the hold, and the surrounding structure all lined up." },
                                  { g: "Strong", d: "A held break with solid supporting structure." },
                                  { g: "Base", d: "A valid held break with fewer extras." },
                                ],
                              },
                              {
                                chip: "V3", accent: "#2dd4bf", chipBg: "#2dd4bf", chipFg: "#0b0b14",
                                title: "Trend Line",
                                intro: "Trades trend continuation. It only looks at assets already in a measurable, structural uptrend, waits for the trend to rest — a brief, quiet pullback where selling pressure visibly dies — and enters the moment buyers retake control. Every trade must have a real, mapped level overhead inside a realistic profit window, or it is skipped entirely. Deliberately selective: silence means no worthy setup exists.",
                                grades: [
                                  { g: "Prime", d: "Nearly every conviction signal lined up at once." },
                                  { g: "Strong", d: "Most of the conviction signals present." },
                                  { g: "Base", d: "A valid trend resumption with fewer extras." },
                                ],
                              },
                              {
                                chip: "V4", accent: "#a78bfa", chipBg: "#a78bfa", chipFg: "#0b0b14",
                                title: "Trading Range",
                                intro: "Trades the floor of a proven trading range. It waits for price to fall into a mapped support zone that has already held at least twice, then demands defense: real buying volume stepping in at the floor and a candle closing decisively back up out of the zone. Only a floor that keeps holding earns the entry — the stop sits just beneath it, and the target is always the mapped ceiling of the range, never a hope.",
                                grades: [
                                  { g: "Prime", d: "A many-times-proven floor defended with every supporting signal present." },
                                  { g: "Strong", d: "A proven floor defended with solid supporting structure." },
                                  { g: "Base", d: "A valid defended floor with fewer extras." },
                                ],
                              },
                              // V2 (buffet) retired 2026-07-10 — card hidden while it runs
                              // silent in the background (V2_RETIRED flag). The NEW V2
                              // (breakout-hold) card is not marked retired and always shows.
                            ].filter((eng) => !(V2_RETIRED && eng.retired)).map((eng) => (
                              <div key={eng.title} style={{ background: "rgba(255,255,255,0.015)", border: "0.5px solid rgba(255,255,255,0.06)", borderLeft: `2px solid ${eng.accent}`, borderRadius: "0 10px 10px 0", padding: "14px 16px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                  <span style={{ padding: "1px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em", color: eng.chipFg, backgroundColor: eng.chipBg }}>{eng.chip}</span>
                                  <span style={{ color: "#e0e0e0", fontSize: "14px", fontWeight: 600 }}>{eng.title}</span>
                                </div>
                                <p style={{ color: "#999", fontSize: "13px", margin: "0 0 12px", lineHeight: 1.7 }}>{eng.intro}</p>
                                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                                  {eng.grades.map(({ g, d }) => (
                                    <div key={g} style={{ display: "flex", alignItems: "baseline", gap: "10px", padding: "7px 10px", background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(255,255,255,0.05)", borderRadius: "7px" }}>
                                      <span style={{ minWidth: "86px", flexShrink: 0, color: eng.accent, fontSize: "13px", fontWeight: 700, letterSpacing: "0.02em" }}>{g}</span>
                                      <span style={{ color: "#999", fontSize: "12.5px", lineHeight: 1.5 }}>{d}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {settingsSubtab === "terms" && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px" }}>
                          <h3 style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600, margin: "0 0 8px" }}>Terms of Service</h3>
                          <p style={{ color: "#888", fontSize: "13px", margin: 0 }}>Coming soon.</p>
                        </div>
                      )}
                      {settingsSubtab === "privacy" && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px" }}>
                          <h3 style={{ color: "#e0e0e0", fontSize: "15px", fontWeight: 600, margin: "0 0 8px" }}>Privacy Policy</h3>
                          <p style={{ color: "#888", fontSize: "13px", margin: 0 }}>Coming soon.</p>
                        </div>
                      )}
                    </div>
                   )}
                   {/* Admin Portal (Build 1): account list. Gated on the founder uid client-side
                       (defense in depth); the admin_list_accounts() RPC is the real fence. */}
                   {activeTab === "admin" && userId === FOUNDER_UID && (
                    <div style={{ marginTop: "20px", padding: "14px", maxWidth: "760px" }}>
                      <h2 style={{ color: "#e0e0e0", fontSize: "20px", fontWeight: 600, margin: "0 0 4px" }}>Admin</h2>
                      <p style={{ color: "#888", fontSize: "13px", margin: "0 0 18px" }}>Every signed-up account and its tier.</p>
                      {adminLoading && (
                        <div style={{ color: "#888", fontSize: "13px" }}>Loading accounts…</div>
                      )}
                      {!adminLoading && adminError && (
                        <div style={{ color: "#f97316", fontSize: "13px" }}>Couldn't load accounts: {adminError}</div>
                      )}
                      {!adminLoading && !adminError && adminAccounts.length === 0 && (
                        <div style={{ color: "#888", fontSize: "13px" }}>No accounts found.</div>
                      )}
                      {!adminLoading && !adminError && adminAccounts.length > 0 && (
                        <div style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: "12px", padding: "20px", overflowX: "auto" }}>
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                              <tr style={{ color: "#888", textAlign: "left" }}>
                                <th style={{ padding: "8px 12px 8px 0", fontWeight: 600 }}>Email</th>
                                <th style={{ padding: "8px 12px", fontWeight: 600 }}>Tier</th>
                                <th style={{ padding: "8px 0 8px 12px", fontWeight: 600 }}>Created</th>
                              </tr>
                            </thead>
                            <tbody>
                              {adminAccounts.map((acct) => (
                                <tr key={acct.id} style={{ borderTop: "0.5px solid rgba(255,255,255,0.06)", color: "#e0e0e0" }}>
                                  <td style={{ padding: "8px 12px 8px 0" }}>{acct.email || "—"}</td>
                                  <td style={{ padding: "8px 12px", color: tierColorFor(acct.tier) }}>{acct.tier || "—"}</td>
                                  <td style={{ padding: "8px 0 8px 12px", color: "#888" }}>{acct.created_at ? new Date(acct.created_at).toLocaleDateString() : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                   )}
                   {activeTab === "dashboard" && (
                    <>
                    <div className="dashboard-stack">
{/* Stat row — always allowed to wrap (2026-07-10): at full width all 7 cards fit one
    row exactly as before; in a narrower window (or with the nav expanded) cards flow
    to a second row instead of crushing/clipping their text. */}
<div style={{ display: "flex", flexWrap: "wrap", gap: "14px", marginBottom: "4px" }}>

  {(() => {
    const scanningCount = [...coins, ...stockSymbols].filter(c => followedCoins[c]).length;
    const activeSignals = notificationLog.filter(n => n.type === "trade").length;
    const selectedPrice = livePrices[selectedCoin];   // full-symbol key ("ADA-USD" / "AAPL")
    // 24h High/Low — derived from the cached 1h candle set (timeframe-INDEPENDENT, always
    // the "1h" slice), filtered to a TRUE rolling 24h window by timestamp (not slice(-24),
    // which can't shed an aged-out peak). candle.time is epoch SECONDS (formatCandles maps
    // time:c[0]; the Alpaca path emits Math.floor(Date.parse/1000)) — so cutoff is in seconds.
    // .high/.low are the formatCandles object fields (NOT the c[3] open-as-low bug).
    const dayCandles = allCandles?.[selectedCoin]?.["1h"] || [];
    const nowSec = Math.floor(Date.now() / 1000);
    const cutoff = nowSec - 24 * 60 * 60;
    const windowCandles = dayCandles.filter((c) => c.time >= cutoff);
    const has24h = windowCandles.length > 0;
    const dayHigh = has24h ? Math.max(...windowCandles.map((c) => c.high)) : null;
    const dayLow  = has24h ? Math.min(...windowCandles.map((c) => c.low))  : null;
    // Est. Profit card removed — its slot now holds the chart-selector dropdown.
    const cards = [
      { label: displayName(selectedCoin), value: selectedPrice ? `$${Number(selectedPrice).toLocaleString()}` : "—", bar: "#818cf8", real: !!selectedPrice },
      { label: "Scanning",       value: String(scanningCount),                                    bar: "#666",    real: true },
      { label: "Signals",        value: String(activeSignals),                                    bar: "#f97316", real: true },
    ];
    const cardChrome = {
      backgroundColor: "rgba(32,32,32,0.96)",
      border: "0.5px solid #303030",
      borderRadius: "10px",
      padding: isMobile ? "10px 12px" : "14px 16px",   // tighter cards on phones
      position: "relative",
      overflow: "hidden",
    };
    // Mobile pairing (2026-07-10): on phones cards sit two-per-row (calc(50% − half-gap));
    // desktop keeps the single-row 7-card behavior untouched.
    const compactSize = isMobile
      ? { flex: "1 1 calc(50% - 7px)", minWidth: 0 }
      : { flex: "0 0 auto", width: "62px", minWidth: "62px" };
    // flexSize — shared basis for the 5 growable cards (price/range/change/2 dropdowns) so all 7
    // cards fit on one row without wrapping. flex-shrink is enabled (the "1" in "1 1 0") and the
    // 96px floor is the smallest width the value text/labels stay legible at; below that the row
    // would rather scroll than crush text further, but nowrap + this floor comfortably fits any
    // realistic main-content width (side nav 185px + SIGNALS aside 232px still leaves room).
    // minWidth raised 96 → 150 (2026-07-10): 96px crushed a full BTC-scale price at 24px
    // ("$64,004.035" clipped). 150px keeps every value legible; combined with the row's
    // flexWrap, tight widths now wrap cards to a second row instead of clipping.
    const flexSize = isMobile
      ? { flex: "1 1 calc(50% - 7px)", minWidth: 0 }   // two per row on phones
      : { flex: "1 1 150px", minWidth: "150px" };
    // Price gets its own full-width row on mobile (founder ask 2026-07-10); pairs below it.
    const priceSize = isMobile ? { flex: "1 1 100%", minWidth: 0 } : flexSize;
    const textCard = (card, key, sizeStyle = flexSize) => (
      <div key={key} style={{ ...cardChrome, ...sizeStyle }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", backgroundColor: card.bar }} />
        <div style={{ fontSize: "24px", fontWeight: 700, color: card.real ? "#f0f0f0" : "#666", lineHeight: 1.1, marginBottom: "4px" }}>
          {card.value}
        </div>
        <div style={{ fontSize: "11px", fontWeight: 500, color: "#666", letterSpacing: "0.02em" }}>
          {card.label}
        </div>
      </div>
    );
    // Chart-selector cards — split from one combined <select> into two: Crypto (purple)
    // + Stocks (orange). Both write the SAME selectedCoin state, so choosing an asset in
    // one blanks the other: each select shows selectedCoin only when it belongs to that
    // group, otherwise value="" surfaces the disabled "—" placeholder. Same wiring as
    // before: setSelectedCoin(e.target.value).
    const chartSelectStyle = {
      width: "100%", boxSizing: "border-box",
      background: "#141414", border: "0.5px solid #2a2a2a", borderRadius: "7px",
      color: "#e0e0e0", fontSize: "13px", fontWeight: 600, padding: "6px 26px 6px 10px",
      fontFamily: "inherit", appearance: "none", outline: "none", marginBottom: "6px",
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23555' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E\")",
      backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center",
    };
    // poolKey ("crypto" | "stock") drives the search modal below: which symbol pool to list
    // and which accent color to use. Native <select> replaced with a button that opens it —
    // browser selects can't host a search box or custom row styling.
    const assetDropdownCard = (key, poolKey, bar, groupSymbols, isMember, renderLabel, cardLabel) => (
      <div key={key} style={{ ...cardChrome, ...flexSize }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", backgroundColor: bar }} />
        <button
          type="button"
          onClick={() => { setChartSelectQuery(""); setChartSelectOpen(poolKey); }}
          style={{ ...chartSelectStyle, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left" }}
        >
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {isMember ? renderLabel(selectedCoin) : "—"}
          </span>
        </button>
        <div style={{ fontSize: "11px", fontWeight: 500, color: "#666", letterSpacing: "0.02em" }}>
          {cardLabel}
        </div>
      </div>
    );
    const cryptoDropdownCard = assetDropdownCard(
      "chart-select-crypto", "crypto", "#818cf8", coins, coins.includes(selectedCoin),
      (c) => `${displayName(c)} (${shortTicker(c)})`, "Crypto"
    );
    const stockDropdownCard = assetDropdownCard(
      "chart-select-stock", "stock", "#f97316", stockSymbols, stockSymbols.includes(selectedCoin),
      (s) => `${displayName(s)} (${s})`, "Stocks"
    );
    // 24h High/Low card — two stacked values, so it does NOT fit the single-value
    // textCard helper; built inline reusing cardChrome so the chrome matches. Greys to
    // #666 (the existing missing-data treatment) when the rolling window is empty.
    const fmtPrice = (v) => Number(v).toLocaleString(undefined, v >= 1 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : { minimumFractionDigits: 4, maximumFractionDigits: 6 });
    const rangeCard = (
      <div key="range" style={{ ...cardChrome, ...flexSize }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", backgroundColor: "#818cf8" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <div style={{ fontSize: "16px", fontWeight: 700, color: has24h ? "#f0f0f0" : "#666", lineHeight: 1.1 }}>
              {has24h ? `$${fmtPrice(dayHigh)}` : "—"}
            </div>
            <div style={{ fontSize: "12px", fontWeight: 500, color: "#666", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
              24h High
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
            <div style={{ fontSize: "16px", fontWeight: 700, color: has24h ? "#f0f0f0" : "#666", lineHeight: 1.1 }}>
              {has24h ? `$${fmtPrice(dayLow)}` : "—"}
            </div>
            <div style={{ fontSize: "12px", fontWeight: 500, color: "#666", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
              24h Low
            </div>
          </div>
        </div>
      </div>
    );
    // 24h % Change card — reuses the existing dayChangeMap[selectedCoin] state (populated
    // every 60s by the fetchDayChange effect); no recompute. Mirrors rangeCard's chrome and
    // label-beside-value row; green/red/#666 + signed precision match the watchlist sites.
    const change = dayChangeMap[selectedCoin];
    const hasChange = change != null;
    const changeCard = (
      <div key="change" style={{ ...cardChrome, ...flexSize }}>
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", backgroundColor: "#818cf8" }} />
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <div style={{ fontSize: "24px", fontWeight: 700, lineHeight: 1.1, color: hasChange ? (change >= 0 ? "#4ade80" : "#f87171") : "#666" }}>
            {hasChange ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}%` : "—"}
          </div>
          <div style={{ fontSize: "12px", fontWeight: 500, color: "#666", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
            24h Change
          </div>
        </div>
      </div>
    );
    // Final order: Price → 24h High/Low → 24h Change → Crypto dropdown → Stock dropdown → Scanning → Active Signals (7 children).
    return (
      <>
        {textCard(cards[0], "price", priceSize)}
        {rangeCard}
        {changeCard}
        {cryptoDropdownCard}
        {stockDropdownCard}
        {textCard(cards[1], "scanning", compactSize)}
        {textCard(cards[2], "signals", compactSize)}
      </>
    );
  })()}

</div>

{/* Quick access + Recent Signals — side by side on desktop, STACKED on mobile
    (founder ask 2026-07-10) so neither forces horizontal scroll on a phone. */}
<div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: "16px", alignItems: "flex-start", marginBottom: "6px" }}>
<div style={{ flexShrink: 0, width: isMobile ? "100%" : undefined }}>
<div style={{ marginBottom: "6px" }}>
  <span style={{ color: "#888", fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>Quick access</span>
</div>
{/* Quick-access box — ONE border that hugs the cards: alignSelf flex-start stops the flex
    column from stretching it full-width, and a 5-column grid (repeat(5, max-content)) keeps
    crypto (row 1) and stock (row 2) in aligned, equal-width columns so the two rows are
    perfectly parallel. Single edit pencil in the top-right corner opens the tabbed modal. */}
<div style={{
  position: "relative",
  // Mobile (2026-07-10): stretch full-width and let the 5 columns SHRINK to fit the
  // viewport (minmax(0,1fr)) instead of max-content overflow that caused L/R scroll.
  alignSelf: isMobile ? "stretch" : "flex-start",
  width: isMobile ? "100%" : undefined,
  boxSizing: "border-box",
  display: "grid",
  gridTemplateColumns: isMobile ? "repeat(5, minmax(0, 1fr))" : "repeat(5, max-content)",
  gap: isMobile ? "5px" : "8px",
  border: "0.5px solid #303030",
  borderRadius: "10px",
  padding: isMobile ? "8px 8px" : "8px 10px",
  marginBottom: "0px",
}}>
  <button
    onClick={() => { setQuickEditDraft([...quickButtons]); setStockQuickEditDraft([...stockQuickButtons]); setQuickEditTab("crypto"); setShowQuickEdit(true); }}
    title="Edit quick access"
    style={{ position: "absolute", top: "3px", right: "3px", width: "24px", height: "24px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "rgba(15,16,20,0.9)", border: "0.5px solid #303030", borderRadius: "6px", color: "#888", cursor: "pointer", padding: 0, zIndex: 2 }}
  >
    <IconPencil size={13} />
  </button>
  {/* Crypto row (grid row 1, indigo accent) */}
  {quickButtons.map((sym, i) => {
    const selected = sym === selectedCoin;
    const price = livePrices[sym];
    const hasPrice = price !== undefined && price !== null && price !== "";
    return (
      <div
        key={sym}
        onClick={() => setSelectedCoin(sym)}
        style={{
          gridColumn: i + 1,
          gridRow: 1,
          minWidth: 0,
          background: "rgba(28,28,28,0.96)",
          border: selected ? "1px solid #818cf8" : "0.5px solid #303030",
          borderRadius: "8px",
          padding: isMobile ? "6px 5px" : "8px 14px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span style={{ color: selected ? "#818cf8" : "#888", fontWeight: 600, fontSize: isMobile ? "10px" : "13px", whiteSpace: "nowrap" }}>{sym}</span>
        <span style={{ color: "#ddd", fontWeight: 500, fontSize: isMobile ? "10px" : "12px", whiteSpace: "nowrap" }}>
          {hasPrice ? `$${Number(price).toLocaleString()}` : "—"}
        </span>
      </div>
    );
  })}
  {/* Stock row (grid row 2, orange accent) — shares the same columns, so widths match crypto */}
  {stockQuickButtons.map((sym, i) => {
    const selected = sym === selectedCoin;
    const price = livePrices[sym];
    const hasPrice = price !== undefined && price !== null && price !== "";
    return (
      <div
        key={sym}
        onClick={() => setSelectedCoin(sym)}
        style={{
          gridColumn: i + 1,
          gridRow: 2,
          minWidth: 0,
          background: "rgba(28,28,28,0.96)",
          border: selected ? "1px solid #f97316" : "0.5px solid #303030",
          borderRadius: "8px",
          padding: isMobile ? "6px 5px" : "8px 14px",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <span style={{ color: selected ? "#f97316" : "#888", fontWeight: 600, fontSize: isMobile ? "10px" : "13px", whiteSpace: "nowrap" }}>{sym}</span>
        <span style={{ color: "#ddd", fontWeight: 500, fontSize: isMobile ? "10px" : "12px", whiteSpace: "nowrap" }}>
          {hasPrice ? `$${Number(price).toLocaleString()}` : "—"}
        </span>
      </div>
    );
  })}
</div>
</div>
{/* Asset signal card — sits to the RIGHT of Quick access, same row, instead of stacked
    below it. This is the founder's corrected placement: earlier versions inserted this
    block BELOW Quick access in the vertical flow, which either pushed the interval-toggle/
    RSI-MACD/chart down (full-detail version) or required an artificial fixed-height spacer
    to avoid that (compact version) — both were fighting the layout. Placed beside Quick
    access instead, the row's height is always set by Quick access's two button rows
    (constant regardless of signal state), so this can never add or remove page height.
    Renders nothing (blank) when there's no signal for the selected asset. */}
<div style={{ flex: 1, minWidth: 0 }}>
<div style={{ marginBottom: "6px" }}>
  <span style={{ color: "#888", fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>Recent Signals</span>
</div>
{(() => {
  const note = notificationLog.find((n) => n.coin === selectedCoin && !dismissedIds.has(n.id));
  if (!note) return null;
  const TAG_MAP = {
    trade: { label: "SIGNAL", bg: "rgba(249,115,22,0.15)", fg: "#f97316" },
    success: { label: "EXIT ✓", bg: "rgba(74,222,128,0.12)", fg: "#4ade80" },
    bearish: { label: "BEARISH", bg: "rgba(248,113,113,0.12)", fg: "#f87171" },
    "early-exit-warning": { label: "WARNING", bg: "rgba(248,113,113,0.12)", fg: "#f87171" },
    adjusted: { label: "ADJUSTED", bg: "rgba(129,140,248,0.12)", fg: "#818cf8" },
  };
  const tag = TAG_MAP[note.type] || { label: note.type, bg: "rgba(129,140,248,0.12)", fg: "#818cf8" };
  const fmtPrice = (n) =>
    "$" + Number(n).toLocaleString("en-US", {
      minimumFractionDigits: n < 1 ? 4 : 2,
      maximumFractionDigits: n < 1 ? 4 : 2,
    });
  // Labeled stat fields (Entry/Exit/Profit %/Est. Profit/Grade) — replaces the old single
  // run-on summary string per founder feedback ("make entry, exit, %, profit, time and
  // grade more clear"). Only trade-type notes have these fields; other note types
  // (exit/bearish/adjusted alerts) fall back to their plain message text, same as before.
  const stats = note.type === "trade" ? [
    { k: "Entry", v: fmtPrice(note.entry), color: "#ddd" },
    { k: "Exit", v: fmtPrice(note.exit), color: "#ddd" },
    { k: "Profit %", v: `+${note.profitPct.toFixed(2)}%`, color: "#4ade80" },
    { k: "Est. Profit", v: `$${note.estProfit.toFixed(2)}`, color: "#4ade80" },
    { k: "Grade", v: note.gradeLabel || note.grade, color: "#818cf8" },
  ] : null;
  return (
    <div style={{ backgroundColor: "rgba(22,22,22,0.82)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)", overflow: "hidden" }}>
      <div style={{ padding: "8px 12px 6px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <span style={{ fontSize: "10px", fontWeight: 600, backgroundColor: tag.bg, color: tag.fg, borderRadius: "4px", padding: "2px 7px", letterSpacing: "0.04em" }}>
          {tag.label}
        </span>
        <span style={{ fontSize: "12px", fontWeight: 600, color: "#d0d0d0" }}>
          {note.coin}
        </span>
        <span style={{ fontSize: "10px", color: "#555", backgroundColor: "rgba(249,115,22,0.08)", borderRadius: "4px", padding: "2px 6px" }}>
          {note.timeframe}
        </span>
        {note.timestamp && (
          <span style={{ fontSize: "11px", color: "#888", marginLeft: "auto" }}>
            {new Date(note.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
      {stats ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", padding: "2px 12px 10px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
            {stats.map((s) => (
              <div key={s.k} style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                <span style={{ fontSize: "9px", fontWeight: 500, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {s.k}
                </span>
                <span style={{ fontSize: "13px", fontWeight: 600, color: s.color }}>
                  {s.v}
                </span>
              </div>
            ))}
          </div>
          {note.type === "trade" && (
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              <button
                onClick={() => handleFollowTrade(note)}
                style={{ minHeight: "30px", padding: "0 14px", backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "11px", fontWeight: 600, whiteSpace: "nowrap" }}
              >
                Follow Trade →
              </button>
              <button
                onClick={() => { setSelectedNote(note); setSelectedCoin(note.coin); setSelectedInterval(note.timeframe); setActiveTab("more_info"); }}
                style={{
                  minHeight: "30px",
                  padding: "0 14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "11px",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  color: "#aaa",
                  border: "0.5px solid rgba(255,255,255,0.1)",
                }}
              >
                <IconInfoCircle size={13} stroke={2} />
                More Info
              </button>
              {/* Dismiss (trash) — same handleDismissNotification per-user soft-hide used by
                  the SIGNALS aside and More Info page; global signals row is never touched. */}
              <button
                type="button"
                aria-label="Dismiss signal"
                onClick={() => handleDismissNotification(note.id)}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#B87333"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#666"; }}
                style={{
                  minWidth: "30px",
                  minHeight: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "6px",
                  cursor: "pointer",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  color: "#666",
                  border: "0.5px solid rgba(255,255,255,0.1)",
                  flexShrink: 0,
                }}
              >
                <IconTrash size={14} stroke={2} />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "0 12px 10px", fontSize: "11px", color: "#aaa", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
          {note.message}
        </div>
      )}
      {/* CONFLUENCE TAGS — same fingerprint.tags as the other two card renderers. Tag-only. */}
      {note.tags && note.tags.length > 0 && (
        <div style={{ padding: "0 12px 10px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {note.tags.map((t, ti) => (
            <span key={ti} style={{
              fontSize: "10px",
              padding: "2px 8px",
              borderRadius: "6px",
              backgroundColor: "rgba(255,255,255,0.06)",
              color: "#aaa",
              border: "0.5px solid rgba(255,255,255,0.1)",
            }}>
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
})()}
</div>
</div>
        {/* Quick-access edit panel — unified + tabbed (Crypto | Stocks). Both 5-slot lists are
            edited in one modal; the pool, draft, accent, and count switch with the active tab.
            Save commits BOTH lists at once (disabled if either would be left empty). */}
        {showQuickEdit && (() => {
          const isCrypto = quickEditTab === "crypto";
          const pool = (isCrypto ? coins : stockSymbols).filter(sym => followedCoins[sym]);
          const draft = isCrypto ? quickEditDraft : stockQuickEditDraft;
          const setDraft = isCrypto ? setQuickEditDraft : setStockQuickEditDraft;
          const nudge = isCrypto ? quickEditNudge : stockQuickEditNudge;
          const setNudge = isCrypto ? setQuickEditNudge : setStockQuickEditNudge;
          const accent = isCrypto ? "#818cf8" : "#f97316";
          const toggleDraft = (sym) => {
            if (draft.includes(sym)) {
              setDraft(draft.filter((s) => s !== sym));
            } else if (draft.length >= 5) {
              setNudge(true);
              setTimeout(() => setNudge(false), 300);
            } else {
              setDraft([...draft, sym]);
            }
          };
          const canSave = quickEditDraft.length > 0 && stockQuickEditDraft.length > 0;
          return (
            <div
              onClick={() => setShowQuickEdit(false)}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ width: "100%", maxWidth: "440px", maxHeight: "80vh", background: "#0b0b14", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: "16px", display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                {/* header (pinned) */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  <span style={{ color: "#e0e0e0", fontWeight: 600, fontSize: "15px" }}>Edit quick access</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: nudge ? "#f97316" : "#888", fontSize: "12px", fontWeight: 600, transition: "color 0.15s ease" }}>{draft.length}/5</span>
                    <button
                      onClick={() => setShowQuickEdit(false)}
                      title="Close"
                      style={{ minWidth: "44px", minHeight: "44px", background: "none", border: "none", color: "#888", fontSize: "20px", lineHeight: 1, cursor: "pointer", padding: 0 }}
                    >
                      ×
                    </button>
                  </div>
                </div>
                {/* sub-tabs — Crypto | Stocks */}
                <div style={{ display: "flex", gap: "8px", padding: "10px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  {[["crypto", "Crypto", "#818cf8"], ["stocks", "Stocks", "#f97316"]].map(([key, label, c]) => {
                    const active = quickEditTab === key;
                    return (
                      <button
                        key={key}
                        onClick={() => setQuickEditTab(key)}
                        style={{ flex: 1, minHeight: "38px", borderRadius: "8px", background: active ? `${c}22` : "transparent", border: active ? `0.5px solid ${c}` : "0.5px solid #2a2a2a", color: active ? c : "#888", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* followed-pool list (scroll) — whole row toggles draft membership */}
                <div style={{ overflowY: "auto", padding: "6px 8px" }}>
                  {pool.length === 0 ? (
                    <p style={{ color: "#888", fontSize: "0.9rem", padding: "12px" }}>No followed {isCrypto ? "crypto" : "stocks"}.</p>
                  ) : pool.map(sym => {
                    const inDraft = draft.includes(sym);
                    return (
                      <div
                        key={sym}
                        onClick={() => toggleDraft(sym)}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", minHeight: "44px", padding: "6px 10px", borderRadius: "8px", cursor: "pointer" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                          <span style={{ color: "#e0e0e0", fontSize: "14px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName(sym)}</span>
                          <span style={{ color: "#666", fontSize: "11px", fontWeight: 500 }}>{shortTicker(sym)}</span>
                        </div>
                        {inDraft ? (
                          <IconCircleCheck size={20} color={accent} style={{ flexShrink: 0 }} />
                        ) : (
                          <span style={{ flexShrink: 0, width: "20px", height: "20px", borderRadius: "50%", border: "1.5px solid #3a3a44" }} />
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* footer (pinned) — Save commits BOTH lists; disabled if either would be empty */}
                <div style={{ padding: "10px 16px", borderTop: "0.5px solid rgba(255,255,255,0.06)" }}>
                  <button
                    onClick={() => { if (canSave) { setQuickButtons(quickEditDraft); setStockQuickButtons(stockQuickEditDraft); setShowQuickEdit(false); } }}
                    disabled={!canSave}
                    style={{ width: "100%", minHeight: "44px", borderRadius: "8px", background: `${accent}24`, border: `0.5px solid ${accent}55`, color: "#e8e8f2", fontSize: "13px", fontWeight: 600, cursor: canSave ? "pointer" : "default", opacity: canSave ? 1 : 0.4, fontFamily: "inherit" }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
        {/* Chart-selector search modal (dashboard Crypto/Stocks cards) — moved here from the
            quick_view-tab render tree, where it was dead code on the dashboard tab (chartSelectOpen
            could be set, but the modal JSX never rendered because it lived behind
            activeTab === "quick_view"). Same overlay/header/search pattern as Edit-quick-access above,
            single-select: click a row to set selectedCoin and close. */}
        {chartSelectOpen && (() => {
          const isCrypto = chartSelectOpen === "crypto";
          const accent = isCrypto ? "#818cf8" : "#f97316";
          const pool = isCrypto ? coins : stockSymbols;
          const q = chartSelectQuery.trim().toLowerCase();
          const results = pool.filter(sym =>
            !q || displayName(sym).toLowerCase().includes(q) || sym.toLowerCase().includes(q) || shortTicker(sym).toLowerCase().includes(q)
          );
          const closeModal = () => setChartSelectOpen(null);
          return (
            <div
              onClick={closeModal}
              style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{ width: "100%", maxWidth: "320px", maxHeight: "70vh", background: "#0b0b14", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: "16px", display: "flex", flexDirection: "column", overflow: "hidden" }}
              >
                {/* header (pinned) */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  <span style={{ color: "#e0e0e0", fontWeight: 600, fontSize: "15px" }}>Select {isCrypto ? "crypto" : "stock"}</span>
                  <button
                    onClick={closeModal}
                    title="Close"
                    style={{ minWidth: "44px", minHeight: "44px", background: "none", border: "none", color: "#888", fontSize: "20px", lineHeight: 1, cursor: "pointer", padding: 0 }}
                  >
                    ×
                  </button>
                </div>
                {/* search input (pinned) */}
                <div style={{ padding: "10px 16px", borderBottom: "0.5px solid rgba(255,255,255,0.06)" }}>
                  <input
                    type="text"
                    value={chartSelectQuery}
                    onChange={(e) => setChartSelectQuery(e.target.value)}
                    autoFocus
                    placeholder="Search name or symbol…"
                    style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", background: `${accent}12`, border: `0.5px solid ${accent}4d`, borderRadius: "10px", color: "#e0e0e0", fontSize: "13px", outline: "none", fontFamily: "inherit" }}
                  />
                </div>
                {/* results (scroll) — same row layout as the Edit-quick-access pool list */}
                <div style={{ overflowY: "auto", padding: "6px 8px" }}>
                  {results.length === 0 ? (
                    <p style={{ color: "#888", fontSize: "0.9rem", padding: "12px" }}>No matches.</p>
                  ) : results.map(sym => {
                    const selected = sym === selectedCoin;
                    return (
                      <div
                        key={sym}
                        onClick={() => { setSelectedCoin(sym); closeModal(); }}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", minHeight: "44px", padding: "6px 10px", borderRadius: "8px", cursor: "pointer" }}
                      >
                        <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                          <span style={{ color: "#e0e0e0", fontSize: "14px", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayName(sym)}</span>
                          <span style={{ color: "#666", fontSize: "11px", fontWeight: 500 }}>{shortTicker(sym)}</span>
                        </div>
                        {selected ? (
                          <IconCircleCheck size={20} color={accent} style={{ flexShrink: 0 }} />
                        ) : (
                          <span style={{ flexShrink: 0, width: "20px", height: "20px", borderRadius: "50%", border: "1.5px solid #3a3a44" }} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
                        {/* TEMP HIDDEN — timeframe selector. Server now scans all timeframes centrally; this per-user selector is parked for higher-tier revival once the scan widens past 1h. Keep for later, do not delete.
                        <div style={{ marginTop: "2px", padding: "10px" }}>
                          <h4 style={{ color: "#58a6ff" }}>Timeframe Selection</h4>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                            {["1m", "5m", "15m", "1h", "6h", "1d"].map((tf) => (
                              <label key={tf} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", background: timeframes[tf] ? "rgba(129,140,248,0.14)" : "rgba(28,28,28,0.96)", padding: "8px 12px", borderRadius: "999px", border: timeframes[tf] ? "0.5px solid #818cf8" : "0.5px solid #2a2a2a" }}>
                                <input
                                  type="checkbox"
                                  checked={timeframes[tf]}
                                  onChange={() => setTimeframes((prev) => ({ ...prev, [tf]: !prev[tf] }))}
                                  style={{ display: "none" }}
                                />
                                <span style={{ color: timeframes[tf] ? "#818cf8" : "#555" }}>{tf}</span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div style={{ display: "flex", gap: "30px", marginTop: "10px", flexWrap: "wrap" }}>
  {[
    { label: "Scalp", key: "scalp", description: "5m & 15m" },
    { label: "Swing", key: "swing", description: "1h & 6h" },
    { label: "Day Trade", key: "day", description: "1d" }
  ].map(({ label, key, description }) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: "12px", background: "rgba(28,28,28,0.96)", padding: "12px 16px", borderRadius: "10px", border: tradeStyles[key] ? "0.5px solid #f97316" : "0.5px solid #2a2a2a" }}>
      <div>
        <div style={{ fontSize: "10px", fontWeight: 600, color: tradeStyles[key] ? "#f97316" : "#666666" }}>{label}{tradeStyles[key] && <span style={{ color: "#f97316", marginLeft: "6px", fontSize: "11px", fontWeight: 600 }}>✓</span>}</div>
        <div style={{ fontSize: "10px", color: "#666666" }}>{description}</div>
      </div>
      <label style={{ position: "relative", display: "inline-block", width: "50px", height: "26px" }}>
        <input
          type="checkbox"
          checked={tradeStyles[key]}
          onChange={() => {
            const newStyles = { ...tradeStyles, [key]: !tradeStyles[key] };
            setTradeStyles(newStyles);
            setTimeframes({
              ...timeframes,
              ...(key === "scalp" && { "5m": newStyles[key], "15m": newStyles[key] }),
              ...(key === "swing" && { "1h": newStyles[key], "6h": newStyles[key] }),
              ...(key === "day" && { "1d": newStyles[key] })
            });
          }}
          style={{ opacity: 0, width: 0, height: 0 }}
        />
        <span style={{
          position: "absolute",
          cursor: "pointer",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: tradeStyles[key] ? "#f97316" : "#555",
          borderRadius: "26px",
          transition: ".4s"
        }}>
          <span style={{
            position: "absolute",
            height: "20px",
            width: "20px",
            left: tradeStyles[key] ? "26px" : "4px",
            bottom: "3px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            transition: ".4s"
          }} />
        </span>
      </label>
    </div>
  ))}
</div>

                        */}
{/* Move Notifications BELOW the chart */}
{/* TEMP HIDDEN — Recent Notifications card, commented out per founder. Keep for later,
    do not delete.
<div style={{ marginTop: "20px", padding: "20px", background: "rgba(22,22,22,0.82)", borderRadius: "10px", border: "0.5px solid rgba(255,255,255,0.05)" }}>
    <h3 style={{ color: "#58a6ff" }}>🔔 Recent Notifications</h3>
    {notificationLog.length === 0 ? (
        <p>No alerts yet.</p>
    ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {notificationLog.slice(0, 5).map((note, i) => (
                <div key={i} style={{ background: "rgba(22,22,22,0.82)", border: "0.5px solid rgba(255,255,255,0.05)", padding: "10px", borderRadius: "10px", marginBottom: "10px" }}>
                    <p 
                        onClick={() => {
                            setSelectedCoin(note.coin);
                            setActiveTab("dashboard");
                        }} 
                        style={{ 
                            cursor: "pointer", 
                             
                            color: "#58a6ff" 
                        }}
                    >
                        {note.message}
                    </p>
                    <button
                        onClick={() => handleFollowTrade(note)}
                        style={{
                            marginTop: "8px",
                            backgroundColor: "#2563eb",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            cursor: "pointer"
                        }}
                    >
                        Follow Trade
                    </button>
                </div>
            ))}
        </div>
    )}
</div>
*/}


</div>
{/* Dashboard-local interval toggle — 1h / 6h / 1d. Mirrors the quick-access chip
    selected style (#818cf8 border). Drives dashboardInterval only (not selectedInterval). */}
<div style={{ display: "flex", gap: "8px", marginTop: "20px", marginBottom: "8px" }}>
  {["1h", "6h", "1d"].map((tf) => {
    const selected = tf === dashboardInterval;
    return (
      <button
        key={tf}
        onClick={() => setDashboardInterval(tf)}
        style={{
          minHeight: "44px",
          minWidth: "44px",
          padding: "0 16px",
          background: "rgba(28,28,28,0.96)",
          border: selected ? "1px solid #818cf8" : "0.5px solid #303030",
          borderRadius: "8px",
          color: selected ? "#818cf8" : "#888",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {tf}
      </button>
    );
  })}
</div>
{/* Dashboard-local RSI / MACD subpanel toggles — same chip style as the interval
    toggle (#818cf8 selected border). Drives dashShowRSI / dashShowMACD only. */}
<div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
  {[
    { label: "RSI", on: dashShowRSI, flip: () => setDashShowRSI((v) => !v) },
    { label: "MACD", on: dashShowMACD, flip: () => setDashShowMACD((v) => !v) },
    { label: "BB", on: dashShowBB, flip: () => setDashShowBB((v) => !v) },
    { label: "Vol Profile", on: dashShowVolProfile, flip: () => setDashShowVolProfile((v) => !v) },
    { label: "Levels", on: dashShowLevels, flip: () => setDashShowLevels((v) => { const next = !v; if (next) setDashLevelCats({ support: true, resistance: true, fvg: true }); return next; }) },
  ].map(({ label, on, flip }) => (
    <button
      key={label}
      onClick={flip}
      style={{
        minHeight: "44px",
        minWidth: "44px",
        padding: "0 16px",
        background: "rgba(28,28,28,0.96)",
        border: on ? "1px solid #818cf8" : "0.5px solid #303030",
        borderRadius: "8px",
        color: on ? "#818cf8" : "#888",
        fontSize: "13px",
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  ))}
</div>
{/* Build 3: per-category Levels sub-chips — shown only when the Levels master is on. Same chip
    style; each toggles one category (Support / Resistance / FVG), redrawing from cache (no refetch). */}
{dashShowLevels && (
<div style={{ display: "flex", gap: "8px", marginBottom: "8px" }}>
  {[
    { key: "support", label: "Support" },
    { key: "resistance", label: "Resistance" },
    { key: "fvg", label: "FVG" },
  ].map(({ key, label }) => {
    const on = dashLevelCats[key];
    return (
      <button
        key={key}
        onClick={() => setDashLevelCats((c) => ({ ...c, [key]: !c[key] }))}
        style={{
          minHeight: "44px",
          minWidth: "44px",
          padding: "0 16px",
          background: "rgba(28,28,28,0.96)",
          border: on ? "1px solid #818cf8" : "0.5px solid #303030",
          borderRadius: "8px",
          color: on ? "#818cf8" : "#888",
          fontSize: "13px",
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        {label}
      </button>
    );
  })}
</div>
)}
<div style={{ padding: "0 clamp(0px, 3vw, 48px)" }}>
<div style={{ marginTop: "20px", width: "100%", background: "rgba(17,17,17,0.96)", border: "1px solid #3A3A3A", borderRadius: "10px", overflow: "hidden" }}>
<SignalChart symbol={selectedCoin} interval={dashboardInterval} showRSI={dashShowRSI} showMACD={dashShowMACD} showBB={dashShowBB} showVolProfile={dashShowVolProfile} showLevels={dashShowLevels} levelCats={dashLevelCats} userId={userId} />
</div>
</div>
                    </>
                )}
                </main>

            {/* SIGNALS rail — desktop only (founder call 2026-07-09): on phones it ate half
                the viewport; the same cards remain reachable via the Notifications tab. */}
            {!isMobile && (
            <aside style={{
              width: "232px",
              minWidth: "232px",
              flexShrink: 0,
              backgroundColor: "transparent",
              borderLeft: "1px solid rgba(129,140,248,0.35)",
              display: "flex",
              flexDirection: "column",
              padding: "0px 12px",
              gap: "12px",
              overflowY: "auto",
            }}>

              {/* Panel header — sticky within the scrolling aside. Negative margins cancel the
                  aside's 16px/12px padding so the solid bg spans edge-to-edge and covers the top
                  padding strip (else cards peek above it when scrolled); inner padding restores spacing. */}
              <div style={{
                position: "sticky", top: 0, zIndex: 2,
                backgroundColor: "#070707",
                marginTop: "0px", marginLeft: "-12px", marginRight: "-12px",
                padding: "8px 12px 8px",
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px", fontWeight: 700, color: "#c7d2fe", letterSpacing: "2px", textTransform: "uppercase" }}>
                    Signals
                  </span>
                  <select
                    value={signalGradeFilter}
                    onChange={(e) => setSignalGradeFilter(e.target.value)}
                    style={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", color: "#aaa", fontSize: "11px", borderRadius: "6px", padding: "2px 6px", cursor: "pointer" }}
                  >
                    {/* Options = DISPLAYED grades (gradeLabel). V1 dropped (founder call
                        2026-07-09); its fires remain reachable via "All". */}
                    {/* Engine-agnostic grade list (see Notification History select). */}
                    <option value="All">All</option>
                    <option value="Prime">Prime</option>
                    <option value="Strong">Strong</option>
                    <option value="Base">Base</option>
                  </select>
                </div>
                <span style={{ fontSize: "11px", color: "#c7d2fe" }}>{filteredSignals.length}</span>
              </div>

              {/* Live signal cards from notificationLog (newest-first), grade-filtered */}
              {filteredSignals.length === 0 ? (
                /* Empty state message */
                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: "11px", color: "#333", textAlign: "center", lineHeight: 1.6 }}>
                    {notificationLog.length === 0 ? <>No active signals.<br />Scan to populate.</> : <>No {signalGradeFilter} signals.</>}
                  </span>
                </div>
              ) : (
                filteredSignals.map((note, i) => {
                  const TAG_MAP = {
                    trade: { label: "SIGNAL", bg: "rgba(249,115,22,0.15)", fg: "#f97316" },
                    success: { label: "EXIT ✓", bg: "rgba(74,222,128,0.12)", fg: "#4ade80" },
                    bearish: { label: "BEARISH", bg: "rgba(248,113,113,0.12)", fg: "#f87171" },
                    "early-exit-warning": { label: "WARNING", bg: "rgba(248,113,113,0.12)", fg: "#f87171" },
                    adjusted: { label: "ADJUSTED", bg: "rgba(129,140,248,0.12)", fg: "#818cf8" },
                  };
                  const tag = TAG_MAP[note.type] || { label: note.type, bg: "rgba(129,140,248,0.12)", fg: "#818cf8" };
                  if (note.type === "trade") {
                    const fmtPrice = (n) =>
                      "$" + n.toLocaleString("en-US", {
                        minimumFractionDigits: n < 1 ? 4 : 2,
                        maximumFractionDigits: n < 1 ? 4 : 2,
                      });
                    // Live price (App-state livePrices, string keyed by full symbol) — reuses fmtPrice
                    // (same 2dp≥1 / 4dp<1 format); undefined/NaN pre-poll → "--". Re-renders on poll.
                    const lp = parseFloat(livePrices[note.coin]);
                    const lpStr = Number.isFinite(lp) ? fmtPrice(lp) : "--";
                    // tier_label display commented out ("tier_label comment-out" build) — local consts
                    // preserved for later revival. The card's voice is now `grade`.
                    // const TIER_ICONS = {
                    //   "Strong Trade Signal": IconCircleCheck,
                    //   "Breakout Candidate": IconTrendingUp,
                    //   "High Potential Setup": IconChartBar,
                    //   "Watchlist Candidate": IconEye,
                    //   "Risky Setup": IconAlertTriangle,
                    // };
                    // const TierIcon = (note.tier && TIER_ICONS[note.tier]) || IconBell;
                    // const tierLabel = note.tier
                    //   ? note.tier.charAt(0) + note.tier.slice(1).toLowerCase()
                    //   : "Trade signal";
                    // const tierColor = tierColorFor(note.tier);
                    /* former tier_label JSX, preserved for revival (Tier line):
                      <div style={{ display: "flex", alignItems: "center", gap: "6px", padding: "6px 12px 0", color: tierColor }}>
                        <TierIcon size={14} stroke={2} />
                        <span style={{ fontSize: "12px", fontWeight: 500 }}>{tierLabel}</span>
                      </div>
                    */
                    return (
                      <div key={i} style={{ position: "relative", flexShrink: 0, borderRadius: "10px", /* asset-class border: crypto = indigo, stock = orange */ border: `1px solid ${getProvider(note.coin) === "coinbase" ? "rgba(129,140,248,0.55)" : "rgba(249,115,22,0.55)"}`, overflow: "hidden" }}>

                        {/* Dismiss — absolute top-right corner; per-user soft hide, global signals row
                            untouched. stopPropagation so the card's handlers do not fire. The HEADER
                            reserves paddingRight below so its Live-price cluster clears this corner. */}
                        <button
                          type="button"
                          aria-label="Dismiss signal"
                          onClick={(e) => { e.stopPropagation(); handleDismissNotification(note.id); }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = "#B87333"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = "#666"; }}
                          style={{
                            position: "absolute",
                            top: "2px",
                            right: "2px",
                            zIndex: 1,
                            minWidth: "24px",
                            minHeight: "24px",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 0,
                            background: "none",
                            border: "none",
                            color: "#666",
                            cursor: "pointer",
                          }}
                        >
                          <IconX size={14} stroke={2} />
                        </button>

                        {/* HEADER section — timeframe · date · time · Live flow inline on one line.
                            paddingRight (26px) = the corner x's 2px offset + its 24px width, so the
                            right-aligned cluster clears the x with room to spare instead of squishing. */}
                        <div style={{ backgroundColor: "rgba(18,18,18,0.82)", padding: "10px 26px 10px 12px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                          <span style={{ fontSize: "10px", fontWeight: 600, backgroundColor: tag.bg, color: tag.fg, borderRadius: "4px", padding: "2px 7px", letterSpacing: "0.04em" }}>
                            {tag.label}
                          </span>
                          <span style={{ fontSize: "18px", fontWeight: 700, color: "#f0f0f0", lineHeight: 1.1 }}>
                            {note.coin}
                          </span>
                          {/* Full asset name beside the ticker (founder ask 2026-07-09) */}
                          {displayName(note.coin) !== note.coin && (
                            <span style={{ fontSize: "11px", color: "#999" }}>
                              {displayName(note.coin)}
                            </span>
                          )}
                          <span style={{ fontSize: "11px", color: "#666", marginLeft: "auto" }}>
                            {note.timeframe}
                          </span>
                          {note.timestamp && (
                            <span style={{ fontSize: "10px", color: "#555" }}>
                              {new Date(note.timestamp).toLocaleDateString([], { month: "short", day: "numeric" })} · {new Date(note.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                          <span style={{ display: "inline-flex", alignItems: "baseline", gap: "3px" }}>
                            <span style={{ fontSize: "10px", color: "#F87171" }}>Live</span>
                            <span style={{ fontSize: "11px", color: "#ddd", fontWeight: 500 }}>{lpStr}</span>
                          </span>
                        </div>

                        {/* tier_label line removed here — markup preserved in the commented const block above */}

                        {/* DATA section */}
                        <div style={{ backgroundColor: "rgba(22,22,22,0.82)", padding: "10px 12px" }}>
                          {[
                            { k: "Entry", v: fmtPrice(note.entry), green: false },
                            { k: "Exit", v: fmtPrice(note.exit), green: false },
                            { k: "Profit %", v: `+${note.profitPct.toFixed(2)}%`, green: true },
                            { k: "Est. Profit", v: `$${note.estProfit.toFixed(2)}`, green: true },
                          ].map((row, ri, arr) => (
                            <div key={row.k} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: ri < arr.length - 1 ? "0.3px solid rgba(255,255,255,0.06)" : "none" }}>
                              <span style={{ fontSize: "11px", color: "#888" }}>{row.k}</span>
                              <span style={{ fontSize: "12px", fontWeight: 500, color: row.green ? "#4ade80" : "#ddd" }}>{row.v}</span>
                            </div>
                          ))}
                          {/* CONFLUENCE TAGS — same fingerprint.tags as the Notification History list
                              (worker.cjs buildConfluenceTags). Tag-only, no grade effect. */}
                          {note.tags && note.tags.length > 0 && (
                            <div style={{ marginTop: "8px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                              {note.tags.map((t, ti) => (
                                <span key={ti} style={{
                                  fontSize: "10px",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  backgroundColor: "rgba(255,255,255,0.06)",
                                  color: "#aaa",
                                  border: "0.5px solid rgba(255,255,255,0.1)",
                                }}>
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* GRADE section */}
                        <div style={{ backgroundColor: "rgba(10,10,10,0.82)", padding: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
                              {/* Uniform grade sizing (founder ask 2026-07-09) — matches the
                                  notification card: 26px/600 for every grade, no length switch. */}
                              <span style={{ fontSize: "26px", fontWeight: 600, color: "#818cf8", lineHeight: 1 }}>{note.gradeLabel || note.grade}</span>
                              {/* Engine-version chip (v1/v2/v3 parallel comparison) */}
                              <span style={{
                                padding: "1px 5px", borderRadius: "4px",
                                fontSize: "9px", fontWeight: 700, letterSpacing: "0.04em",
                                color: engineChipStyle(note.version).fg,
                                backgroundColor: engineChipStyle(note.version).bg,
                              }}>
                                {engineChipStyle(note.version).text}
                              </span>
                            </div>
                            <span style={{ fontSize: "10px", color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: "2px" }}>grade</span>
                            {/* Setup-type label — two lines, own row (no overflow): KIND of trade
                                in skinny italic serif, SIDE in smaller muted text beneath. */}
                            {note.setupKind && (
                              <div style={{ marginTop: "5px" }}>
                                <div style={{ fontSize: "13px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#ddd", lineHeight: 1.2 }}>
                                  {note.setupKind}
                                </div>
                                {note.setupSide && (
                                  <div style={{ fontSize: "12px", fontWeight: 400, fontStyle: "italic", fontFamily: "Georgia, 'Times New Roman', serif", color: "#999", lineHeight: 1.3 }}>
                                    {note.setupSide}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "flex", gap: "8px" }}>
                          <button
                            onClick={() => handleFollowTrade(note)}
                            style={{ flex: 1, minHeight: "44px", backgroundColor: "#2563eb", color: "white", border: "none", padding: "6px 12px", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: 500 }}
                          >
                            Follow Trade →
                          </button>
                          <button
                            onClick={() => {
                              setSelectedNote(note);
                              setSelectedCoin(note.coin);
                              setSelectedInterval(note.timeframe);
                              setActiveTab("more_info");
                            }}
                            style={{
                              flex: 1,
                              minHeight: "44px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "6px",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: 500,
                              backgroundColor: "rgba(255,255,255,0.04)",
                              color: "#aaa",
                              border: "0.5px solid rgba(255,255,255,0.1)",
                            }}
                          >
                            <IconInfoCircle size={16} stroke={2} />
                            More Info
                          </button>
                          </div>
                          {note.reasons && note.reasons.length > 0 && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                              {note.reasons.map((r, ri) => (
                                <span key={ri} style={{ fontSize: "10px", padding: "3px 8px", backgroundColor: "rgba(129,140,248,0.10)", color: "#aaa", borderRadius: "6px" }}>
                                  {r}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                      </div>
                    );
                  }
                  return (
                    <div key={i} style={{
                      flexShrink: 0,
                      backgroundColor: "rgba(22,22,22,0.82)",
                      borderRadius: "10px",
                      border: "0.5px solid rgba(255,255,255,0.05)",
                      overflow: "hidden",
                    }}>

                      {/* Card header — tag + symbol + timeframe + optional time */}
                      <div style={{ padding: "10px 12px 8px", borderBottom: "0.5px solid rgba(255,255,255,0.05)", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "10px", fontWeight: 600, backgroundColor: tag.bg, color: tag.fg, borderRadius: "4px", padding: "2px 7px", letterSpacing: "0.04em" }}>
                          {tag.label}
                        </span>
                        <span style={{ fontSize: "12px", fontWeight: 600, color: "#d0d0d0" }}>
                          {note.coin}
                        </span>
                        <span style={{ fontSize: "10px", color: "#555", backgroundColor: "rgba(249,115,22,0.08)", borderRadius: "4px", padding: "2px 6px" }}>
                          {note.timeframe}
                        </span>
                        {note.timestamp && (
                          <span style={{ fontSize: "10px", color: "#555", marginLeft: "auto" }}>
                            {new Date(note.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          </span>
                        )}
                      </div>

                      {/* Message body */}
                      <div style={{ padding: "8px 12px", fontSize: "11px", color: "#aaa", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                        {note.message}
                      </div>

                    </div>
                  );
                })
              )}

            </aside>
            )}
            </div>
            </div>
        );
}

export default App;